import { uploadAtlasImage } from "../services/storage";
import { auth } from "../firebase/client";
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePatient } from '../contexts/PatientContext';
import { cleanAIText } from '../utils/cleanAIText';
import { atlasLabel, normalizeAtlasText } from '../utils/atlasText';
import { 
  Search, 
  Sparkles, 
  AlertCircle, 
  RotateCcw, 
  RotateCw,
  Download, 
  Rotate3d, 
  ZoomIn,
  ZoomOut, 
  Maximize, 
  Upload, 
  Trash2, 
  ScanSearch, 
  Zap, 
  Hammer, 
  Grip, 
  FileText, 
  MousePointer2, 
  Layers, 
  CircleDot, 
  Key, 
  ExternalLink, 
  Camera, 
  Compass, 
  ArrowRightLeft, 
  Move, 
  Image as ImageIcon, 
  Video, 
  Save, 
  BookMarked, 
  FolderSearch,
  X, 
  Share2, 
  ChevronLeft, 
  ChevronRight, 
  Play, 
  Pause, 
  ClipboardCheck, 
  CheckSquare, 
  Square, 
  Copy, 
  Ruler,
  Printer,
  LifeBuoy,
  FileSignature,
  ClipboardList,
  Plus,
  MousePointerClick,
  PackagePlus,
  Check,
  FilePlus,
  FileType,
  Lightbulb,
  Library,
  Mic,
  MicOff
} from 'lucide-react';
import {
  highlightLesionInImage,
  analyzeAtlasImageFindings,
  measureAnatomyFromImage,
  generateVisualDiagnosticCheck,
  generateSurgicalPlanningAnalysisJson,
  generateSurgicalPlanV2,
  generateSurgicalPlanText,
  generateScientificSurgicalPlanText,
  calculateSurgicalBiometrics,
  defineSurgicalGeometry,
  generateStructuredPatientOrientations,
  fallbackStructuredOrientation,
  generateOrthoDiagram,
  generate360OrthoVideo,
  normalizeSurgicalPlanResponse,
  type AtlasImageAnalysis,
  type PostOpGenerationContext,
  type ScientificEvidenceItem,
  type StructuredSurgicalPlanEvidence
} from '../services/geminiService';
import PatientFilePickerModal from './PatientFilePickerModal';
import SurgicalPlanningPremiumV2 from './atlas/SurgicalPlanningPremiumV2';
import SurgicalPlanOriginalImageViewer from './atlas/SurgicalPlanOriginalImageViewer';
import type { TreatmentRecommendation, TreatmentStrategy } from './atlas/TreatmentSelector';
import ClinicalCard from './atlas/ClinicalCard';
import ClinicalGrid from './atlas/ClinicalGrid';
import { atlasColors } from './atlas/atlasTheme';
import type { SurgicalPlanV2 } from '../utils/surgicalPlanV2';

type ScientificPlanResponse = Omit<StructuredSurgicalPlanEvidence, 'surgicalIndication'> & {
  surgicalIndication: string;
};

// Tipo explÃ­cito para o resultado da anÃ¡lise de lesÃ£o
type AtlasLesionResult = {
  imageData: string | null;
  description: string;
  anatomicalRegion?: string;
  anatomicalAnchors?: string;
  fractureLocation?: string;
  suspectedLesionRegion?: string;
  imagingEvidenceRegion?: string;
  lesionBoundingBox?: { x: number; y: number; width: number; height: number; confidence?: number } | null;
  visualPrompt?: string;
  differentialDiagnosis?: string[];
  analysis?: any;
};
import { InventoryItem, ClinicalNote } from '../contexts/types';
import { findSimilarAtlasReferenceCases, inferAtlasReferenceBone, inferAtlasReferenceFractureType } from '../data/atlasReferenceCases';
import {
  UsageLimitError,
  type UsageMetric,
} from '../services/usageLimits';
import { compressImageForSurgicalPlan, estimateDataUrlBytes } from '../services/surgicalPlanImageCompression';



const getAiStudio = () => (window as any).aistudio;
const surgicalPlanningPrimaryFlow = true;
const SURGICAL_PLAN_MAX_IMAGES = 2;
const SURGICAL_PLAN_MAX_PAYLOAD_BYTES = 220_000;

const isValidSurgicalPlanAttachment = (value?: string | null) => {
  if (!value) return false;
  return value.startsWith("data:image/") || value.startsWith("data:application/pdf");
};

const getDataUrlMimeType = (value?: string | null) => {
  if (!value?.startsWith("data:")) return "";
  const match = value.match(/^data:([^;,]+)/i);
  return match?.[1] || "";
};

// --- RICH TEXT RENDERER ---
const parseBold = (text: string) => {
  return text.split(/(\*\*.*?\*\*)/g).map((part, j) => 
    part.startsWith('**') && part.endsWith('**') 
      ? <strong key={j} className="text-white font-bold">{part.slice(2, -2)}</strong> 
      : part
  );
};

const renderMarkdown = (text: string) => {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    // Headers
    if (line.startsWith('## ')) return <h3 key={i} className="text-lg font-bold mt-4 mb-2 border-b pb-1" style={{ color: atlasColors.accent, borderColor: atlasColors.border }}>{line.replace('## ', '')}</h3>;
    if (line.startsWith('### ')) return <h4 key={i} className="text-md font-bold mt-3 mb-1" style={{ color: atlasColors.textPrimary }}>{line.replace('### ', '')}</h4>;
    if (line.startsWith('# ')) return <h2 key={i} className="text-xl font-bold text-slate-800 mt-5 mb-3">{line.replace('# ', '')}</h2>;
    
    const trimmed = line.trim();
    // Lists
    if (trimmed.startsWith('* ') || trimmed.startsWith('- ') || trimmed.startsWith('â€¢ ')) {
       return (
         <div key={i} className="flex gap-2 mb-1 ml-1">
            <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0 block" style={{ background: atlasColors.accent }}></span>
            <span className="text-sm leading-relaxed" style={{ color: atlasColors.textPrimary }}>{trimmed.replace(/^[*â€¢-]\s*/, '')}</span>
         </div>
       );
    }

    // Numbered Lists (1. 2. etc)
    if (/^\d+\.\s/.test(trimmed)) {
       return (
         <div key={i} className="flex gap-2 mb-1 ml-1">
            <span className="font-bold text-sm mt-0.5 shrink-0" style={{ color: atlasColors.accent }}>{trimmed.match(/^\d+\./)?.[0]}</span>
            <span className="text-sm leading-relaxed" style={{ color: atlasColors.textPrimary }}>{trimmed.replace(/^\d+\.\s*/, '')}</span>
         </div>
       );
    }

    // Empty lines
    if (!trimmed) return <div key={i} className="h-2" />;

    // Paragraphs
    return <p key={i} className="text-slate-700 text-sm leading-relaxed mb-1">{line}</p>;
  });
};

const renderMarkdownDark = (text: string) => {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    // Headers
    if (line.startsWith('## ')) return <h3 key={i} className="text-lg font-bold mt-4 mb-2 border-b pb-1" style={{ color: atlasColors.accent, borderColor: atlasColors.border }}>{line.replace('## ', '')}</h3>;
    if (line.startsWith('### ')) return <h4 key={i} className="text-md font-bold mt-3 mb-1" style={{ color: atlasColors.textPrimary }}>{line.replace('### ', '')}</h4>;
    if (line.startsWith('# ')) return <h2 key={i} className="text-xl font-bold mt-5 mb-3" style={{ color: atlasColors.textPrimary }}>{line.replace('# ', '')}</h2>;
    
    const trimmed = line.trim();
    // Lists
    if (trimmed.startsWith('* ') || trimmed.startsWith('- ') || trimmed.startsWith('â€¢ ')) {
       return (
         <div key={i} className="flex gap-2 mb-1 ml-1">
            <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0 block" style={{ background: atlasColors.accent }}></span>
            <span className="text-sm leading-relaxed" style={{ color: atlasColors.textPrimary }}>{parseBold(trimmed.replace(/^[*â€¢-]\s*/, ''))}</span>
         </div>
       );
    }

    // Numbered Lists (1. 2. etc)
    if (/^\d+\.\s/.test(trimmed)) {
       return (
         <div key={i} className="flex gap-2 mb-1 ml-1">
            <span className="font-bold text-sm mt-0.5 shrink-0" style={{ color: atlasColors.accent }}>{trimmed.match(/^\d+\./)?.[0]}</span>
            <span className="text-sm leading-relaxed" style={{ color: atlasColors.textPrimary }}>{parseBold(trimmed.replace(/^\d+\.\s*/, ''))}</span>
         </div>
       );
    }

    // Empty lines
    if (!trimmed) return <div key={i} className="h-2" />;

    // Paragraphs
    return <p key={i} className="text-sm leading-relaxed mb-1" style={{ color: atlasColors.textPrimary }}>{parseBold(line)}</p>;
  });
};

// Interface para os casos salvos
interface SavedCase {
  id: string;
  title: string;
  date: number;
  image: string; // Base64 da imagem estÃ¡tica
  plan: {
    diagnostico: string;
    diagnosticosDiferenciais: string[];
    materiais: string;
    tecnica: string;
    dicas: string;
    planoB: string;
    conduta?: string;
    anatomicalLocation?: string;
    classification?: string;
    severity?: string;
    classificationCode?: string;
    classificationLabel?: string;
    classificationDescription?: string;
    classificationSeverity?: "low" | "medium" | "high";
    level?: string;
    affectedRoot?: string;
    complementaryExams?: string[];
    preoperativePlanning?: string[];
    surgicalTechniqueOrConservativeProtocol?: string[];
    intraoperativeSteps?: string[];
    risksAndPitfalls?: string[];
    postoperativeCare?: string[];
    redFlags?: string[];
    scientificReferences?: string[];
  };
}

type SurgicalPlanState = {
  diagnostico: string;
  mainDiagnosis?: string;
  diagnosticosDiferenciais: string[];
  materiais: string;
  tecnica: string;
  dicas: string;
  planoB: string;
  conduta: string;
  anatomicalLocation?: string;
  classification?: string;
  classificationText?: string;
  severity?: string;
  classificationObject?: AootaClassificationState;
  classificationCode?: string;
  classificationLabel?: string;
  classificationDescription?: string;
  classificationSeverity?: "low" | "medium" | "high";
  level?: string;
  affectedRoot?: string;
  differentialDiagnoses?: string[];
  complementaryExams?: string[];
  preoperativePlanning?: string[];
  surgicalTechniqueOrConservativeProtocol?: string[];
  materials?: string[];
  intraoperativeSteps?: string[];
  risksAndPitfalls?: string[];
  planB?: string[];
  postoperativeCare?: string[];
  redFlags?: string[];
  scientificReferences?: string[];
  scientificEvidence?: string[];
  evidence?: string[];
  differentialDiagnosis?: string[];
  associatedInjuries?: string[];
  associatedLesions?: string[];
  recommendations?: string[];
  recommendation?: "conservative" | "surgical" | "uncertain";
  selectedStrategy?: "conservative" | "surgical" | null;
  conservativePlan?: {
    indication: string;
    protocol: string[];
    followUp: string[];
    redFlags: string[];
    conversionCriteria: string[];
  };
surgicalOption?: {
  indicationCriteria: string[];
  preferredTechnique: string;
  patientPositioning: string[];
  surgicalApproach: string;
  reductionStrategy: string[];
  fixationStrategy: string[];
  stepByStepTechnique: string[];
  implantsAndMaterials: string[];
  fluoroscopyOrImagingControl: string[];
  intraoperativeChecks: string[];
  closureAndImmobilization: string[];
  postoperativeProtocol: string[];
  risksAndPitfalls: string[];
  planB: string[];

  contingencyPlan?: {
    title?: string;
    summary?: string;
    scenarios?: {
      problem: string;
      action: string;
    }[];
    intraoperativeDecisionPoints?: string[];
    conversionOptions?: string[];
  };
};

contingencyPlan?: {
  title?: string;
  summary?: string;
  scenarios?: {
    problem: string;
    action: string;
  }[];
  intraoperativeDecisionPoints?: string[];
  conversionOptions?: string[];
};

surgicalTechniques?: SurgicalTechniqueSection[];
techniqueComparison?: string;
selectionCriteria?: string;
mostCurrentTechniqueTitle?: string;
aiRecommendedTechniqueTitle?: string;
aiRecommendationRationale?: string;
alternativeTechniqueRationale?: string;
};

type AtlasGenerationMode = '' | 'main-2d' | 'main-3d' | 'video-360';

type AtlasGenerationState = {
  active: boolean;
  type: AtlasGenerationMode;
  progress: number;
  stage: string;
};

const emptySurgicalPlanState: SurgicalPlanState = {
  diagnostico: "",
  diagnosticosDiferenciais: [],
  materiais: "",
  tecnica: "",
  dicas: "",
  planoB: "",
  conduta: "",
  recommendations: [],
  recommendation: "uncertain",
  selectedStrategy: null,
  conservativePlan: {
    indication: "",
    protocol: [],
    followUp: [],
    redFlags: [],
    conversionCriteria: [],
  },
  surgicalOption: {
    indicationCriteria: [],
    preferredTechnique: "",
    patientPositioning: [],
    surgicalApproach: "",
    reductionStrategy: [],
    fixationStrategy: [],
    stepByStepTechnique: [],
    implantsAndMaterials: [],
    fluoroscopyOrImagingControl: [],
    intraoperativeChecks: [],
    closureAndImmobilization: [],
    postoperativeProtocol: [],
    risksAndPitfalls: [],
    planB: [],
  },
  surgicalTechniques: [],
  techniqueComparison: "",
  selectionCriteria: "",
};

const initialAtlasGenerationState: AtlasGenerationState = {
  active: false,
  type: '',
  progress: 0,
  stage: '',
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const surgicalPlanAliases = {
  diagnostico: ["DIAGNOSTICO", "DIAGNOSIS", "DIAGNOSTICO", "DIAGNÃ“STICO", "DIAGNOSIS/CLASSIFICATION"],
  diagnosticosDiferenciais: ["DIAGNOSTICOS_DIFERENCIAIS", "DIAGNÃ“STICOS DIFERENCIAIS", "DIAGNOSTICOS DIFERENCIALES", "DIFFERENTIAL_DIAGNOSIS", "DIFFERENTIAL DIAGNOSIS"],
  conduta: ["CONDUTA", "SURGICAL_INDICATION", "SURGICAL INDICATION", "CLINICAL_DISPOSITION", "CLINICAL DISPOSITION"],
  materiais: ["MATERIAIS", "MATERIALS", "MATERIALES", "OPME", "MATERIAIS E OPME"],
  tecnica: ["TECNICA", "TECHNIQUE", "TECNICA CIRURGICA", "TÃ‰CNICA CIRÃšRGICA", "SURGICAL TECHNIQUE"],
  dicas: ["DICAS", "TIPS", "CONSEJOS", "PEARLS", "PITFALLS", "DICAS E ARMADILHAS"],
  planoB: ["PLANO_B", "PLAN_B", "PLANO B", "PLAN B", "CONTINGENCIA", "CONTINGÃŠNCIA", "CONTINGENCY"],
} as const;

const normalizeDifferentialDiagnosisList = (value?: string | string[]) => {
  const rawItems = Array.isArray(value)
    ? value
    : (value || "")
        .replace(/\r\n/g, "\n")
        .split(/\n|;|\|/)
        .map((item) => item.trim());

  return Array.from(
    new Set(
      rawItems
        .map((item) => item.replace(/^[-*â€¢\d.)\s]+/, "").trim())
        .filter(Boolean)
    )
  ).slice(0, 5);
};

const extractTaggedSection = (
  text: string,
  targetAliases: readonly string[],
  allAliases: readonly string[]
) => {
  const startPattern = targetAliases
    .map((alias) => `\\[${escapeRegExp(alias)}\\]|^\\s*${escapeRegExp(alias)}\\s*[:\\-]`)
    .join("|");
  const endPattern = allAliases
    .map((alias) => `\\[${escapeRegExp(alias)}\\]|^\\s*${escapeRegExp(alias)}\\s*[:\\-]`)
    .join("|");
  const regex = new RegExp(`(?:${startPattern})\\s*([\\s\\S]*?)(?=(?:${endPattern})|$)`, "gim");
  const match = regex.exec(text);
  return match?.[1]?.trim() || "";
};

const parseSurgicalPlanPayload = (rawText: string): SurgicalPlanState => {
  if (!rawText) return emptySurgicalPlanState;

  const normalized = rawText.replace(/\r\n/g, "\n").trim();
  const allAliases = Object.values(surgicalPlanAliases).flat();

  return {
    diagnostico: extractTaggedSection(normalized, surgicalPlanAliases.diagnostico, allAliases),
    diagnosticosDiferenciais: normalizeDifferentialDiagnosisList(
      extractTaggedSection(normalized, surgicalPlanAliases.diagnosticosDiferenciais, allAliases)
    ),
    conduta: extractTaggedSection(normalized, surgicalPlanAliases.conduta, allAliases),
    materiais: extractTaggedSection(normalized, surgicalPlanAliases.materiais, allAliases),
    tecnica: extractTaggedSection(normalized, surgicalPlanAliases.tecnica, allAliases),
    dicas: extractTaggedSection(normalized, surgicalPlanAliases.dicas, allAliases),
    planoB: extractTaggedSection(normalized, surgicalPlanAliases.planoB, allAliases),
  };
};

type ClinicalPlanSection = {
  indication?: string;
  protocol?: string[];
  followUp?: string[];
  redFlags?: string[];
  conversionCriteria?: string[];
};

type SurgicalOptionSection = {
  indicationCriteria?: string[];
  preferredTechnique?: string;
  patientPositioning?: string[];
  surgicalApproach?: string;
  reductionStrategy?: string[];
  fixationStrategy?: string[];
  stepByStepTechnique?: string[];
  implantsAndMaterials?: string[];
  fluoroscopyOrImagingControl?: string[];
  intraoperativeChecks?: string[];
  closureAndImmobilization?: string[];
  postoperativeProtocol?: string[];
  risksAndPitfalls?: string[];
  contingencyPlan?: {
    title: string;
    summary: string;
    scenarios: Array<{
      problem: string;
      action: string;
    }>;
    intraoperativeDecisionPoints: string[];
    conversionOptions: string[];
  };
  planB?: string[];
};

type SurgicalTechniqueSection = {
  title: string;
  indication: string;
  whenToChoose: string;
  advantages: string[];
  limitations: string[];
  materials: string[];
  stepByStep: string[];
  stepByStepTechnique?: string[];
  practicalNotes: string[];
  complications?: string[];
  recoveryTime?: string;
};

type AootaClassificationState = {
  system: "AO/OTA";
  code: string;
  isApproximate: boolean;
  severity: "LOW" | "MEDIUM" | "HIGH";
  description: string;
  rationale: string;
};

type StructuredClinicalPlanPayload = {
  recommendation?: "conservative" | "surgical" | "uncertain";
  selectedStrategy?: "conservative" | "surgical" | "";
  diagnosis?: string;
  mainDiagnosis?: string;
  anatomicalLocation?: string;
  classification?: AootaClassificationState | string;
  classificationText?: string;
  severity?: string;
  classificationObject?: AootaClassificationState | string;
  classificationCode?: string;
  classificationLabel?: string;
  classificationDescription?: string;
  classificationSeverity?: "low" | "medium" | "high";
  level?: string;
  affectedRoot?: string;
  differentialDiagnoses?: string[];
  differentialDiagnosis?: string[];
  associatedInjuries?: string[];
  associatedLesions?: string[];
  complementaryExams?: string[];
  preoperativePlanning?: string[];
  conservativePlan?: ClinicalPlanSection;
  surgicalOption?: SurgicalOptionSection;
  contingencyPlan?: string[];
  alternativePlan?: string[];
  planB?: string[];
  postoperativeCare?: string[];
  scientificReferences?: string[];
  scientificEvidence?: string[];
  evidence?: string[];
  surgicalTechniques?: Array<Partial<SurgicalTechniqueSection>>;
  materials?: string[];
  mostCurrentTechniqueTitle?: string;
  aiRecommendedTechniqueTitle?: string;
  aiRecommendationRationale?: string;
  alternativeTechniqueRationale?: string;
  techniqueComparison?: string;
  selectionCriteria?: string;
  clinicalInterpretation?: string;
  treatmentPlan?: string;
  surgicalIndicationReason?: string;
  recommendations?: string[];
  limitations?: string[];
  findings?: {
    region?: string;
    level?: string;
    discCondition?: string;
    herniaType?: string;
    nerveCompression?: boolean;
    severity?: string;
    confidence?: number;
    conflictWithPreviousDiagnosis?: boolean;
    correctedDiagnosis?: string;
  };
};

const SPINE_PLANNING_MARKERS = [
  "coluna",
  "spine",
  "vertebr",
  "disco",
  "disc",
  "hérnia",
  "hernia",
  "lombar",
  "cervical",
  "torác",
  "thorac",
  "radicul",
];

const isSpinePlanningCase = (value: string) => {
  const normalized = cleanAIText(value || "").toLowerCase();
  return SPINE_PLANNING_MARKERS.some((marker) => normalized.includes(marker));
};

const cleanPlanningPlaceholder = (value?: string) => {
  const text = cleanAIText(value || "").trim();
  const normalized = text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (!text) return "";
  if (
    normalized === "nenhum" ||
    normalized === "none" ||
    normalized === "nao aplicavel" ||
    normalized === "not applicable" ||
    normalized === "n/a" ||
    normalized === "achado musculoesqueletico em avaliacao" ||
    normalized === "musculoskeletal finding under evaluation" ||
    normalized === "diagnostico a confirmar" ||
    normalized === "diagnosis to be confirmed" ||
    normalized === "patologia nao especificada" ||
    normalized === "unspecified pathology" ||
    normalized === "lesao ortopedica" ||
    normalized === "orthopedic lesion" ||
    normalized === "localizacao anatomica a confirmar" ||
    normalized === "anatomical location to confirm" ||
    normalized === "regiao anatomica a confirmar" ||
    normalized === "anatomical region to confirm"
  ) {
    return "";
  }

  return text.replace(/\s+em\s+Nenhum\b/gi, "").trim();
};

const preferClinicalText = (...values: Array<string | undefined | null>) => {
  for (const value of values) {
    const cleaned = cleanPlanningPlaceholder(String(value || ""));
    if (cleaned) return cleaned;
  }
  return "";
};

const strategyLabel = (
  value: SurgicalPlanState["recommendation"] | "mixed" | null | undefined,
  language: string
) => {
  if (value === "conservative") {
    return language === "pt" ? "Tratamento conservador" : language === "es" ? "Tratamiento conservador" : "Conservative treatment";
  }
  if (value === "surgical") {
    return language === "pt" ? "Técnica cirúrgica" : language === "es" ? "Técnica quirúrgica" : "Surgical technique";
  }
  return language === "pt" ? "Avaliação médica necessária" : language === "es" ? "Evaluación médica necesaria" : "Medical evaluation required";
};

const normalizeAootaDisplayCode = (code?: string, label?: string) => {
  const raw = cleanPlanningPlaceholder(code || label || "");
  if (!raw) return "";
  if (/aproximad|indefinid|nao aplicavel|não aplicável|not applicable|classifica[cç][aã]o ao\/ota n[aã]o definida/i.test(raw)) return "";
  if (isValidAoOtaCode(raw)) return raw.toUpperCase().replace(/AO\/OTA/i, "AO/OTA");
  if (/^\d{1,2}(?:-[A-Z0-9]+)?$/i.test(raw)) return `AO/OTA ${raw.toUpperCase()}`;
  return `AO/OTA ${raw.replace(/^AO\/OTA\s*/i, "").trim()}`;
};

const normalizeAootaDisplayLabel = (label?: string, code?: string) => {
  const raw = cleanPlanningPlaceholder(label || code || "");
  if (!raw) return "";
  return raw.replace(/^AO\/OTA\s*/i, "").trim();
};

const normalizeAootaSeverityLabel = (value?: string) => {
  const normalized = cleanPlanningPlaceholder(value || "").toUpperCase();
  if (normalized.includes("HIGH") || normalized.includes("GRAVE") || normalized.includes("ALTO")) return "HIGH" as const;
  if (normalized.includes("LOW") || normalized.includes("LEVE") || normalized.includes("BAIXO")) return "LOW" as const;
  return "MEDIUM" as const;
};

const normalizeAoOtaSearchText = (value?: string) =>
  cleanAIText(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const isValidAoOtaCode = (code?: string) => {
  const raw = cleanPlanningPlaceholder(code || "");
  return /^AO\/OTA\s+\d{1,2}(?:-[A-Z0-9]+)?$/i.test(raw);
};

const getAoOtaByDiagnosisText = (text: string) => {
  const source = normalizeAoOtaSearchText(text);
  if (!source) return "";

  const hasTibiaDiaphysisPattern =
    /diafise da tibia|diafise tibial|tibia e fibula|tibial shaft|shaft tibial|fratura diafisica da tibia|fratura diafisica tibial|tibia\/fibula|tibiofib|diaphyseal tibia|diaphyseal fibula/.test(source);

  if (/umero distal|humero distal|distal humer|supracondyl|supracondilar|supracondil|cotovelo/.test(source) && !/cabeca do radio|cabeca radial|radial head|colo do radio|radio proximal|ulna proximal|olecrano/.test(source)) {
    return "AO/OTA 13-A";
  }
  if (/umero proximal|humero proximal|ombro|shoulder|proximal humer/.test(source)) return "AO/OTA 11";
  if (/diafise do umero|diafise do humero|diafise humeral|humeral shaft|humero diafis|umero diafis|humerus shaft/.test(source)) return "AO/OTA 12";
  if (/cabeca do radio|cabeça do radio|col[oó] do radio|radio proximal|ulna proximal|olecrano|olécrano|radial head|radial neck|proximal ulna|proximal radius/.test(source)) {
    return "AO/OTA 21";
  }
  if (/diafise do radio|diafise da ulna|radio diafis|ulna diafis|forearm shaft|radius shaft|ulna shaft/.test(source)) return "AO/OTA 22";
  if (/radio distal|distal radius|rist|punho/.test(source)) return "AO/OTA 23";
  if (/femur proximal|femoral neck|colo femoral|intertrocanter|intertrocanterica|proximal femur/.test(source)) return "AO/OTA 31";
  if (/diafise do femur|femoral shaft|femur diafis/.test(source)) return "AO/OTA 32";
  if (/femur distal|distal femur|supracondyl femur/.test(source)) return "AO/OTA 33";
  if (/patela|patellar/.test(source)) return "AO/OTA 34-C";
  if (/tibia proximal|plat[oó] tibial|planalto tibial|tibial plateau/.test(source)) return "AO/OTA 41";
  if (hasTibiaDiaphysisPattern) {
    if (/cominut|multifragment|complex|fragmentad/.test(source)) return "AO/OTA 42-C";
    if (/cunha|borboleta|butterfly|wedge/.test(source)) return "AO/OTA 42-B";
    if (/simples|transvers|obliqu|espiral|spiral/.test(source)) return "AO/OTA 42-A";
    return "AO/OTA 42";
  }
  if (/tibia distal|pilao tibial|pilon tibial|distal tibia/.test(source)) return "AO/OTA 43";
  if (/tornozelo|maleol|mal[eé]olo|ankle/.test(source)) return "AO/OTA 44";
  if (/calcaneo|calcaneus/.test(source)) return "AO/OTA 82";
  if (/metatar|falange|dedo do pe|pe\b/.test(source)) return "AO/OTA 87";

  return "";
};

type AoOtaInferencePlan = {
  classificationObject?: Partial<AootaClassificationState>;
  classificationCode?: string;
  classificationLabel?: string;
  classificationDescription?: string;
  classificationSeverity?: "low" | "medium" | "high" | "LOW" | "MEDIUM" | "HIGH";
  classification?: string | AootaClassificationState;
  diagnosis?: string;
  mainDiagnosis?: string;
  anatomicalLocation?: string;
  bone?: string;
  lesionClassification?: string;
  visualDiagnosticCheck?: {
    bone?: string;
    joint?: string;
    anatomicalRegion?: string;
    specificLocation?: string;
    suspectedDiagnosis?: string;
    visualEvidence?: string[];
    confidence?: number;
    uncertaintyReason?: string | null;
  };
};

const inferAoOtaClassificationFromDiagnosis = (
  payload: AoOtaInferencePlan & { surgicalTechniqueTitles?: string[] }
): AootaClassificationState => {
  const classificationFromPayload = typeof payload.classification === "string" ? payload.classification : payload.classification?.code;
  const sourceText = cleanPlanningPlaceholder([
    payload.diagnosis,
    payload.mainDiagnosis,
    payload.anatomicalLocation,
    payload.bone,
    payload.lesionClassification,
    ...(payload.surgicalTechniqueTitles || []),
    payload.visualDiagnosticCheck?.bone,
    payload.visualDiagnosticCheck?.joint,
    payload.visualDiagnosticCheck?.anatomicalRegion,
    payload.visualDiagnosticCheck?.specificLocation,
    payload.visualDiagnosticCheck?.suspectedDiagnosis,
  ].filter(Boolean).join(" "));
  const rawCode = normalizeAootaDisplayCode(
    payload.classificationObject?.code || payload.classificationCode || classificationFromPayload,
    payload.classificationObject?.code || payload.classificationLabel || classificationFromPayload
  );
  const inferredCode = getAoOtaByDiagnosisText(sourceText);
  const rawCodeIsValid = isValidAoOtaCode(rawCode);
  const code = rawCodeIsValid ? rawCode : inferredCode;
  const hasValidCode = isValidAoOtaCode(code);
  const isApproximate = Boolean(payload.classificationObject?.isApproximate) || !rawCodeIsValid || (Boolean(code) && code !== rawCode);
  const severity = normalizeAootaSeverityLabel(
    payload.classificationObject?.severity ||
    payload.classificationSeverity ||
    payload.classificationObject?.severity?.toString() ||
    "MEDIUM"
  );
  const approximateDescription = (() => {
    if (!hasValidCode) return "Classificação AO/OTA não definida.";
    if (/AO\/OTA\s+13-A/i.test(code)) {
      return "Fratura extra-articular do úmero distal, padrão supracondiliano. Classificação aproximada baseada nos achados radiográficos.";
    }
    if (/AO\/OTA\s+42-C/i.test(code)) {
      return "Fratura complexa/cominutiva da diáfise da tíbia/fíbula. Classificação aproximada baseada nos achados radiográficos.";
    }
    if (/AO\/OTA\s+42-B/i.test(code)) {
      return "Fratura diafisária da tíbia/fíbula com cunha. Classificação aproximada baseada nos achados radiográficos.";
    }
    if (/AO\/OTA\s+42-A/i.test(code)) {
      return "Fratura diafisária simples da tíbia/fíbula. Classificação aproximada baseada nos achados radiográficos.";
    }
    if (/AO\/OTA\s+21/i.test(code)) {
      return "Fratura da região proximal do antebraço. Classificação aproximada baseada nos achados radiográficos.";
    }
    if (/AO\/OTA\s+23/i.test(code)) {
      return "Fratura do rádio distal. Classificação aproximada baseada nos achados radiográficos.";
    }
    if (/AO\/OTA\s+34-C/i.test(code)) {
      return "Fratura patelar complexa. Classificação aproximada baseada nos achados radiográficos.";
    }
    return "Classificação AO/OTA aproximada baseada nos achados radiográficos.";
  })();
  const description =
    cleanPlanningPlaceholder(payload.classificationObject?.description || payload.classificationDescription || "") ||
    approximateDescription;
  const rationale =
    cleanPlanningPlaceholder(payload.classificationObject?.rationale || "") ||
    (isApproximate
      ? "Classificação aproximada baseada no diagnóstico e na localização anatômica informados."
      : "Classificação estruturada com base nos dados fornecidos.");

  return {
    system: "AO/OTA",
    code: hasValidCode ? code : "Classificação AO/OTA não definida",
    isApproximate,
    severity,
    description,
    rationale,
  };
};

const buildAootaClassificationState = inferAoOtaClassificationFromDiagnosis;

const parseStructuredClinicalPlanPayload = (rawText: string): StructuredClinicalPlanPayload => {
  const normalized = normalizeSurgicalPlanResponse(rawText);
  return {
    recommendation: normalized.recommendation,
    selectedStrategy: normalized.selectedStrategy ?? "",
    diagnosis: normalized.diagnosis,
    mainDiagnosis: normalized.mainDiagnosis,
    anatomicalLocation: normalized.anatomicalLocation,
    classification: normalized.classification,
    classificationObject: normalized.classification,
    classificationCode: normalized.classificationCode,
    classificationLabel: normalized.classificationLabel,
    classificationDescription: normalized.classificationDescription,
    classificationSeverity: normalized.classificationSeverity,
    severity: normalized.severity,
    level: normalized.level,
    affectedRoot: normalized.affectedRoot,
    differentialDiagnoses: normalized.differentialDiagnoses,
    complementaryExams: normalized.complementaryExams,
    preoperativePlanning: normalized.preoperativePlanning,
    conservativePlan: normalized.conservativePlan,
    surgicalOption: normalized.surgicalOption,
    contingencyPlan: normalized.contingencyPlan,
    alternativePlan: normalized.alternativePlan,
    planB: normalized.planB,
    postoperativeCare: normalized.postoperativeCare,
    scientificReferences: normalized.scientificReferences,
    scientificEvidence: normalized.scientificEvidence,
    evidence: normalized.evidence,
    differentialDiagnosis: normalized.differentialDiagnosis,
    associatedInjuries: normalized.associatedInjuries,
    associatedLesions: normalized.associatedLesions,
    surgicalTechniques: normalized.surgicalTechniques,
    materials: normalized.materials,
    techniqueComparison: normalized.techniqueComparison,
    selectionCriteria: normalized.selectionCriteria,
    clinicalInterpretation: normalized.clinicalInterpretation,
    treatmentPlan: normalized.treatmentPlan,
    surgicalIndicationReason: normalized.surgicalIndicationReason,
    mostCurrentTechniqueTitle: normalized.mostCurrentTechniqueTitle,
    aiRecommendedTechniqueTitle: normalized.aiRecommendedTechniqueTitle,
    aiRecommendationRationale: normalized.aiRecommendationRationale,
    alternativeTechniqueRationale: normalized.alternativeTechniqueRationale,
    recommendations: normalized.recommendations,
  };
};

const normalizeSurgicalTechniqueSections = (items?: Array<Partial<SurgicalTechniqueSection>>) =>
  (items || [])
    .slice(0, 2)
    .map((item) => ({
      title: cleanAIText(item.title || ""),
      indication: cleanAIText(item.indication || ""),
      whenToChoose: cleanAIText(item.whenToChoose || ""),
      advantages: Array.isArray(item.advantages) ? item.advantages.map((entry) => cleanAIText(entry)).filter(Boolean) : [],
      limitations: Array.isArray(item.limitations) ? item.limitations.map((entry) => cleanAIText(entry)).filter(Boolean) : [],
      materials: Array.isArray(item.materials) ? item.materials.map((entry) => cleanAIText(entry)).filter(Boolean) : [],
      stepByStep: Array.isArray(item.stepByStep) ? item.stepByStep.map((entry) => cleanAIText(entry)).filter(Boolean) : [],
      stepByStepTechnique: Array.isArray(item.stepByStepTechnique) ? item.stepByStepTechnique.map((entry) => cleanAIText(entry)).filter(Boolean) : [],
      practicalNotes: Array.isArray(item.practicalNotes) ? item.practicalNotes.map((entry) => cleanAIText(entry)).filter(Boolean) : [],
      complications: Array.isArray(item.complications) ? item.complications.map((entry) => cleanAIText(entry)).filter(Boolean) : [],
      recoveryTime: cleanAIText(item.recoveryTime || ""),
    }))
    .filter((item) => Boolean(item.title || item.indication || item.whenToChoose || item.advantages.length || item.limitations.length || item.materials.length || item.stepByStep.length || item.stepByStepTechnique?.length || item.practicalNotes.length || item.complications?.length || item.recoveryTime));

const flattenTechniqueMaterials = (items?: SurgicalTechniqueSection[]) =>
  (items || [])
    .flatMap((item) => Array.isArray(item.materials) ? item.materials : [])
    .map((item) => cleanAIText(item))
    .filter(Boolean);

const dedupeList = (items: string[]) => Array.from(new Set(items.map((item) => item.toLowerCase().trim()).filter(Boolean))).map((key) => items.find((item) => item.toLowerCase().trim() === key) || key);

const buildCaseAwareFallbackTechniques = (
  diagnosis: string,
  anatomicalLocation: string,
  classificationCode: string,
  classificationLabel: string
): SurgicalTechniqueSection[] => {
  const baseDiagnosis = cleanAIText(diagnosis || "");
  const baseLocation = cleanAIText(anatomicalLocation || "");
  const baseClassification = cleanAIText(classificationCode || classificationLabel || "");
  const signature = `${baseDiagnosis} ${baseLocation} ${baseClassification}`.toLowerCase();
  const targetLabel = baseLocation || baseDiagnosis || baseClassification || "lesão ortopédica";
  const codeLabel = baseClassification || "classificação AO/OTA";

  const anatomicalHint = (() => {
    if (signature.includes("úmero") || signature.includes("humer")) return "úmero distal";
    if (signature.includes("rádio") || signature.includes("radius")) return "rádio distal";
    if (signature.includes("ulna") || signature.includes("cúbito") || signature.includes("cubitus")) return "ulna";
    if (signature.includes("fêmur") || signature.includes("femur")) return "fêmur";
    if (signature.includes("tíbia") || signature.includes("tibia")) return "tíbia";
    if (signature.includes("fíbula") || signature.includes("fibula")) return "fíbula";
    if (signature.includes("clavícula") || signature.includes("clavicula")) return "clavícula";
    if (signature.includes("coluna") || signature.includes("spine") || signature.includes("vértebra") || signature.includes("vertebra")) return "coluna";
    return targetLabel;
  })();
  const hasOlecranonCase = /olecrano|olecrân|proximal ulna|ulna proximal/.test(signature);
  const hasComminution = /cominut|multifragment|fragmentad|complex/.test(signature);
  const sharedMaterials = dedupeList([
    "Placa bloqueada anatômica ou sistema compatível com o padrão da lesão",
    "Parafusos corticais e bloqueados conforme fragmentos",
    "Intensificador de imagem",
    "Instrumental de redução e estabilização",
  ]);
  const primaryTechniqueTitle = hasOlecranonCase
    ? (hasComminution ? "Placa bloqueada do olécrano" : "Banda de tensão do olécrano")
    : (baseClassification ? `Osteossíntese orientada por ${baseClassification}` : `Osteossíntese de ${anatomicalHint}`);
  const alternativeTechniqueTitle = hasOlecranonCase
    ? (hasComminution ? "Banda de tensão do olécrano como contingência" : "Placa bloqueada do olécrano")
    : (baseClassification ? `Estratégia alternativa para ${baseClassification}` : `Fixação alternativa para ${anatomicalHint}`);
  const primaryMaterials = hasOlecranonCase
    ? dedupeList([
        "Fios de Kirschner",
        "Fio de cerclagem ou banda de tensão",
        "Broca e instrumental de redução",
        "Intensificador de imagem",
        "Placa bloqueada do olécrano como reserva",
      ])
    : sharedMaterials;
  const alternativeMaterials = hasOlecranonCase
    ? dedupeList([
        "Placa bloqueada do olécrano",
        "Parafusos corticais e bloqueados",
        "Instrumental de redução",
        "Intensificador de imagem",
      ])
    : dedupeList([
        ...sharedMaterials,
        "Implantes complementares para contingência",
      ]);
  const primarySteps = hasOlecranonCase
    ? [
        "Confirmar redução anatômica da superfície articular do olécrano.",
        hasComminution
          ? "Preferir placa bloqueada se houver cominuição, instabilidade ou osso fragilizado."
          : "Usar banda de tensão se o traço for simples, transverso e com fragmentos adequados.",
        "Checar integridade do mecanismo extensor e estabilidade final com fluoroscopia.",
      ]
    : [
        "Confirmar diagnóstico, localização anatômica e classificação AO/OTA inferida.",
        "Definir posicionamento e abordagem cirúrgica conforme a anatomia e a estabilidade.",
        "Realizar redução e fixação com controle fluoroscópico e conferência final.",
      ];
  const alternativeSteps = hasOlecranonCase
    ? [
        "Reservar placa bloqueada como estratégia de maior estabilidade quando a banda de tensão não for suficiente.",
        "Reavaliar cominuição, estabilidade e qualidade óssea antes da fixação definitiva.",
        "Manter plano de conversão intraoperatória se a redução não for mantida.",
      ]
    : [
        "Reavaliar o padrão da lesão e os achados intraoperatórios.",
        "Escolher a fixação com melhor equilíbrio entre estabilidade e preservação de partes moles.",
        "Executar plano B caso a redução ou a estabilidade não sejam ideais.",
      ];

  return [
    {
      title: primaryTechniqueTitle,
      indication: hasOlecranonCase
        ? `Fratura do olécrano com ${hasComminution ? "cominuição ou instabilidade" : "padrão transverso simples"} e decisão guiada pela mecânica do extensor.`
        : `Lesão em ${targetLabel} com planejamento cirúrgico preliminar guiado por ${codeLabel}.`,
      whenToChoose: hasOlecranonCase
        ? "Quando a redução anatômica for factível e o padrão articular permitir estabilização confiável."
        : "Quando a correlação clínico-radiológica sustentar tratamento operatório.",
      advantages: hasOlecranonCase
        ? [
            "Técnica alinhada ao padrão do olécrano.",
            hasComminution ? "Favorece estabilidade em traços com cominuição." : "Boa para traços simples com fragmentos grandes.",
          ]
        : [
            "Permite planejamento inicial específico para a anatomia envolvida.",
            "Favorece definição de abordagem, redução e fixação conforme o padrão da lesão.",
          ],
      limitations: hasOlecranonCase
        ? [
            "Exige conferência articular cuidadosa.",
            hasComminution ? "Pode exigir conversão para placa se a banda de tensão não sustentar a redução." : "Depende da qualidade óssea e do tamanho dos fragmentos.",
          ]
        : [
            "Exige revisão da imagem original, do desvio e das partes moles.",
            "A definição final depende de exame físico e imagens complementares quando necessário.",
          ],
      materials: primaryMaterials,
      stepByStep: primarySteps,
      practicalNotes: hasOlecranonCase
        ? [
            "Preservar a congruência da superfície articular.",
            "Checar o mecanismo extensor ao final da fixação.",
          ]
        : [
            "Revisar alinhamento articular e estabilidade antes da fixação definitiva.",
            "Considerar tomografia quando houver dúvida sobre extensão articular ou cominuição.",
          ],
    },
    {
      title: alternativeTechniqueTitle,
      indication: hasOlecranonCase
        ? "Alternativa quando a estabilidade exigida superar o que a banda de tensão oferece ou quando a cominuição dificultar a compressão."
        : `Alternativa caso a estratégia principal não seja a mais segura para ${targetLabel}.`,
      whenToChoose: hasOlecranonCase
        ? "Quando houver cominuição, instabilidade, osso fragilizado ou falha esperada da banda de tensão."
        : "Quando houver necessidade de adaptação por qualidade óssea, cominuição ou partes moles.",
      advantages: hasOlecranonCase
        ? [
            "Maior estabilidade em padrões cominutos.",
            "Melhor controle da redução articular em casos complexos.",
          ]
        : [
            "Mantém flexibilidade intraoperatória diante de variações anatômicas.",
            "Permite adaptar a fixação conforme estabilidade e fragmentação.",
          ],
      limitations: hasOlecranonCase
        ? [
            "Pode demandar maior exposição e tempo cirúrgico.",
            "Exige avaliação cuidadosa do trajeto do implante e partes moles.",
          ]
        : [
            "Pode exigir materiais adicionais e maior necessidade de imagem intraoperatória.",
            "Depende da avaliação do cirurgião e da disponibilidade de implantes.",
          ],
      materials: alternativeMaterials,
      stepByStep: alternativeSteps,
      practicalNotes: hasOlecranonCase
        ? [
            "Manter opção de placa bloqueada pronta se a banda de tensão não sustentar a fixação.",
            "Registrar decisão final conforme qualidade óssea e estabilidade observada.",
          ]
        : [
            "Manter estratégia de contingência preparada antes da incisão.",
            "Documentar limitações anatômicas e razões da escolha final.",
          ],
    },
  ];
};

const buildClinicalPlanState = (
  payload: StructuredClinicalPlanPayload,
  options?: { suppressSyntheticFallback?: boolean }
): SurgicalPlanState => {
  const findings = payload.findings || {};
  const level = String(payload.level || findings.level || "").trim();
  const region = preferClinicalText(findings.region, payload.anatomicalLocation, (payload as any).location, (payload as any).localizacaoAnatomica);
  const recommendation = (String(payload.recommendation || "").toLowerCase() as SurgicalPlanState["recommendation"]) || "uncertain";
  const selectedStrategy =
    recommendation === "uncertain"
      ? null
      : (String(payload.selectedStrategy || recommendation).toLowerCase() as "conservative" | "surgical");

  const conservativePlan = payload.conservativePlan || {};
  const surgicalOption = payload.surgicalOption || {};
  const surgicalTechniquesFromAi = normalizeSurgicalTechniqueSections(payload.surgicalTechniques);
  const payloadDiagnosis = preferClinicalText(
    payload.diagnosis,
    payload.mainDiagnosis,
    (payload as any).primaryDiagnosis,
    (payload as any).diagnostico,
    payload.clinicalInterpretation
  );
  const baseDiagnosis = cleanAIText(
    payloadDiagnosis ||
      (region ? `Achado musculoesquelético em ${region}` : "Achado musculoesquelético em avaliação")
  );
  const chosenIsSurgical = selectedStrategy === "surgical";
  const selectedTechniqueItems = chosenIsSurgical
    ? surgicalOption.stepByStepTechnique || []
    : conservativePlan.protocol || [];
  const selectedMaterialsItems = chosenIsSurgical ? surgicalOption.implantsAndMaterials || [] : [];
  const selectedPitfallsItems = chosenIsSurgical ? surgicalOption.risksAndPitfalls || [] : conservativePlan.redFlags || [];
  const selectedPlanBItems = chosenIsSurgical 
    ? (surgicalOption.contingencyPlan?.scenarios?.length 
        ? surgicalOption.contingencyPlan.scenarios.map(s => `${s.problem}: ${s.action}`)
        : surgicalOption.planB || [])
    : conservativePlan.conversionCriteria || [];
  const selectedIndication = chosenIsSurgical
    ? surgicalOption.preferredTechnique || payload.treatmentPlan || payload.surgicalIndicationReason || ""
    : conservativePlan.indication || payload.treatmentPlan || payload.surgicalIndicationReason || "";
  const allowSyntheticFallback = options?.suppressSyntheticFallback !== true;
  const summaryPlanText = allowSyntheticFallback
    ? (chosenIsSurgical
        ? [
            surgicalOption.preferredTechnique,
            ...(surgicalOption.stepByStepTechnique || []),
          ].filter(Boolean).join(" • ")
        : [
            conservativePlan.indication,
            ...(conservativePlan.protocol || []),
          ].filter(Boolean).join(" • "))
    : "";
  const recommendations = Array.isArray(payload.recommendations)
    ? payload.recommendations.map((item) => cleanAIText(item)).filter(Boolean)
    : [];
  const differentialDiagnoses = Array.isArray(payload.differentialDiagnoses)
    ? payload.differentialDiagnoses.map((item) => cleanAIText(item)).filter(Boolean)
    : [];
  const scientificEvidence = Array.isArray(payload.scientificEvidence)
    ? payload.scientificEvidence.map((item) => cleanAIText(item)).filter(Boolean)
    : Array.isArray(payload.scientificReferences)
      ? payload.scientificReferences.map((item) => cleanAIText(item)).filter(Boolean)
      : [];
  const fallbackDifferentialDiagnoses = allowSyntheticFallback && differentialDiagnoses.length === 0
    ? dedupeList([
        baseDiagnosis ? `Seguimento de ${baseDiagnosis}` : "",
        region ? `Lesão associada em ${region}` : "",
        (payload.classificationCode || payload.classificationLabel) ? `Padrão anatômico compatível com ${cleanAIText(String(payload.classificationCode || payload.classificationLabel || ""))}` : "",
      ].filter(Boolean))
    : differentialDiagnoses;
  const classificationFromPayload = typeof payload.classification === "string" ? payload.classification : payload.classification?.code;
  const classificationCodeText = normalizeAootaDisplayCode(payload.classificationCode, payload.classificationLabel || classificationFromPayload);
  const classificationLabelText = normalizeAootaDisplayLabel(payload.classificationLabel, payload.classificationCode || classificationFromPayload);
  const fallbackTechnique = allowSyntheticFallback
    ? buildCaseAwareFallbackTechniques(baseDiagnosis, region, classificationCodeText, classificationLabelText)
    : [];
  const surgicalTechniques = surgicalTechniquesFromAi.length > 0 ? surgicalTechniquesFromAi : fallbackTechnique;
  const aiTechniqueMaterials = dedupeList(flattenTechniqueMaterials(surgicalTechniques));
  const aiTechniqueSteps = dedupeList((surgicalTechniques || []).flatMap((item) => item.stepByStep || []).map((item) => cleanAIText(item)).filter(Boolean));
  const aiTechniqueNotes = dedupeList((surgicalTechniques || []).flatMap((item) => item.practicalNotes || []).map((item) => cleanAIText(item)).filter(Boolean));
  const techniqueComparison = cleanAIText(
    payload.techniqueComparison ||
      (allowSyntheticFallback && surgicalTechniques.length > 1
        ? "Comparação entre as técnicas sugeridas disponível para discussão com base na anatomia, estabilidade e morbidade."
        : allowSyntheticFallback && surgicalTechniques.length === 1
          ? "Uma técnica principal está sustentada pelo caso; a alternativa fica reservada para contingência intraoperatória."
          : "")
  );
  const selectionCriteria = cleanAIText(
    payload.selectionCriteria ||
      (allowSyntheticFallback && surgicalTechniques.length > 1
        ? "A escolha final deve considerar localização exata, padrão da lesão, estabilidade, qualidade óssea, partes moles e experiência do cirurgião."
        : allowSyntheticFallback && surgicalTechniques.length === 1
          ? "A escolha final deve considerar estabilidade, desvio, partes moles e a melhor opção de contingência."
          : "")
  );
  const classificationObject = buildAootaClassificationState({
    classificationObject: typeof payload.classificationObject === "string" ? undefined : payload.classificationObject,
    classificationCode: payload.classificationCode || classificationCodeText,
    classificationLabel: classificationLabelText,
    classificationDescription: payload.classificationDescription,
    classificationSeverity: (payload.classificationSeverity || "medium") as "low" | "medium" | "high",
    classification: payload.classification,
    diagnosis: baseDiagnosis,
    mainDiagnosis: payloadDiagnosis || payload.diagnosis || payload.mainDiagnosis || "",
    anatomicalLocation: payload.anatomicalLocation || (payload as any).location || (payload as any).localizacaoAnatomica || region,
  });

  return {
    mainDiagnosis: baseDiagnosis,
    diagnostico: baseDiagnosis,
    diagnosticosDiferenciais: fallbackDifferentialDiagnoses,
    differentialDiagnosis: fallbackDifferentialDiagnoses,
    associatedInjuries: fallbackDifferentialDiagnoses,
    associatedLesions: fallbackDifferentialDiagnoses,
    materiais: dedupeList([
      ...(Array.isArray(payload.materials) ? payload.materials : []),
      ...aiTechniqueMaterials,
      ...selectedMaterialsItems.map((item) => cleanAIText(item)).filter(Boolean),
    ].map((item) => cleanAIText(item)).filter(Boolean)).join("\n"),
    materials: dedupeList([
      ...(Array.isArray(payload.materials) ? payload.materials : []),
      ...aiTechniqueMaterials,
      ...selectedMaterialsItems.map((item) => cleanAIText(item)).filter(Boolean),
    ].map((item) => cleanAIText(item)).filter(Boolean)),
    tecnica: cleanAIText(
      (surgicalTechniques.length > 0
        ? [
            ...surgicalTechniques.map((item) => item.title).filter(Boolean),
            ...surgicalTechniques.flatMap((item) => item.stepByStep || []),
          ].join(" • ")
        : summaryPlanText) || payload.treatmentPlan || ""
    ),
    dicas: cleanAIText(
      payload.surgicalIndicationReason ||
      payload.clinicalInterpretation ||
      (aiTechniqueNotes.length > 0 ? aiTechniqueNotes.join(" • ") : (chosenIsSurgical ? (surgicalOption.surgicalApproach || "") : (conservativePlan.indication || "")))
    ),
    planoB: dedupeList([
      ...selectedPlanBItems.map((item) => cleanAIText(item)).filter(Boolean),
      ...aiTechniqueNotes,
    ].map((item) => cleanAIText(item)).filter(Boolean)).join("\n"),
    planB: dedupeList([
      ...selectedPlanBItems.map((item) => cleanAIText(item)).filter(Boolean),
      ...aiTechniqueNotes,
    ].map((item) => cleanAIText(item)).filter(Boolean)),
    conduta: strategyLabel(recommendation, "pt"),
    anatomicalLocation: cleanPlanningPlaceholder(payload.anatomicalLocation) || region || "Localização anatômica a confirmar",
    classification: classificationObject.code,
    classificationObject,
    classificationCode: classificationObject.code,
    classificationLabel: classificationObject.code.replace(/^AO\/OTA\s*/i, "").trim(),
    classificationDescription: classificationObject.description,
    classificationSeverity: classificationObject.severity.toLowerCase() as "low" | "medium" | "high",
    severity: cleanPlanningPlaceholder(payload.severity || findings.severity) || "Gravidade a correlacionar clinicamente",
    level,
    affectedRoot: cleanAIText(payload.affectedRoot || ""),
    differentialDiagnoses: fallbackDifferentialDiagnoses,
    complementaryExams: Array.isArray(payload.complementaryExams) ? payload.complementaryExams.map((item) => cleanAIText(item)).filter(Boolean) : [],
    preoperativePlanning: Array.isArray(payload.preoperativePlanning) ? payload.preoperativePlanning.map((item) => cleanAIText(item)).filter(Boolean) : [],
    surgicalTechniqueOrConservativeProtocol: chosenIsSurgical
      ? dedupeList([
          ...(surgicalOption.stepByStepTechnique || []).map((item) => cleanAIText(item)).filter(Boolean),
          ...aiTechniqueSteps,
        ].map((item) => cleanAIText(item)).filter(Boolean))
      : (conservativePlan.protocol || []).map((item) => cleanAIText(item)).filter(Boolean),
    intraoperativeSteps: chosenIsSurgical
      ? (surgicalOption.intraoperativeChecks || []).map((item) => cleanAIText(item)).filter(Boolean)
      : [],
    risksAndPitfalls: chosenIsSurgical
      ? (surgicalOption.risksAndPitfalls || []).map((item) => cleanAIText(item)).filter(Boolean)
      : (conservativePlan.redFlags || []).map((item) => cleanAIText(item)).filter(Boolean),
    postoperativeCare: chosenIsSurgical
      ? (surgicalOption.postoperativeProtocol || []).map((item) => cleanAIText(item)).filter(Boolean)
      : (conservativePlan.followUp || []).map((item) => cleanAIText(item)).filter(Boolean),
    redFlags: Array.isArray(conservativePlan.redFlags)
      ? conservativePlan.redFlags.map((item) => cleanAIText(item)).filter(Boolean)
      : [],
    scientificReferences: scientificEvidence,
    scientificEvidence,
    evidence: scientificEvidence,
    recommendation,
    selectedStrategy,
    surgicalTechniques,
    techniqueComparison,
    selectionCriteria,
    conservativePlan: {
      indication: cleanAIText(conservativePlan.indication || ""),
      protocol: Array.isArray(conservativePlan.protocol) ? conservativePlan.protocol.map((item) => cleanAIText(item)).filter(Boolean) : [],
      followUp: Array.isArray(conservativePlan.followUp) ? conservativePlan.followUp.map((item) => cleanAIText(item)).filter(Boolean) : [],
      redFlags: Array.isArray(conservativePlan.redFlags) ? conservativePlan.redFlags.map((item) => cleanAIText(item)).filter(Boolean) : [],
      conversionCriteria: Array.isArray(conservativePlan.conversionCriteria) ? conservativePlan.conversionCriteria.map((item) => cleanAIText(item)).filter(Boolean) : [],
    },
    surgicalOption: {
      indicationCriteria: Array.isArray(surgicalOption.indicationCriteria) ? surgicalOption.indicationCriteria.map((item) => cleanAIText(item)).filter(Boolean) : [],
      preferredTechnique: cleanAIText(surgicalOption.preferredTechnique || surgicalTechniques[0]?.title || ""),
      patientPositioning: Array.isArray(surgicalOption.patientPositioning) ? surgicalOption.patientPositioning.map((item) => cleanAIText(item)).filter(Boolean) : [],
      surgicalApproach: cleanAIText(surgicalOption.surgicalApproach || ""),
      reductionStrategy: Array.isArray(surgicalOption.reductionStrategy) && surgicalOption.reductionStrategy.length > 0
        ? surgicalOption.reductionStrategy.map((item) => cleanAIText(item)).filter(Boolean)
        : dedupeList(surgicalTechniques.flatMap((item) => item.advantages || [])),
      fixationStrategy: Array.isArray(surgicalOption.fixationStrategy) && surgicalOption.fixationStrategy.length > 0
        ? surgicalOption.fixationStrategy.map((item) => cleanAIText(item)).filter(Boolean)
        : dedupeList(surgicalTechniques.flatMap((item) => item.limitations || [])),
      stepByStepTechnique: Array.isArray(surgicalOption.stepByStepTechnique) && surgicalOption.stepByStepTechnique.length > 0
        ? surgicalOption.stepByStepTechnique.map((item) => cleanAIText(item)).filter(Boolean)
        : dedupeList(surgicalTechniques.flatMap((item) => item.stepByStep || [])),
      implantsAndMaterials: Array.isArray(surgicalOption.implantsAndMaterials) && surgicalOption.implantsAndMaterials.length > 0
        ? surgicalOption.implantsAndMaterials.map((item) => cleanAIText(item)).filter(Boolean)
        : aiTechniqueMaterials,
      fluoroscopyOrImagingControl: Array.isArray(surgicalOption.fluoroscopyOrImagingControl) ? surgicalOption.fluoroscopyOrImagingControl.map((item) => cleanAIText(item)).filter(Boolean) : [],
      intraoperativeChecks: Array.isArray(surgicalOption.intraoperativeChecks) ? surgicalOption.intraoperativeChecks.map((item) => cleanAIText(item)).filter(Boolean) : [],
      closureAndImmobilization: Array.isArray(surgicalOption.closureAndImmobilization) ? surgicalOption.closureAndImmobilization.map((item) => cleanAIText(item)).filter(Boolean) : [],
      postoperativeProtocol: Array.isArray(surgicalOption.postoperativeProtocol) ? surgicalOption.postoperativeProtocol.map((item) => cleanAIText(item)).filter(Boolean) : [],
      risksAndPitfalls: Array.isArray(surgicalOption.risksAndPitfalls) && surgicalOption.risksAndPitfalls.length > 0
        ? surgicalOption.risksAndPitfalls.map((item) => cleanAIText(item)).filter(Boolean)
        : dedupeList(surgicalTechniques.flatMap((item) => item.limitations || [])),
      planB: Array.isArray(surgicalOption.planB) && surgicalOption.planB.length > 0
        ? surgicalOption.planB.map((item) => cleanAIText(item)).filter(Boolean)
        : dedupeList(surgicalTechniques.flatMap((item) => item.practicalNotes || [])),
    },
  };
};

const normalizeSurgicalConduct = (value?: string) => {
  const normalized = (value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (!normalized) return "";
  if (
    normalized.includes("non-surgical") ||
    normalized.includes("nao cirurg") ||
    normalized.includes("nao-cirurg") ||
    normalized.includes("nao operat") ||
    normalized.includes("nao cirurgico") ||
    normalized.includes("nao cirurgica") ||
    normalized.includes("conservador") ||
    normalized.includes("conservative")
  ) {
    return "non-surgical";
  }
  if (
    normalized.includes("relative") ||
    normalized.includes("relativo") ||
    normalized.includes("indicacao relativa") ||
    normalized.includes("possivelmente_cirurgico") ||
    normalized.includes("possivelmente cirurgico")
  ) {
    return "relative";
  }
  if (normalized.includes("surgical") || normalized.includes("cirurg")) {
    return "surgical";
  }
  return "";
};

const parseScientificPlanResponse = (raw: string): ScientificPlanResponse | null => {
  if (!raw) return null;

  try {
    return JSON.parse(raw) as StructuredSurgicalPlanEvidence;
  } catch {
    return {
      diagnosis: "",
      differentialDiagnosis: [],
      materials: [],
      justification: "",
      surgicalIndication: "",
      classificationReason: "",
      surgicalTechnique: "",
      scientificEvidence: [],
      medicalDisclaimer: raw.trim(),
      tips: "",
      planB: "",
    };
  }
};

const getScientificIndicationLabel = (value: string | undefined, language: string) => {
  const normalized = normalizeSurgicalConduct(value);

  if (normalized === 'surgical') {
    return language === 'en' ? 'SURGICAL' : language === 'es' ? 'QUIRÚRGICO' : 'CIRÚRGICO';
  }

  if (normalized === 'relative') {
    return language === 'en'
      ? 'RELATIVE SURGICAL INDICATION'
      : language === 'es'
        ? 'INDICACIÓN RELATIVA DE CIRUGÍA'
        : 'INDICAÇÃO RELATIVA DE CIRURGIA';
  }

  if (normalized === 'non-surgical') {
    return language === 'en' ? 'NON-SURGICAL' : language === 'es' ? 'NO QUIRÚRGICO' : 'NÃO CIRÚRGICO';
  }

  return language === 'en' ? 'UNDER REVIEW' : language === 'es' ? 'EN ANÁLISIS' : 'EM ANÁLISE';
};

const getScientificSourceLabel = (item: ScientificEvidenceItem | string) => {
  if (typeof item === 'string') {
    return item.trim();
  }

  const source = (item.source || "").trim();
  const title = (item.title || "").trim();
  const combined =
    source && title && source.toLowerCase() !== title.toLowerCase()
      ? `${source} - ${title}`
      : source || title;

  const normalized = combined.replace(/\s+/g, " ").replace(/\s*[-–—]\s*/g, " - ").trim();
  return normalized.length > 84 ? `${normalized.slice(0, 81).trimEnd()}...` : normalized;
};

const getTopEvidenceForReport = (evidence: ScientificEvidenceItem[] = []) => {
  const normalized = (Array.isArray(evidence) ? evidence : [])
    .map((item) => ({
      ...item,
      yearNumber: Number.parseInt(String(item.year || ""), 10),
      url: String(item.url || "").trim(),
      source: String(item.source || "").trim(),
      title: String(item.title || "").trim(),
    }))
    .filter((item) => Number.isFinite(item.yearNumber) && item.yearNumber > 0);

  const seen = new Set<string>();

  return normalized
    .sort((a, b) => {
      const yearDelta = b.yearNumber - a.yearNumber;
      if (yearDelta !== 0) return yearDelta;

      const aValidated = /doi\.org|pubmed\.ncbi\.nlm\.nih\.gov|pubmed\.ncbi\.nlm\.nih\.gov|pubmed\.ncbi\.nlm\.nih\.gov/.test(a.url) || /doi|pubmed/i.test(a.source);
      const bValidated = /doi\.org|pubmed\.ncbi\.nlm\.nih\.gov|pubmed\.ncbi\.nlm\.nih\.gov|pubmed\.ncbi\.nlm\.nih\.gov/.test(b.url) || /doi|pubmed/i.test(b.source);
      if (aValidated !== bValidated) return bValidated ? 1 : -1;

      return (b.source || b.title).localeCompare(a.source || a.title);
    })
    .filter((item) => {
      const key = `${item.url || item.source || item.title}`.toLowerCase().trim();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 3)
    .map((item) => {
      const validated = /doi\.org|pubmed\.ncbi\.nlm\.nih\.gov|pubmed\.ncbi\.nlm\.nih\.gov/.test(item.url) || /doi|pubmed/i.test(item.source);
      return {
        ...item,
        isValidated: validated,
      };
    });
};

const buildScientificEvidenceFallbackLabels = (report: ScientificPlanResponse | null) => {
  const text = `${report?.diagnosis || ""} ${report?.surgicalTechnique || ""} ${report?.justification || ""}`
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  const fallbackSets: Array<{ patterns: RegExp[]; labels: string[] }> = [
    {
      patterns: [/scaphoid|escafoid/],
      labels: [
        "AO Surgery Reference - Scaphoid waist fracture",
        "Herbert classification - Scaphoid fracture management",
        "PubMed - Scaphoid fracture fixation review",
      ],
    },
    {
      patterns: [/distal radius|radio distal|colles/],
      labels: [
        "AO Surgery Reference - Distal radius fracture",
        "AAOS - Distal radius fracture guidance",
        "PubMed - Distal radius fixation review",
      ],
    },
    {
      patterns: [/patella|patelar/],
      labels: [
        "AO Surgery Reference - Patella fracture",
        "AAOS - Patella fracture guidance",
        "PubMed - Patella fixation review",
      ],
    },
    {
      patterns: [/tibia|tibial shaft|diafise/],
      labels: [
        "AO Surgery Reference - Tibial shaft fracture",
        "OTA - Tibial shaft fracture consensus",
        "PubMed - Tibial nailing review",
      ],
    },
    {
      patterns: [/distal humerus|elbow|cotovelo/],
      labels: [
        "AO Surgery Reference - Distal humerus fracture",
        "AAOS - Elbow fracture guidance",
        "PubMed - Distal humerus fixation review",
      ],
    },
    {
      patterns: [/acl|ligament|meniscus|meniscal/],
      labels: [
        "AAOS - Knee ligament or meniscus guidance",
        "PubMed - Knee ligament repair review",
        "AO Foundation - Soft tissue injury principles",
      ],
    },
    {
      patterns: [/fracture|fratura|trauma/],
      labels: [
        "AO Surgery Reference - Fracture management",
        "AAOS - Fracture care guidance",
        "PubMed - Orthopedic trauma review",
      ],
    },
  ];

  const match = fallbackSets.find((entry) => entry.patterns.some((pattern) => pattern.test(text)));
  return match
    ? match.labels.slice(0, 4).map((label) => ({ label }))
    : [
        { label: "AO Surgery Reference - Orthopedic trauma principles" },
        { label: "AAOS - Fracture care guidance" },
        { label: "PubMed - Orthopedic trauma review" },
      ];
};

const getScientificEvidenceCards = (report: ScientificPlanResponse | null, language: string) => {
  if (!report) {
    return buildScientificEvidenceFallbackLabels(null);
  }

  const sources = Array.isArray(report.scientificEvidence) ? report.scientificEvidence : [];
  const uniqueSources = Array.from(
    new Map(
      sources
        .map((item) => ({
          label: getScientificSourceLabel(item),
        }))
        .filter((item) => item.label.trim().length > 0)
        .map((item) => [item.label.toLowerCase(), item] as const)
    ).values()
  ).slice(0, 4);

  if (uniqueSources.length > 0) {
    return uniqueSources;
  }

  const fallbackSources = buildScientificEvidenceFallbackLabels(report);
  return fallbackSources.length > 0 ? fallbackSources : buildScientificEvidenceFallbackLabels(null);
};

const formatScientificEvidenceText = (
  report: ScientificPlanResponse | null,
  language: string
) => {
  const title =
    language === 'en'
      ? 'SCIENTIFIC EVIDENCE'
      : language === 'es'
        ? 'EVIDENCIA CIENTÍFICA'
        : 'EVIDÊNCIA CIENTÍFICA';
  const sources = getScientificEvidenceCards(report, language);
  const labels = sources.map((item) => item.label).filter(Boolean);

  return [`## ${title}`, ...labels.map((label) => `- ${label}`)].join('\n');
};

const formatEvidenceReportLine = (item: ScientificEvidenceItem) => {
  const source = String(item.source || item.title || "").trim();
  const year = String(item.year || "").trim();
  return `${source || "Referência científica"}${year ? `, ${year}` : ""}`;
};

const getFixedDifferentials = (language: string, planText: string, regionText: string) => {
  const text = `${planText} ${regionText}`.toLowerCase();
  const isSpine = /(coluna|spine|vertebr|disco|disc|h[eé]rnia|hernia|lombar|cervical|tor[aá]c|radicul)/i.test(text);
  const isTrauma = /(fratura|fracture|trauma|luxa|disloc|elbow|cotovelo|fêmur|femur|joelho|knee|ombro|shoulder)/i.test(text);

  if (isSpine) {
    return language === 'pt'
      ? [
          'Hérnia discal no nível descrito',
          'Protusão discal',
          'Estenose foraminal',
          'Espondilolistese',
          'Síndrome facetária',
          'Radiculopatia compressiva',
          'Lesão tumoral ou infecciosa se houver sinais atípicos',
        ]
      : language === 'es'
        ? [
            'Hernia discal en el nivel descrito',
            'Protusión discal',
            'Estenosis foraminal',
            'Espondilolistesis',
            'Síndrome facetaria',
            'Radiculopatía compresiva',
            'Lesión tumoral o infecciosa si hay signos atípicos',
          ]
        : [
            'Disc herniation at the described level',
            'Disc protrusion',
            'Foraminal stenosis',
            'Spondylolisthesis',
            'Facet syndrome',
            'Compressive radiculopathy',
            'Tumoral or infectious lesion if atypical signs are present',
          ];
  }

  if (isTrauma) {
    return language === 'pt'
      ? [
          'Fratura oculta',
          'Lesão ligamentar associada',
          'Luxação ou subluxação',
          'Lesão osteocondral',
          'Lesão neurovascular',
          'Variante anatômica',
        ]
      : language === 'es'
        ? [
            'Fractura oculta',
            'Lesión ligamentaria asociada',
            'Luxación o subluxación',
            'Lesión osteocondral',
            'Lesión neurovascular',
            'Variante anatómica',
          ]
        : [
            'Occult fracture',
            'Associated ligament injury',
            'Dislocation or subluxation',
            'Osteochondral injury',
            'Neurovascular injury',
            'Anatomical variant',
          ];
  }

  return language === 'pt'
    ? [
        'Diagnóstico diferencial relacionado ao segmento avaliado',
        'Correlação clínica e radiológica necessária',
        'Lesão associada de partes moles se houver sinais indiretos',
      ]
    : language === 'es'
      ? [
          'Diagnóstico diferencial relacionado con el segmento evaluado',
          'Correlación clínico-radiológica necesaria',
          'Lesión asociada de partes blandas si hay signos indirectos',
        ]
      : [
          'Differential diagnosis related to the evaluated segment',
          'Clinical and radiological correlation required',
          'Associated soft-tissue injury if indirect signs are present',
        ];
};

const toBulletList = (value: string, language: string) =>
  String(value || '')
    .replace(/\r\n/g, '\n')
    .split(/\n|•|;/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 8)
    .length > 0
    ? String(value || '')
        .replace(/\r\n/g, '\n')
        .split(/\n|•|;/)
        .map((item) => item.trim().replace(/^[-*\d.)\s]+/, ''))
        .filter(Boolean)
        .slice(0, 8)
    : [language === 'pt' ? 'Não informado' : language === 'es' ? 'No informado' : 'Not informed'];

const AtlasModule: React.FC = () => {

  const handleImageWheel = (e: React.WheelEvent<HTMLDivElement>) => {
  e.stopPropagation();

  const el = e.currentTarget;
  const canScrollVertically = el.scrollHeight > el.clientHeight;
  const canScrollHorizontally = el.scrollWidth > el.clientWidth;

  if (canScrollVertically || canScrollHorizontally) {
    e.preventDefault();
    el.scrollTop += e.deltaY;
    el.scrollLeft += e.deltaX;
  }
};
  const { language, t } = useLanguage();
  const { activePatient, updatePatient } = usePatient();
  const emptySectionLabel =
    language === 'pt' ? 'Conteúdo não gerado' : language === 'es' ? 'Contenido no generado' : 'Content not generated';
  const emptyDataLabel =
    language === 'pt' ? 'Sem dados disponíveis' : language === 'es' ? 'Sin datos disponibles' : 'No data available';
  const scientificPlanFallbackMessage =
    language === 'pt'
      ? 'Planejamento científico indisponível no momento. Você pode gerar novamente depois.'
      : language === 'es'
        ? 'La planificación científica no está disponible en este momento. Puede volver a generarla más tarde.'
        : 'Scientific planning is currently unavailable. You can try again later.';
  const atlasScientificPlanUnavailableMessage =
    language === 'pt'
      ? 'Planejamento detalhado indisponível no momento. A análise principal foi gerada normalmente.'
    : language === 'es'
        ? 'La planificación detallada no está disponible en este momento. El análisis principal se generó normalmente.'
        : 'Detailed planning is unavailable right now. The main analysis was generated normally.';
  const differentialDiagnosisTitle =
    language === 'pt' ? 'Diagnósticos Diferenciais' : language === 'es' ? 'Diagnósticos Diferenciales' : 'Differential Diagnosis';

  const getUsageMetricLabel = (metric: UsageMetric) => {
    if (metric === 'ocrMonthly') return t('usageOcrMonthlyLabel');
    if (metric === 'threeDMonthly') return t('usageThreeDMonthlyLabel');
    if (metric === 'patients') return t('usagePatientsLabel');
    return t('usageAnalysesMonthlyLabel');
  };

  const getUsageErrorMessage = (error: unknown) => {
    if (error instanceof UsageLimitError) {
      return t('usageLimitReachedMessage', {
        feature: getUsageMetricLabel(error.metric).toLowerCase(),
        plan: error.tier.toUpperCase(),
        limit:
          error.limit == null ? t('usageUnlimited') : String(error.limit),
      });
    }

    return error instanceof Error ? error.message : t('usageActionBlocked');
  };

  const normalizeAtlasDisplayedText = useCallback((value: unknown, keepNewlines = true) => {
    const raw = String(value ?? '');
    if (!raw.trim()) return '';
    return cleanAIText(normalizeAtlasText(raw, language), { keepNewlines });
  }, [language]);

  const makeShortAtlasSummary = useCallback(
    (value: unknown, maxLength = 92) => {
      const normalized = normalizeAtlasDisplayedText(value, false)
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^[-•\d.\s]+/, '');

      if (!normalized) return emptySectionLabel;
      if (normalized.length <= maxLength) return normalized;
      return `${normalized.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
    },
    [emptySectionLabel, normalizeAtlasDisplayedText]
  );

  const atlasProgressCopy = {
    title2d: language === 'pt' ? 'Gerando plano cirúrgico' : 'Generating surgical plan',
    title3d: language === 'pt' ? 'Gerando plano cirúrgico' : 'Generating surgical plan',
    start2d: language === 'pt' ? 'Iniciando análise clínica' : 'Starting clinical analysis',
    start3d: language === 'pt' ? 'Iniciando análise clínica' : 'Starting clinical analysis',
    validate: language === 'pt' ? 'Validando exame' : 'Validating exam',
    prepare2d: language === 'pt' ? 'Preparando dados clínicos' : 'Preparing clinical data',
    prepare3d: language === 'pt' ? 'Preparando dados clínicos' : 'Preparing clinical data',
    analyze2d: language === 'pt' ? 'Analisando anatomia' : 'Analyzing anatomy',
    analyze3d: language === 'pt' ? 'Analisando anatomia' : 'Analyzing anatomy',
    generate2d: language === 'pt' ? 'Estruturando diagnóstico' : 'Structuring diagnosis',
    generate3d: language === 'pt' ? 'Estruturando diagnóstico' : 'Structuring diagnosis',
    renderFinal: language === 'pt' ? 'Montando plano cirúrgico' : 'Building surgical plan',
    finalAdjustments: language === 'pt' ? 'Aplicando ajustes finais' : 'Applying final adjustments',
    refine: language === 'pt' ? 'Refinando conduta' : 'Refining management',
    finalize: language === 'pt' ? 'Finalizando plano' : 'Finalizing plan',
    done: language === 'pt' ? 'Plano concluído' : 'Plan complete',
  };

  const atlasProgressText = {
    title2d: language === 'pt' ? 'Gerando plano cirúrgico' : 'Generating surgical plan',
    title3d: language === 'pt' ? 'Gerando plano cirúrgico' : 'Generating surgical plan',
    start2d: language === 'pt' ? 'Iniciando análise clínica' : 'Starting clinical analysis',
    start3d: language === 'pt' ? 'Iniciando análise clínica' : 'Starting clinical analysis',
    validate: language === 'pt' ? 'Validando exame' : 'Validating exam',
    prepare2d: language === 'pt' ? 'Preparando dados clínicos' : 'Preparing clinical data',
    prepare3d: language === 'pt' ? 'Preparando dados clínicos' : 'Preparing clinical data',
    analyze2d: language === 'pt' ? 'Analisando anatomia' : 'Analyzing anatomy',
    analyze3d: language === 'pt' ? 'Analisando anatomia' : 'Analyzing anatomy',
    generate2d: language === 'pt' ? 'Estruturando diagnóstico' : 'Structuring diagnosis',
    generate3d: language === 'pt' ? 'Estruturando diagnóstico' : 'Structuring diagnosis',
    renderFinal: language === 'pt' ? 'Montando plano cirúrgico' : 'Building surgical plan',
    finalAdjustments: language === 'pt' ? 'Aplicando ajustes finais' : 'Applying final adjustments',
    refine: language === 'pt' ? 'Refinando conduta' : 'Refining management',
    finalize: language === 'pt' ? 'Finalizando plano' : 'Finalizing plan',
    done: language === 'pt' ? 'Plano concluído' : 'Plan complete',
  };

  const atlasGenerationLabels = {
    titleVideo360: language === 'pt' ? 'Gerando visualização 360' : 'Generating 360 view',
    start360: language === 'pt' ? 'Iniciando geração' : 'Starting generation',
    prepare: language === 'pt' ? 'Preparando imagens' : 'Preparing images',
    prepare360: language === 'pt' ? 'Preparando visualização 360' : 'Preparing 360 view',
    generate360: language === 'pt' ? 'Gerando visualização 360' : 'Generating 360 view',
    refine360: language === 'pt' ? 'Refinando visualização' : 'Refining view',
    tracking: language === 'pt' ? 'Acompanhe o progresso da geração no Atlas' : 'Track the Atlas generation progress',
  };

  const getAtlasGenerationTitle = (mode: AtlasGenerationMode) => {
    if (mode === 'main-2d') return atlasProgressText.title2d;
    if (mode === 'main-3d') return atlasProgressText.title3d;
    if (mode === 'video-360') return atlasGenerationLabels.titleVideo360;
    return language === 'pt' ? 'Gerando imagem' : 'Generating image';
  };

  const [selectedMedia, setSelectedMedia] = useState<string | null>(null);
  const [staticImage, setStaticImage] = useState<string | null>(null);
  const [inputImage, setInputImage] = useState<string | null>(null);
  const [generated2DImageUrl, setGenerated2DImageUrl] = useState<string | null>(null);
  const [generated2DImageBase64, setGenerated2DImageBase64] = useState<string | null>(null);
  const [generated3DImageUrl, setGenerated3DImageUrl] = useState<string | null>(null);
  const [generated3DImageBase64, setGenerated3DImageBase64] = useState<string | null>(null);
  const [generatedAtlasImage, setGeneratedAtlasImage] = useState<{
    base64?: string;
    url?: string;
    mimeType?: string;
    createdAt?: number;
  } | null>(null);
  
  const [lesionMarker, setLesionMarker] = useState<{x:number,y:number}|null>(null);
  const [lesionBox, setLesionBox] = useState<{x:number;y:number;width:number;height:number}|null>(null);
  const [lesionConfidence, setLesionConfidence] = useState<number | null>(null);

  const estimateLesionMarkerFromRegion = useCallback((regionHint?: string) => {
    const hint = (regionHint || '').toLowerCase();
    if (!hint) return { x: 55, y: 48 };

    let x = 55;
    let y = 48;

    if (/prox|super|upper|pole proximal/.test(hint)) y = 30;
    if (/dist|infer|lower|pole distal/.test(hint)) y = 70;
    if (/waist|shaft|diaf|middle|mid/.test(hint)) y = 50;

    if (/medial|ulnar|ulna/.test(hint)) x = 40;
    if (/lateral|radial|radius/.test(hint)) x = 68;

    if (/dorsal|posterior/.test(hint)) y = Math.max(25, y - 8);
    if (/volar|anterior|palmar/.test(hint)) y = Math.min(75, y + 8);

    return { x, y };
  }, []);

  const [detectedAnatomyRegion, setDetectedAnatomyRegion] = useState('');
  const [detectedAnatomicalAnchors, setDetectedAnatomicalAnchors] = useState('');

  const [video360, setVideo360] = useState<string | null>(null);
  
  // 'image' = Static High Res, 'video' = 360 Rotation
  const [viewMode, setViewMode] = useState<'image' | 'video'>('image');

  const [compareMode, setCompareMode] = useState(false);
  const [compareSyncEnabled, setCompareSyncEnabled] = useState(true);
  const [activeComparePane, setActiveComparePane] = useState<'original' | 'reconstruction'>('original');
  const [compareOriginalZoomScale, setCompareOriginalZoomScale] = useState(1);
  const [compareOriginalPanOffset, setCompareOriginalPanOffset] = useState({ x: 0, y: 0 });
  const [compareAtlasZoomScale, setCompareAtlasZoomScale] = useState(1);
  const [compareAtlasPanOffset, setCompareAtlasPanOffset] = useState({ x: 0, y: 0 });
  const [compareDragPane, setCompareDragPane] = useState<'original' | 'reconstruction' | null>(null);
  const [compareInitialX, setCompareInitialX] = useState(0);
  const [compareInitialY, setCompareInitialY] = useState(0);
  const [compareInitialPan, setCompareInitialPan] = useState({ x: 0, y: 0 });
  const [activeVisualizationMode, setActiveVisualizationMode] = useState<'2d' | '3d'>('3d');
  const [activeTab, setActiveTab] = useState<'visualizacao2d' | 'visualizacao3d' | 'medidas' | 'relatorio' | 'plano'>('plano');
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentItem, setCurrentItem] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<{ message: string; code?: string; details?: string; showKeyBtn?: boolean } | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureNotice, setCaptureNotice] = useState<{ message: string; kind: 'success' | 'error' } | null>(null);
  
  const [renderStyle, setRenderStyle] = useState<'2D' | '3D'>('2D');
  const [analysisMode, setAnalysisMode] = useState<'fast' | 'detailed'>('detailed');
  const [isInteractive, setIsInteractive] = useState(false); // Controls auto-play vs manual
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [showReport, setShowReport] = useState(false); // Report Print Modal
  const [savedToPatient, setSavedToPatient] = useState(false);
  
  const [genStep, setGenStep] = useState('');
  const [atlasGeneration, setAtlasGeneration] = useState<AtlasGenerationState>(initialAtlasGenerationState);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isUploadDragging, setIsUploadDragging] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);

  const generated2DImageMedia = generated2DImageUrl || (generated2DImageBase64 ? `data:image/png;base64,${generated2DImageBase64}` : null);
  const generated3DImageMedia = generated3DImageUrl || (generated3DImageBase64 ? `data:image/png;base64,${generated3DImageBase64}` : null);
  const activeGeneratedAtlasMedia = activeVisualizationMode === '2d' ? generated2DImageMedia : generated3DImageMedia;
  const activeGeneratedAtlasLabel = activeVisualizationMode === '2d' ? 'Visualização 2D' : 'Visualização 3D';

  const activateVisualizationTab = useCallback((mode: '2d' | '3d') => {
    setCompareMode(false);
    setActiveVisualizationMode(mode);
    setActiveTab(mode === '2d' ? 'visualizacao2d' : 'visualizacao3d');
    setViewMode('image');
  }, []);
  
  // UPDATED: Now supports multiple images
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  // NEW: Manual anatomy context
  const [manualAnatomy, setManualAnatomy] = useState('');
  
  const [surgicalPlan, setSurgicalPlan] = useState<SurgicalPlanState | null>(null);
  const [surgicalPlanV2, setSurgicalPlanV2] = useState<SurgicalPlanV2 | null>(null);
  const [scientificEvidenceText, setScientificEvidenceText] = useState('');
  const [scientificEvidenceReport, setScientificEvidenceReport] = useState<ScientificPlanResponse | null>(null);
  const [selectedStrategy, setSelectedStrategy] = useState<TreatmentStrategy | null>(null);
  const [planRecommendation, setPlanRecommendation] = useState<TreatmentRecommendation>("uncertain");

  const normalizedConduct = normalizeSurgicalConduct(
    selectedStrategy === "surgical"
      ? "surgical"
      : selectedStrategy === "conservative"
        ? "conservative"
        : surgicalPlan?.conduta
  );
  const isPostOpBlocked = normalizedConduct === 'relative' || normalizedConduct === 'non-surgical';
  const conductBadge =
    normalizedConduct === 'surgical'
      ? { label: t('surgicalIndicatorSurgical'), className: 'border-cyan-400/14 bg-[rgba(2,6,23,0.72)] text-cyan-300' }
      : normalizedConduct === 'relative'
        ? { label: t('surgicalIndicatorRelative'), className: 'border-cyan-400/14 bg-[rgba(2,6,23,0.72)] text-cyan-300' }
        : normalizedConduct === 'non-surgical'
          ? { label: t('surgicalIndicatorNonSurgical'), className: 'border-cyan-400/14 bg-[rgba(2,6,23,0.72)] text-cyan-300' }
          : { label: t('surgicalIndicatorPending'), className: 'border-cyan-400/14 bg-[rgba(2,6,23,0.72)] text-slate-300' };

  // Dynamic Patient Orientations State
  const [patientOrientations, setPatientOrientations] = useState<{
    woundCare: string;
    painManagement: string;
    mobility: string;
    warningSigns: string[];
    rehab: string;
  } | null>(null);

  // Auto-Measurements State
  const [autoMeasurements, setAutoMeasurements] = useState<{
    region: string;
    measurements: { label: string; value: string; status: string; }[];
  } | null>(null);

  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform || '');
  const isSafari = typeof navigator !== 'undefined' && /^((?!chrome|android).)*safari/i.test(navigator.userAgent || '');
  const isLowPerfDevice = isMac || isSafari;
  const atlasEntranceClass = isLowPerfDevice ? '' : 'animate-in';
  const atlasBackdropBlurClass = isLowPerfDevice ? '' : 'backdrop-blur-xl';

  const surgicalPlanDifferentials = Array.isArray(surgicalPlan?.diagnosticosDiferenciais)
    ? surgicalPlan.diagnosticosDiferenciais
    : [];
  const autoMeasurementRows = Array.isArray(autoMeasurements?.measurements)
    ? autoMeasurements.measurements
    : [];

  // Material Checklist State
  const [materialList, setMaterialList] = useState<{text: string; checked: boolean}[]>([]);
  const [materialsCopied, setMaterialsCopied] = useState(false);
  const [opmeCopied, setOpmeCopied] = useState(false);

  // Library State
  const [savedCases, setSavedCases] = useState<SavedCase[]>([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showPatientFilePicker, setShowPatientFilePicker] = useState(false);
  const [doctorData, setDoctorData] = useState<{ name: string; crm: string; rqe: string } | null>(null);

  if (typeof window !== "undefined" && import.meta.env.DEV) {
    console.log("[AtlasModule] render diagnostics", {
      route: window.location.pathname,
      uploadedImages: uploadedImages.length,
      surgicalPlanDifferentials: surgicalPlanDifferentials.length,
      autoMeasurements: autoMeasurementRows.length,
      materialList: materialList.length,
      savedCases: savedCases.length,
    });
  }

  // Viewer State
  const [isDragging, setIsDragging] = useState(false);
  const [initialX, setInitialX] = useState(0);
  const [initialY, setInitialY] = useState(0);
  const [initialTime, setInitialTime] = useState(0);
  const [progress, setProgress] = useState(0); // 0 to 100 for slider
  
  const [zoomScale, setZoomScale] = useState(1);
  const [originalRotation, setOriginalRotation] = useState(0);
  const [atlasRotation, setAtlasRotation] = useState(0);
  
  
  const [brightness, setBrightness] = useState(100);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [lastPanPos, setLastPanPos] = useState({ x: 0, y: 0 });

  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const compareCaptureRef = useRef<HTMLDivElement>(null);
  const comparePanelRefs = useRef<Record<'original' | 'reconstruction', HTMLDivElement | null>>({
    original: null,
    reconstruction: null,
  });
  const compareTouchRef = useRef<{
    pane: 'original' | 'reconstruction';
    initialDistance: number;
    initialZoom: number;
    initialPan: { x: number; y: number };
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const smoothProgressIntervalRef = useRef<number | null>(null);
  const smoothProgressStageTimeoutRef = useRef<number | null>(null);
  const atlasProgressFinishTimeoutRef = useRef<number | null>(null);

  // Biometrics
  const [biometrics, setBiometrics] = useState({ screwLength: 0, canalOcc: 0, angulation: 0 });
  const atlasSaveButtonLabel = savedToPatient ? t('atlasSavedToMedicalRecord') : t('atlasAddToRecord');
  const atlasSaveButtonTitle = activePatient ? atlasSaveButtonLabel : t('selectPatientFirst');
  const atlasSaveButtonDisabled = !activePatient || savedToPatient;

  const clampComparePan = useCallback((pane: 'original' | 'reconstruction', zoom: number, pan: { x: number; y: number }) => {
    const panel = comparePanelRefs.current[pane];
    const width = Math.max(1, panel?.clientWidth || compareCaptureRef.current?.clientWidth || containerRef.current?.clientWidth || 1);
    const height = Math.max(1, panel?.clientHeight || compareCaptureRef.current?.clientHeight || containerRef.current?.clientHeight || 1);
    const maxPanX = Math.max(70, Math.round(width * 0.28 * Math.max(0, zoom - 1)) + 40);
    const maxPanY = Math.max(70, Math.round(height * 0.28 * Math.max(0, zoom - 1)) + 40);

    return {
      x: Math.max(-maxPanX, Math.min(maxPanX, pan.x)),
      y: Math.max(-maxPanY, Math.min(maxPanY, pan.y)),
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const previous = document.body.style.overflow;
    if (isFullscreen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isFullscreen]);

  useEffect(() => {
    console.log("[ATLAS][MOUNT]");
    console.log("[ATLAS][BROWSER]", {
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      platform: typeof navigator !== 'undefined' ? navigator.platform : '',
      memory: typeof navigator !== 'undefined' ? (navigator as any).deviceMemory : undefined,
    });
  }, []);

  useEffect(() => {
    if (!compareMode) return;

    setActiveComparePane('original');

    const panelWidth = Math.max(1, (compareCaptureRef.current?.clientWidth || containerRef.current?.clientWidth || 1) / 2);
    const panelHeight = Math.max(1, compareCaptureRef.current?.clientHeight || containerRef.current?.clientHeight || 1);

    if (lesionBox) {
      const lesionCenterX = lesionBox.x + lesionBox.width / 2;
      const lesionCenterY = lesionBox.y + lesionBox.height / 2;
      const offsetX = Math.round(((50 - lesionCenterX) / 100) * panelWidth * 0.36);
      const offsetY = Math.round(((50 - lesionCenterY) / 100) * panelHeight * 0.36);

      const initialZoom = isLowPerfDevice ? 1.15 : 1.35;
      setCompareOriginalZoomScale(initialZoom);
      setCompareAtlasZoomScale(initialZoom);
      setCompareOriginalPanOffset({ x: offsetX, y: offsetY });
      setCompareAtlasPanOffset({ x: offsetX, y: offsetY });
      return;
    }

    setCompareOriginalZoomScale(1);
    setCompareAtlasZoomScale(1);
    setCompareOriginalPanOffset({ x: 0, y: 0 });
    setCompareAtlasPanOffset({ x: 0, y: 0 });
  }, [compareMode, lesionBox, isLowPerfDevice]);

  const clearAtlasProgressFinishTimeout = useCallback(() => {
    if (atlasProgressFinishTimeoutRef.current !== null) {
      window.clearTimeout(atlasProgressFinishTimeoutRef.current);
      atlasProgressFinishTimeoutRef.current = null;
    }
  }, []);

  const stopSmoothProgress = useCallback(() => {
    if (smoothProgressIntervalRef.current !== null) {
      window.clearInterval(smoothProgressIntervalRef.current);
      smoothProgressIntervalRef.current = null;
    }

    if (smoothProgressStageTimeoutRef.current !== null) {
      window.clearTimeout(smoothProgressStageTimeoutRef.current);
      smoothProgressStageTimeoutRef.current = null;
    }
  }, []);

  const updateAtlasProgress = useCallback(
    (progressValue: number, stage: string, type?: AtlasGenerationMode) => {
      clearAtlasProgressFinishTimeout();
      setAtlasGeneration((prev) => ({
        active: true,
        type: type ?? prev.type,
        progress: Math.max(0, Math.min(100, Math.round(progressValue))),
        stage,
      }));
      setGenStep(stage);
    },
    [clearAtlasProgressFinishTimeout]
  );

  const resetAtlasProgress = useCallback(() => {
    stopSmoothProgress();
    clearAtlasProgressFinishTimeout();
    setAtlasGeneration(initialAtlasGenerationState);
    setGenStep('');
  }, [clearAtlasProgressFinishTimeout, stopSmoothProgress]);

  const finishAtlasProgress = useCallback(() => {
    stopSmoothProgress();
    clearAtlasProgressFinishTimeout();
    setAtlasGeneration((prev) => ({
      active: true,
      type: prev.type,
      progress: 100,
      stage: atlasProgressText.done,
    }));
    setGenStep(atlasProgressText.done);

    return new Promise<void>((resolve) => {
      atlasProgressFinishTimeoutRef.current = window.setTimeout(() => {
        setAtlasGeneration(initialAtlasGenerationState);
        setGenStep('');
        atlasProgressFinishTimeoutRef.current = null;
        resolve();
      }, 700);
    });
  }, [atlasProgressText.done, clearAtlasProgressFinishTimeout, stopSmoothProgress]);

  const startSmoothProgress = useCallback(
    (
      start: number,
      end: number,
      stage: string,
      type: AtlasGenerationMode,
      step = 1,
      interval = 180
    ) => {
      stopSmoothProgress();
      clearAtlasProgressFinishTimeout();
      updateAtlasProgress(start, stage, type);

      smoothProgressIntervalRef.current = window.setInterval(() => {
        setAtlasGeneration((prev) => {
          const nextProgress = Math.min(end, prev.progress + step);

          if (nextProgress >= end && smoothProgressIntervalRef.current !== null) {
            window.clearInterval(smoothProgressIntervalRef.current);
            smoothProgressIntervalRef.current = null;
          }

          return {
            active: true,
            type,
            progress: nextProgress,
            stage,
          };
        });
      }, interval);
    },
    [clearAtlasProgressFinishTimeout, stopSmoothProgress, updateAtlasProgress]
  );

  const scheduleSmoothProgress = useCallback(
    (
      delay: number,
      start: number,
      end: number,
      stage: string,
      type: AtlasGenerationMode,
      step = 1,
      interval = 180
    ) => {
      if (smoothProgressStageTimeoutRef.current !== null) {
        window.clearTimeout(smoothProgressStageTimeoutRef.current);
      }

      smoothProgressStageTimeoutRef.current = window.setTimeout(() => {
        smoothProgressStageTimeoutRef.current = null;
        startSmoothProgress(start, end, stage, type, step, interval);
      }, delay);
    },
    [startSmoothProgress]
  );

  useEffect(() => {
    return () => {
      stopSmoothProgress();
      clearAtlasProgressFinishTimeout();
    };
  }, [clearAtlasProgressFinishTimeout, stopSmoothProgress]);

  // Voice Commands
  useEffect(() => {
    if (isLowPerfDevice) return;
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = false;
      recognition.lang = language === 'pt' ? 'pt-BR' : 'en-US';

      recognition.onresult = (event: any) => {
        const transcript = event.results[event.results.length - 1][0].transcript.toLowerCase();
        
        if (transcript.includes("raio x") || transcript.includes("x-ray")) {
          return;
        } else if (transcript.includes("zoom")) {
          setZoomScale(prev => Math.min(prev + 0.5, 4));
        } else if (transcript.includes("brilho") || transcript.includes("brightness") || transcript.includes("aumentar")) {
          setBrightness(prev => (prev >= 175 ? 100 : prev + 25));
        }
      };

      recognition.onstart = () => setIsListening(true);
      recognition.onend = () => setIsListening(false);
      recognitionRef.current = recognition;
    }
  }, [isLowPerfDevice, language]);

  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      recognitionRef.current?.start();
    }
  };

  // Load Saved Cases on Mount
  useEffect(() => {
    const saved = localStorage.getItem('orthoprecision_atlas_library');
    if (saved) {
      try {
        setSavedCases(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to load saved cases", e);
      }
    }

    // Load Doctor Data
    const storedUsers = localStorage.getItem('orthoprecision_users');
    if (storedUsers) {
      try {
        const users = JSON.parse(storedUsers);
        const currentUser = localStorage.getItem('orthoprecision_current_user') || 'admin';
        const user = users[currentUser];
        if (user) {
          setDoctorData({
            name: user.name || (currentUser === 'admin' ? 'Administrador do Sistema' : currentUser),
            crm: user.crm || '',
            rqe: user.rqe || ''
          });
        }
      } catch (e) {
        console.error("Failed to load doctor data", e);
      }
    }
  }, []);

  // Save changes to LocalStorage with Error Handling
  useEffect(() => {
    try {
      localStorage.setItem('orthoprecision_atlas_library', JSON.stringify(savedCases));
    } catch (e) {
      console.warn("Failed to save to library (Storage Full?)", e);
    }
  }, [savedCases]);

  useEffect(() => {
  const el = containerRef.current;
  if (!el) return;

  const wheelHandler = (e: WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (compareMode) return;

    if (viewMode === 'video') {
      if (!selectedMedia) return;
    } else if (!activeGeneratedAtlasMedia) {
      return;
    }

    const zoomStep = 0.12;
    const direction = e.deltaY > 0 ? -1 : 1;

    setZoomScale(prev => {
      const next = Math.min(Math.max(1, prev + direction * zoomStep), 4);

      if (next === 1) {
        setPanOffset({ x: 0, y: 0 });
      }

      return next;
    });
  };

  el.addEventListener("wheel", wheelHandler, { passive: false });

  return () => {
    el.removeEventListener("wheel", wheelHandler);
  };
}, [activeGeneratedAtlasMedia, compareMode, selectedMedia]);
  // Effect to parse materials into checklist when surgicalPlan updates
  useEffect(() => {
    if (surgicalPlan?.materiais) {
      const items = surgicalPlan.materiais
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map(line => ({
           text: line.replace(/^-\s*/, '').replace(/^\*\s*/, '').replace(/^â€¢\s*/, ''),
           checked: false
        }));
      setMaterialList(items);
    } else {
      setMaterialList([]);
    }
  }, [surgicalPlan]);

  // Reset saved state when report opens
  useEffect(() => {
    if(showReport) setSavedToPatient(false);
  }, [showReport]);

  // --- MAIN ANALYZE FUNCTION (Hoisted for Auto-Trigger) ---
  const handleSelectItem = useCallback(async (item: string, overrideSource?: string[], context?: string) => {
    setCurrentItem(item); setError(null); 
    setScientificEvidenceText('');
    setScientificEvidenceReport(null);
    setZoomScale(1); setPanOffset({x:0, y:0});
    setDetectedAnatomyRegion(''); setDetectedAnatomicalAnchors('');
        setLesionMarker(null);
        setLesionBox(null);
        setLesionConfidence(null);
        setStaticImage(null); setVideo360(null);
    setPatientOrientations(null); 
    setAutoMeasurements(null); 

      const sourceImages = overrideSource || uploadedImages;
      if (surgicalPlanningPrimaryFlow && item === t('surgicalPlanAnalysisImage')) {
        return handleProcessSurgicalPlanOnly(sourceImages, context);
      }
      setInputImage(sourceImages[0] || null);
      setGeneratedAtlasImage(null);
      const effectiveAnalysisMode = surgicalPlanningPrimaryFlow ? 'detailed' : (renderStyle === '2D' || analysisMode === 'fast' ? 'fast' : 'detailed');
      const effectiveRenderStyle = surgicalPlanningPrimaryFlow ? '2D' : (effectiveAnalysisMode === 'fast' ? '2D' : '3D');
      const atlasGenerationType: AtlasGenerationMode = effectiveRenderStyle === '2D' ? 'main-2d' : 'main-3d';
      const isAtlas3DGeneration = surgicalPlanningPrimaryFlow ? false : atlasGenerationType === 'main-3d';
      const originalSizes = sourceImages.map((image) => estimateDataUrlBytes(image));
      const compressedPlanImages = surgicalPlanningPrimaryFlow
        ? await Promise.all(
            sourceImages
              .filter((image) => image.startsWith('data:image/'))
              .slice(0, SURGICAL_PLAN_MAX_IMAGES)
              .map((image) => compressImageForSurgicalPlan(image, { maxDimension: 1024, quality: 0.7, targetBytes: 95_000 }))
          )
        : [];
      const compressedSizes = compressedPlanImages.map((image) => image.sizeBytes);
      const totalPayloadBytes = compressedPlanImages.reduce((sum, image) => sum + image.dataUrl.length, 0);
      if (surgicalPlanningPrimaryFlow) {
        console.log('[SurgicalPlan][payload:size]', {
          imagesCount: compressedPlanImages.length,
          originalSizes,
          compressedSizes,
          totalPayloadBytes,
        });

        const hasUploadedImages = sourceImages.filter(img => img.startsWith('data:image/')).length > 0;
        if (hasUploadedImages && compressedPlanImages.length === 0) {
          setError({
            message: "Falha ao processar imagem",
            details: "Não foi possível processar a imagem enviada. Tente com outra imagem.",
          });
          return;
        }
        if (totalPayloadBytes > SURGICAL_PLAN_MAX_PAYLOAD_BYTES) {
          setError({
            message: "Imagem muito grande",
            details: "Imagem muito grande. Reduza ou envie até 2 imagens para análise rápida.",
          });
          return;
        }
      }
      const aiSourceImages = surgicalPlanningPrimaryFlow ? compressedPlanImages.map((image) => image.dataUrl) : sourceImages;
      const analysisImages = effectiveAnalysisMode === 'fast' ? aiSourceImages.slice(0, 1) : aiSourceImages;
      const atlasQuality = effectiveAnalysisMode === 'fast' || isLowPerfDevice ? '1K' : '2K';
      const atlasFlowStartedAt = Date.now();
      const uid = auth.currentUser?.uid;
      resetAtlasProgress();

      console.info("[ATLAS][frontend][generation:start]", {
        item,
        selectedMode: renderStyle,
        analysisMode,
        effectiveRenderStyle,
        effectiveAnalysisMode,
        atlasGenerationType,
        imageCount: sourceImages.length,
        analysisImageCount: analysisImages.length,
        atlasQuality,
        isLowPerfDevice,
        firstImageBase64Length: sourceImages[0]?.length || 0,
      });

    try {
      if (!uid) {
        throw new Error(t('usageAuthenticationRequired'));
      }

      const aistudio = getAiStudio();
      if (aistudio && typeof aistudio.hasSelectedApiKey === 'function') {
        if (!(await aistudio.hasSelectedApiKey())) {
            await aistudio.openSelectKey();
        }
      }
      
      setIsGenerating(true);
      updateAtlasProgress(
        isAtlas3DGeneration ? 4 : 5,
        isAtlas3DGeneration ? atlasProgressText.start3d : atlasProgressText.start2d,
        atlasGenerationType
      );
      updateAtlasProgress(
        isAtlas3DGeneration ? 8 : 10,
        atlasProgressText.validate,
        atlasGenerationType
      );

      const tagMap: any = {
        pt: { d: 'DIAGNOSTICO', dd: 'DIAGNOSTICOS_DIFERENCIAIS', m: 'MATERIAIS', t: 'TECNICA', i: 'DICAS', b: 'PLANO_B' },
        es: { d: 'DIAGNOSTICO', dd: 'DIAGNOSTICOS_DIFERENCIALES', m: 'MATERIALES', t: 'TECNICA', i: 'CONSEJOS', b: 'PLAN_B' },
        en: { d: 'DIAGNOSIS', dd: 'DIFFERENTIAL_DIAGNOSIS', m: 'MATERIALS', t: 'TECHNIQUE', i: 'TIPS', b: 'PLAN_B' }
      };
      const tags = tagMap[language] || tagMap['pt'];

      const parse = (text: string, tag: string) => {
        const regex = new RegExp(`\\[${tag}\\](.*?)(?=\\[|$)`, 'is');
        const match = text.match(regex);
        return match ? match[1].trim() : '';
      };
      
      const reductionLine = language === 'pt' ? '- Redução da Fratura' : (language === 'es' ? '- Reducción de la Fractura' : '- Fracture Reduction');

      let biomechanicsPromise;

      if (item === t('surgicalPlanAnalysisImage') && sourceImages.length > 0) {
        updateAtlasProgress(
          isAtlas3DGeneration ? 18 : 25,
          isAtlas3DGeneration ? atlasProgressText.prepare3d : atlasProgressText.prepare2d,
          atlasGenerationType
        );

        if (isAtlas3DGeneration) {
          startSmoothProgress(20, 38, atlasProgressText.analyze3d, 'main-3d', 2, 220);
          scheduleSmoothProgress(2400, 42, 68, atlasProgressText.generate3d, 'main-3d', 2, 260);
        } else {
          updateAtlasProgress(45, atlasProgressText.analyze2d, 'main-2d');
          console.info("[Atlas2D][analysis:start]", {
            item,
            analysisMode: effectiveAnalysisMode,
            renderStyle: effectiveRenderStyle,
            imageCount: analysisImages.length,
          });
        }
        
        const imageAnalysisStartedAt = Date.now();
        if (!isAtlas3DGeneration) {
          console.info("[Atlas2D][image:start]", {
            item,
            analysisMode: effectiveAnalysisMode,
            renderStyle: effectiveRenderStyle,
          });
        }
        const analysisResult = await analyzeAtlasImageFindings(analysisImages, language, context, effectiveAnalysisMode);
        const result: AtlasLesionResult = {
          imageData: null,
          description: JSON.stringify(analysisResult.clinicalOutput || analysisResult),
          anatomicalRegion: analysisResult.anatomicalRegion || analysisResult.examRegion || analysisResult.atlasFindings?.anatomyRegion,
          anatomicalAnchors: analysisResult.anatomicalAnchors || analysisResult.atlasFindings?.alignmentNotes,
          fractureLocation: analysisResult.fractureLocation || analysisResult.boneSegment || analysisResult.suspectedSubregion,
          suspectedLesionRegion: analysisResult.suspectedSubregion || analysisResult.fractureLocation,
          imagingEvidenceRegion: (analysisResult.imagingEvidence || analysisResult.atlasFindings?.imagingEvidence || []).join(" | "),
          lesionBoundingBox: null,
          visualPrompt: "",
          differentialDiagnosis: analysisResult.clinicalOutput?.differentialDiagnosis || analysisResult.differentialDiagnosis || [],
          analysis: analysisResult,
        };
        if (!isAtlas3DGeneration) {
          console.info("[Atlas2D][analysis:end]", {
            item,
            durationMs: Date.now() - imageAnalysisStartedAt,
            hasImageData: Boolean(result?.imageData),
            hasBox: Boolean(result?.lesionBoundingBox),
          });
          console.info("[Atlas2D][image:end]", {
            item,
            durationMs: Date.now() - imageAnalysisStartedAt,
            hasImageData: Boolean(result?.imageData),
          });
        }
        console.info("[ATLAS][frontend][generation:analysis-end]", {
          mode: effectiveRenderStyle,
          analysisMode: effectiveAnalysisMode,
          durationMs: Date.now() - imageAnalysisStartedAt,
          hasImageData: Boolean(result?.imageData),
          hasBox: Boolean(result?.lesionBoundingBox),
        });
        stopSmoothProgress();
        setDetectedAnatomyRegion(
          normalizeAtlasDisplayedText(
            result.fractureLocation || result.anatomicalRegion || result.analysis?.examRegion || result.analysis?.anatomicalRegion || context || item
          )
        );
        setDetectedAnatomicalAnchors(
          normalizeAtlasDisplayedText(
            result.anatomicalAnchors || result.analysis?.anatomicalAnchors || result.analysis?.atlasFindings?.alignmentNotes || ""
          )
        );
        if (result.lesionBoundingBox && (result.lesionBoundingBox.confidence ?? 1) >= 0.45) {
          setLesionConfidence(result.lesionBoundingBox.confidence ?? 1);
          setLesionBox({
            x: result.lesionBoundingBox.x,
            y: result.lesionBoundingBox.y,
            width: result.lesionBoundingBox.width,
            height: result.lesionBoundingBox.height,
          });
          setLesionMarker({
            x: result.lesionBoundingBox.x + result.lesionBoundingBox.width / 2,
            y: result.lesionBoundingBox.y + result.lesionBoundingBox.height / 2,
          });
        } else {
          setLesionBox(null);
          setLesionMarker(null);
          setLesionConfidence(null);
        }
        updateAtlasProgress(
          isAtlas3DGeneration ? 72 : 70,
          isAtlas3DGeneration ? atlasProgressText.renderFinal : atlasProgressText.generate2d,
          atlasGenerationType
        );
        
        biomechanicsPromise = measureAnatomyFromImage(aiSourceImages[0], language).catch((measureError) => {
          console.warn("Automatic anatomy measurement failed for Atlas image flow", measureError);
          return null;
        });

        let currentStaticImage = "";
        const generatedResultUrl = result.imageData ? `data:image/png;base64,${result.imageData}` : "";
        if (result.imageData) {
          currentStaticImage = generatedResultUrl;
          setStaticImage(generatedResultUrl);
          if (isAtlas3DGeneration) {
            setGenerated3DImageUrl(generatedResultUrl);
            setGenerated3DImageBase64(result.imageData);
            setGeneratedAtlasImage({
              base64: result.imageData,
              url: generatedResultUrl,
              mimeType: "image/png",
              createdAt: Date.now(),
            });
            setActiveVisualizationMode('3d');
            setActiveTab('visualizacao3d');
          }
          else {
            setGenerated2DImageUrl(generatedResultUrl);
            setGenerated2DImageBase64(result.imageData);
            setActiveVisualizationMode('2d');
            if (!surgicalPlanningPrimaryFlow) setActiveTab('visualizacao2d');
          }
        } else {
          currentStaticImage = "";
          setStaticImage(null);
        }

        if (!isAtlas3DGeneration) {
          setViewMode('image');
          setSelectedMedia(surgicalPlanningPrimaryFlow ? null : (currentStaticImage || generatedResultUrl || null));
          console.info("[Atlas2D][flow:partial-success]", {
            item,
            hasImage: Boolean(currentStaticImage || generatedResultUrl),
            imageSetImmediately: Boolean(currentStaticImage || generatedResultUrl),
          });
        }

        const scientificPlanTimeoutMs = isAtlas3DGeneration ? 22000 : 12000;
        const generateScientificPlanWithTimeout = async (planQuery: string, imageForPlan?: string) => {
          const startedAtScientific = Date.now();
          console.info("[Atlas2D][plan:start]", {
            item,
            analysisMode: effectiveAnalysisMode,
            renderStyle: effectiveRenderStyle,
            timeoutMs: scientificPlanTimeoutMs,
            queryLength: planQuery.length,
            hasImage: Boolean(imageForPlan),
          });

          let timeoutId: number | null = null;
          const timeoutPromise = new Promise<string>((_, reject) => {
            timeoutId = window.setTimeout(() => {
              const timeoutError: any = new Error("deadline-exceeded");
              timeoutError.code = "deadline-exceeded";
              timeoutError.status = 504;
              reject(timeoutError);
            }, scientificPlanTimeoutMs);
          });

          try {
            const rawPlan = await Promise.race([
              generateScientificSurgicalPlanText(
                planQuery,
                language,
                undefined,
                { concise: !isAtlas3DGeneration }
              ),
              timeoutPromise,
            ]);

            console.info("[Atlas2D][plan:end]", {
              item,
              durationMs: Date.now() - startedAtScientific,
              mode: effectiveRenderStyle,
              analysisMode: effectiveAnalysisMode,
            });

            return String(rawPlan || "");
          } catch (planError: any) {
            const isDeadline = String(planError?.code || planError?.message || "").includes("deadline-exceeded") || String(planError?.message || "").includes("deadline-exceeded");
            console.error("[Atlas2D][plan:error]", {
              item,
              mode: effectiveRenderStyle,
              analysisMode: effectiveAnalysisMode,
              durationMs: Date.now() - startedAtScientific,
              isDeadline,
              error: planError,
            });
            if (isDeadline) {
              return "";
            }
            throw planError;
          } finally {
            if (timeoutId !== null) {
              window.clearTimeout(timeoutId);
            }
          }
        };

        if (result.description) {
          const visualDiagnosticCheck = await generateVisualDiagnosticCheck(
            [
              "Etapa visual obrigatória antes do planejamento cirúrgico.",
              `Modalidade esperada: ${result.analysis?.examType || "radiografia"}`,
              `Contexto clínico: ${context || item}`,
              `Região suspeita: ${result.analysis?.examRegion || result.anatomicalRegion || ""}`,
              `Lado suspeito: ${result.analysis?.laterality || ""}`,
            ].filter(Boolean).join("\n"),
            language,
            aiSourceImages.slice(0, SURGICAL_PLAN_MAX_IMAGES)
          );
          console.log("[SurgicalPlan][visualDiagnosticCheck]", visualDiagnosticCheck);

          const visualPlanContext = [
            "VISUAL DIAGNOSTIC CHECK (MANDATORY PRIMARY INPUT):",
            `modality: ${visualDiagnosticCheck.modality}`,
            `views: ${(visualDiagnosticCheck.views || []).join(" | ")}`,
            `side: ${visualDiagnosticCheck.side || ""}`,
            `bone: ${visualDiagnosticCheck.bone}`,
            `joint: ${visualDiagnosticCheck.joint}`,
            `anatomicalRegion: ${visualDiagnosticCheck.anatomicalRegion}`,
            `specificLocation: ${visualDiagnosticCheck.specificLocation}`,
            `suspectedDiagnosis: ${visualDiagnosticCheck.suspectedDiagnosis}`,
            `visualEvidence: ${(visualDiagnosticCheck.visualEvidence || []).join(" | ")}`,
            `confidence: ${visualDiagnosticCheck.confidence}`,
            `uncertaintyReason: ${visualDiagnosticCheck.uncertaintyReason || ""}`,
            visualDiagnosticCheck.specificLocation ? `Use specificLocation exactly as written when building the plan.` : "",
            visualDiagnosticCheck.suspectedDiagnosis ? `Use suspectedDiagnosis exactly as written when building the plan.` : "",
          ].filter(Boolean).join("\n");

          const planQuery = [
            visualPlanContext,
            "Plano Cirúrgico por imagem",
            context ? `Contexto anatômico informado: ${context}` : "",
            `Modalidade: ${result.analysis?.examType || "radiografia"}`,
            `Região: ${result.analysis?.examRegion || result.anatomicalRegion || ""}`,
            `Localização: ${result.fractureLocation || result.analysis?.fractureLocation || result.analysis?.suspectedSubregion || ""}`,
            `Diagnóstico preliminar: ${result.analysis?.diagnosticSummary || result.analysis?.diagnosis || ""}`,
            `Classificação: ${result.analysis?.fracturePattern || result.analysis?.fractureType || result.analysis?.lesionType || ""}`,
            `Gravidade: ${result.analysis?.severityLevel || result.analysis?.fractureConfidence || ""}`,
            `Conduta preliminar: ${result.analysis?.recommendedConduct || result.analysis?.treatmentRecommendation || ""}`,
            `Evidências: ${(result.analysis?.imagingEvidence || result.analysis?.atlasFindings?.imagingEvidence || []).join(" | ")}`,
            `Limitações/incerteza: ${result.analysis?.diagnosticUncertainty || result.analysis?.uncertaintyNote || result.analysis?.confidenceReason || ""}`,
          ].filter(Boolean).join("\n");
      const rawSurgicalPlan = await generateSurgicalPlanningAnalysisJson(
        planQuery,
        language,
        aiSourceImages.slice(0, SURGICAL_PLAN_MAX_IMAGES)
      );
      console.log("[SurgicalPlan][main_call:success]", {
        hasResponse: Boolean(rawSurgicalPlan),
        responseLength: String(rawSurgicalPlan || "").length,
      });
      console.log("[SurgicalPlan][raw-ai-response]", rawSurgicalPlan);
      console.log("[SurgicalPlan][AI_RAW_USED]", rawSurgicalPlan);
          const parsedPlan = parseStructuredClinicalPlanPayload(rawSurgicalPlan);
          const structuredState = buildClinicalPlanState(parsedPlan);
          const parsedPlanLog = {
            hasTechniques: Array.isArray(structuredState.surgicalTechniques) && structuredState.surgicalTechniques.length > 0,
            techniquesCount: Array.isArray(structuredState.surgicalTechniques) ? structuredState.surgicalTechniques.length : 0,
            materialListCount: Array.isArray(structuredState.materials) ? structuredState.materials.length : 0,
            differentialsCount: Array.isArray(structuredState.differentialDiagnoses) ? structuredState.differentialDiagnoses.length : 0,
            autoMeasurementsCount: Array.isArray(autoMeasurementRows) ? autoMeasurementRows.length : 0,
            source: (Array.isArray(structuredState.surgicalTechniques) && structuredState.surgicalTechniques.length > 0) || (Array.isArray(structuredState.materials) && structuredState.materials.length > 0) ? "ai" : "fallback",
          } as const;
          const parsedPlanReason = parsedPlanLog.source === "fallback"
            ? (parsedPlanLog.techniquesCount === 0 && parsedPlanLog.materialListCount === 0 && parsedPlanLog.differentialsCount === 0
                ? "empty_fields"
                : parsedPlanLog.techniquesCount === 0
                  ? "missing_techniques"
                  : "parse_failed")
            : "ai";
          const visualLockedState = {
            ...structuredState,
            diagnostico: normalizeAtlasDisplayedText(
              cleanPlanningPlaceholder(visualDiagnosticCheck.suspectedDiagnosis) || structuredState.diagnostico
            ),
            anatomicalLocation: normalizeAtlasDisplayedText(
              cleanPlanningPlaceholder(visualDiagnosticCheck.specificLocation) || structuredState.anatomicalLocation
            ),
          };
          console.log("[SurgicalPlan][parsed]", { ...parsedPlanLog, reason: parsedPlanReason });
          console.log("[SurgicalPlan][normalized-result]", parsedPlan);
          console.log("[SurgicalPlan][cards-state]", visualLockedState);
          const diag = visualLockedState.diagnostico;
          const spinePlanQuery = [
            diag,
            result.analysis?.anatomicalRegion || result.anatomicalRegion || "",
            result.analysis?.fractureLocation || result.fractureLocation || result.analysis?.boneSegment || result.suspectedLesionRegion || "",
            result.analysis?.lesionType || result.analysis?.fractureType || "",
            result.analysis?.recommendedConduct || result.analysis?.clinicalOutput?.conduta || result.analysis?.clinicalOutput?.diagnosis || "",
          ].filter(Boolean).join(" | ");

          if (isSpinePlanningCase(spinePlanQuery)) {
            const runSpineScientificPlan = async () => {
              const spinePlanRaw = await generateSurgicalPlanText(
                spinePlanQuery || diag || context || item,
                language,
                aiSourceImages[0] || ""
              );
              const spinePlan = parseStructuredClinicalPlanPayload(spinePlanRaw);
              const baseFinalState = buildClinicalPlanState(spinePlan);
              console.log("[SurgicalPlan][parsed]", {
                hasTechniques: Array.isArray(baseFinalState.surgicalTechniques) && baseFinalState.surgicalTechniques.length > 0,
                techniquesCount: Array.isArray(baseFinalState.surgicalTechniques) ? baseFinalState.surgicalTechniques.length : 0,
                materialListCount: Array.isArray(baseFinalState.materials) ? baseFinalState.materials.length : 0,
                differentialsCount: Array.isArray(baseFinalState.diagnosticosDiferenciais) ? baseFinalState.diagnosticosDiferenciais.length : 0,
                autoMeasurementsCount: Array.isArray(autoMeasurementRows) ? autoMeasurementRows.length : 0,
                source: (Array.isArray(baseFinalState.surgicalTechniques) && baseFinalState.surgicalTechniques.length > 0) || (Array.isArray(baseFinalState.materials) && baseFinalState.materials.length > 0) ? "ai" : "fallback",
                reason: (Array.isArray(baseFinalState.surgicalTechniques) && baseFinalState.surgicalTechniques.length > 0) || (Array.isArray(baseFinalState.materials) && baseFinalState.materials.length > 0)
                  ? "ai"
                  : "missing_techniques",
              });
              const finalState = {
                ...baseFinalState,
                diagnostico: normalizeAtlasDisplayedText(
                  cleanPlanningPlaceholder(visualDiagnosticCheck.suspectedDiagnosis) || baseFinalState.diagnostico
                ),
                anatomicalLocation: normalizeAtlasDisplayedText(
                  cleanPlanningPlaceholder(visualDiagnosticCheck.specificLocation) || baseFinalState.anatomicalLocation
                ),
              };
              setSurgicalPlan(finalState);
              setPlanRecommendation(finalState.recommendation || "uncertain");
              setSelectedStrategy(finalState.selectedStrategy ?? (finalState.recommendation === "surgical" ? "surgical" : finalState.recommendation === "conservative" ? "conservative" : null));

              const scientificPlanRaw = await generateScientificPlanWithTimeout(
                [
                  finalState.diagnostico,
                  spinePlan.anatomicalLocation,
                  spinePlan.level,
                  spinePlan.classification,
                  spinePlan.severity,
                ].filter(Boolean).join(" | "),
                aiSourceImages[0] || currentStaticImage || ""
              );
              if (scientificPlanRaw) {
                const scientificPlan = parseScientificPlanResponse(scientificPlanRaw);
                setScientificEvidenceReport(scientificPlan);
                setScientificEvidenceText(normalizeAtlasDisplayedText(formatScientificEvidenceText(scientificPlan, language)));
                if ((!finalState.conduta || !finalState.conduta.trim()) && scientificPlan?.surgicalIndication) {
                  setSurgicalPlan((prev) => (prev ? { ...prev, conduta: scientificPlan.surgicalIndication } : prev));
                }
              } else {
                setScientificEvidenceText(atlasScientificPlanUnavailableMessage);
                setScientificEvidenceReport(null);
              }
              void generateStructuredPatientOrientations(finalState.diagnostico || diag, language)
                .then((res) => setPatientOrientations(res))
                .catch(() => setPatientOrientations(fallbackStructuredOrientation({
                  diagnosisOrProcedure: finalState.diagnostico || diag,
                  anatomicalLocation: finalState.anatomicalLocation || "",
                  aoOtaCode: finalState.classificationCode || finalState.classificationLabel || "",
                  clinicalDescription: finalState.conduta || "",
                  language,
                })));
            };
            void runSpineScientificPlan().catch((e) => {
              console.warn("Spine structured planning failed for Atlas image flow", e);
              setScientificEvidenceText(atlasScientificPlanUnavailableMessage);
              setScientificEvidenceReport(null);
              setSurgicalPlan(emptySurgicalPlanState);
              setPlanRecommendation("uncertain");
              setSelectedStrategy(null);
            });
          } else {
            setSurgicalPlan(visualLockedState);
            setPlanRecommendation(visualLockedState.recommendation || "uncertain");
            setSelectedStrategy(visualLockedState.selectedStrategy ?? (visualLockedState.recommendation === "surgical" ? "surgical" : visualLockedState.recommendation === "conservative" ? "conservative" : null));
            const runScientificPlan = async () => {
              const scientificPlanQuery = [
                visualLockedState.diagnostico,
                result.analysis?.anatomicalRegion || result.anatomicalRegion || "",
                result.analysis?.fractureLocation || result.fractureLocation || result.analysis?.boneSegment || result.suspectedLesionRegion || "",
                result.analysis?.lesionType || result.analysis?.fractureType || "",
                result.analysis?.recommendedConduct || result.analysis?.clinicalOutput?.conduta || result.analysis?.clinicalOutput?.diagnosis || "",
              ].filter(Boolean).join(" | ");
              const scientificPlanRaw = await generateScientificPlanWithTimeout(
                scientificPlanQuery || visualLockedState.diagnostico || context || item,
                currentStaticImage || generatedResultUrl || undefined
              );
              if (scientificPlanRaw) {
                const scientificPlan = parseScientificPlanResponse(scientificPlanRaw);
                setScientificEvidenceReport(scientificPlan);
                setScientificEvidenceText(normalizeAtlasDisplayedText(formatScientificEvidenceText(scientificPlan, language)));
                if ((!visualLockedState.conduta || !visualLockedState.conduta.trim()) && scientificPlan?.surgicalIndication) {
                  setSurgicalPlan((prev) => (prev ? { ...prev, conduta: scientificPlan.surgicalIndication } : prev));
                }
              } else {
                setScientificEvidenceText(atlasScientificPlanUnavailableMessage);
                setScientificEvidenceReport(null);
              }
              console.log("[SurgicalPlan][parsed]", {
                hasTechniques: Array.isArray(visualLockedState.surgicalTechniques) && visualLockedState.surgicalTechniques.length > 0,
                techniquesCount: Array.isArray(visualLockedState.surgicalTechniques) ? visualLockedState.surgicalTechniques.length : 0,
                materialListCount: Array.isArray(visualLockedState.materials) ? visualLockedState.materials.length : 0,
                differentialsCount: Array.isArray(visualLockedState.diagnosticosDiferenciais) ? visualLockedState.diagnosticosDiferenciais.length : 0,
                autoMeasurementsCount: Array.isArray(autoMeasurementRows) ? autoMeasurementRows.length : 0,
                source: (Array.isArray(visualLockedState.surgicalTechniques) && visualLockedState.surgicalTechniques.length > 0) || (Array.isArray(visualLockedState.materials) && visualLockedState.materials.length > 0) ? "ai" : "fallback",
                reason: (Array.isArray(visualLockedState.surgicalTechniques) && visualLockedState.surgicalTechniques.length > 0) || (Array.isArray(visualLockedState.materials) && visualLockedState.materials.length > 0)
                  ? "ai"
                  : "missing_techniques",
              });
              void generateStructuredPatientOrientations(visualLockedState.diagnostico || diag, language)
                .then((res) => setPatientOrientations(res))
                .catch(() => setPatientOrientations(fallbackStructuredOrientation({
                  diagnosisOrProcedure: visualLockedState.diagnostico || diag,
                  anatomicalLocation: visualLockedState.anatomicalLocation || "",
                  aoOtaCode: visualLockedState.classificationCode || visualLockedState.classificationLabel || "",
                  clinicalDescription: visualLockedState.conduta || "",
                  language,
                })));
            };
            void runScientificPlan().catch((e) => {
              console.warn("Scientific evidence generation failed for Atlas image flow", e);
              setScientificEvidenceText(atlasScientificPlanUnavailableMessage);
              setScientificEvidenceReport(null);
            });
          }
        } else {
          setSurgicalPlan(emptySurgicalPlanState);
          setScientificEvidenceText('');
          setScientificEvidenceReport(null);
          setPlanRecommendation("uncertain");
          setSelectedStrategy(null);
        }

        if (isAtlas3DGeneration) {
          startSmoothProgress(72, 84, atlasProgressText.renderFinal, 'main-3d', 1, 200);
          scheduleSmoothProgress(1800, 84, 92, atlasProgressText.refine, 'main-3d', 1, 220);
        } else {
          updateAtlasProgress(90, atlasProgressText.finalAdjustments, 'main-2d');
        }

        const meas = await biomechanicsPromise;
        stopSmoothProgress();
        setAutoMeasurements(meas);
        setSelectedMedia(surgicalPlanningPrimaryFlow ? null : (isAtlas3DGeneration ? (generatedResultUrl || null) : (currentStaticImage || null)));
        console.info("[ATLAS][frontend][generation:media-set]", {
          mode: effectiveRenderStyle,
          analysisMode: effectiveAnalysisMode,
          durationMs: Date.now() - atlasFlowStartedAt,
          hasSelectedMedia: Boolean(isAtlas3DGeneration ? generatedResultUrl : currentStaticImage),
        });

        if (isAtlas3DGeneration) {
          updateAtlasProgress(96, atlasProgressText.finalize, 'main-3d');
        }

      } else {
        updateAtlasProgress(
          isAtlas3DGeneration ? 18 : 25,
          isAtlas3DGeneration ? atlasProgressText.prepare3d : atlasProgressText.prepare2d,
          atlasGenerationType
        );
        setDetectedAnatomyRegion(context || item);
        setDetectedAnatomicalAnchors('');

        if (isAtlas3DGeneration) {
          startSmoothProgress(20, 38, atlasProgressText.analyze3d, 'main-3d', 2, 220);
          scheduleSmoothProgress(2400, 42, 68, atlasProgressText.generate3d, 'main-3d', 2, 260);
        } else {
          updateAtlasProgress(45, atlasProgressText.analyze2d, 'main-2d');
        }
        
        const orientationPromise = generateStructuredPatientOrientations(item, language)
          .catch((error) => {
            console.warn("[ATLAS][orientation:fallback]", {
              item,
              mode: effectiveRenderStyle,
              analysisMode: effectiveAnalysisMode,
              error,
            });
            return fallbackStructuredOrientation({
              diagnosisOrProcedure: item,
              anatomicalLocation: context || "",
              aoOtaCode: "",
              clinicalDescription: context || item,
              language,
            });
          });
        const base64 = surgicalPlanningPrimaryFlow
          ? ""
          : await generateOrthoDiagram(item, effectiveRenderStyle, "", atlasQuality, effectiveAnalysisMode);
        console.info("[ATLAS][frontend][generation:diagram-end]", {
          mode: effectiveRenderStyle,
          analysisMode: effectiveAnalysisMode,
          durationMs: Date.now() - atlasFlowStartedAt,
          hasDiagram: Boolean(base64),
          hasOrientations: false,
        });
        stopSmoothProgress();
        updateAtlasProgress(
          isAtlas3DGeneration ? 72 : 70,
          isAtlas3DGeneration ? atlasProgressText.renderFinal : atlasProgressText.generate2d,
          atlasGenerationType
        );

        if (base64) {
           const img = `data:image/png;base64,${base64}`;
           setStaticImage(img);
           setSelectedMedia(img);
           if (isAtlas3DGeneration) {
             setGenerated3DImageUrl(img);
             setGenerated3DImageBase64(base64);
             setGeneratedAtlasImage({
               base64,
               url: img,
               mimeType: "image/png",
               createdAt: Date.now(),
             });
             setActiveVisualizationMode('3d');
             setActiveTab('visualizacao3d');
           } else {
             setGenerated2DImageUrl(img);
             setGenerated2DImageBase64(base64);
             setActiveVisualizationMode('2d');
             setActiveTab('visualizacao2d');
           }
           measureAnatomyFromImage(img, language)
             .then(setAutoMeasurements)
             .catch((measureError) => {
               console.warn("Automatic anatomy measurement failed for Atlas render flow", measureError);
             });
        }
          setLesionMarker(null);
          setLesionBox(null);

        let planText = "";
        const spinePlanQuery = [
          item,
          context || "",
        ].filter(Boolean).join(" | ");

        if (isSpinePlanningCase(spinePlanQuery)) {
          try {
            planText = await generateSurgicalPlanText(
              spinePlanQuery,
              language,
              base64 ? `data:image/png;base64,${base64}` : ""
            );
            const parsedPlan = parseStructuredClinicalPlanPayload(planText);
            const structuredState = buildClinicalPlanState(parsedPlan);
            const planDiagnosis = structuredState.diagnostico;
            setSurgicalPlan(structuredState);
            setPlanRecommendation(structuredState.recommendation || "uncertain");
            setSelectedStrategy(structuredState.selectedStrategy ?? (structuredState.recommendation === "surgical" ? "surgical" : structuredState.recommendation === "conservative" ? "conservative" : null));

            try {
              const scientificPlanRaw = await generateScientificSurgicalPlanText(
                [
                  planDiagnosis,
                  parsedPlan.anatomicalLocation,
                  parsedPlan.level,
                  parsedPlan.classification,
                  parsedPlan.severity,
                ].filter(Boolean).join(" | "),
                language
              );
              const scientificPlan = parseScientificPlanResponse(scientificPlanRaw);
              setScientificEvidenceReport(scientificPlan);
              setScientificEvidenceText(normalizeAtlasDisplayedText(formatScientificEvidenceText(scientificPlan, language)));
              if ((!structuredState.conduta || !structuredState.conduta.trim()) && scientificPlan?.surgicalIndication) {
                setSurgicalPlan((prev) => (prev ? { ...prev, conduta: scientificPlan.surgicalIndication } : prev));
              }
            } catch (e) {
              console.warn("Scientific evidence generation failed for Atlas text flow", e);
              setScientificEvidenceText(scientificPlanFallbackMessage);
              setScientificEvidenceReport(null);
            }
          } catch (planError) {
            console.warn("Structured spine plan generation failed for Atlas text flow", planError);
            setSurgicalPlan(emptySurgicalPlanState);
            setScientificEvidenceText(scientificPlanFallbackMessage);
            setScientificEvidenceReport(null);
            setPlanRecommendation("uncertain");
            setSelectedStrategy(null);
          }
        } else {
          try {
            planText = await generateSurgicalPlanText(item, language);
          } catch (planError) {
            console.warn("Surgical plan generation failed for Atlas text flow", planError);
            setScientificEvidenceText(scientificPlanFallbackMessage);
            setScientificEvidenceReport(null);
          }

          if (planText) {
            const parsedPlan = parseStructuredClinicalPlanPayload(planText);
            const structuredState = buildClinicalPlanState(parsedPlan);
            const planDiagnosis = structuredState.diagnostico;
            setSurgicalPlan(structuredState);
            setPlanRecommendation(structuredState.recommendation || "uncertain");
            setSelectedStrategy(structuredState.selectedStrategy ?? (structuredState.recommendation === "surgical" ? "surgical" : structuredState.recommendation === "conservative" ? "conservative" : null));
            try {
              const scientificPlanRaw = await generateScientificSurgicalPlanText(planDiagnosis || item, language);
              const scientificPlan = parseScientificPlanResponse(scientificPlanRaw);
              setScientificEvidenceReport(scientificPlan);
              setScientificEvidenceText(normalizeAtlasDisplayedText(formatScientificEvidenceText(scientificPlan, language)));
              if ((!structuredState.conduta || !structuredState.conduta.trim()) && scientificPlan?.surgicalIndication) {
                setSurgicalPlan((prev) => (prev ? { ...prev, conduta: scientificPlan.surgicalIndication } : prev));
              }
            } catch (e) {
              console.warn("Scientific evidence generation failed for Atlas text flow", e);
              setScientificEvidenceText(scientificPlanFallbackMessage);
              setScientificEvidenceReport(null);
            }
          } else {
            setSurgicalPlan(emptySurgicalPlanState);
            setScientificEvidenceText(scientificPlanFallbackMessage);
            setScientificEvidenceReport(null);
            setPlanRecommendation("uncertain");
            setSelectedStrategy(null);
          }
        }

        void orientationPromise.then((orientationData) => {
          if (orientationData) {
            setPatientOrientations(orientationData);
          }
        });

        if (isAtlas3DGeneration) {
          updateAtlasProgress(92, atlasProgressText.refine, 'main-3d');
          updateAtlasProgress(96, atlasProgressText.finalize, 'main-3d');
        } else {
          updateAtlasProgress(90, atlasProgressText.finalAdjustments, 'main-2d');
        }
      }

      setViewMode('image');
      if (surgicalPlanningPrimaryFlow) {
        setActiveTab('plano');
      }
      await finishAtlasProgress();
      console.info("[ATLAS][frontend][generation:done]", {
        item,
        mode: renderStyle,
        analysisMode,
        durationMs: Date.now() - atlasFlowStartedAt,
      });
      
    } catch (err: any) {
      resetAtlasProgress();
      console.error("[ATLAS][frontend][generation:error]", {
        item,
        mode: renderStyle,
        analysisMode,
        rawError: err,
      });
      if (err instanceof UsageLimitError || err?.message === t('usageAuthenticationRequired') || err?.debugCode === "ATLAS_RATE_LIMIT") {
        console.warn("[SurgicalPlan][plan-permission:blocked]", {
          reason: err instanceof UsageLimitError || err?.debugCode === "ATLAS_RATE_LIMIT" ? "usage-limit" : "authentication-required",
          details: err?.message || getUsageErrorMessage(err),
        });
        setError({ message: t('usageLimitTitle'), details: err?.message || getUsageErrorMessage(err) });
      } else {
        handleApiError(err);
      }
    } finally {
      setIsGenerating(false); setGenStep('');
    }
  }, [analysisMode, atlasProgressText, finishAtlasProgress, language, renderStyle, resetAtlasProgress, scheduleSmoothProgress, startSmoothProgress, stopSmoothProgress, t, updateAtlasProgress, uploadedImages]);

  const handleSaveAtlasToPatient = async () => {
  try {
    if (!activePatient?.id) return alert("Nenhum paciente selecionado.");
    const uid = auth.currentUser?.uid;
    if (!uid) return alert("Você precisa estar logado.");
    if (!staticImage) return alert("Nenhuma imagem gerada.");

    const uploaded = await uploadAtlasImage({
      uid,
      patientId: activePatient.id,
      dataUrl: staticImage,
      kind: "static",
    });

    const prevImages = (activePatient as any)?.atlasAssets?.images ?? [];

    await updatePatient(activePatient.id, {
      atlasAssets: {
        ...(activePatient as any)?.atlasAssets,
        images: [
          {
            url: uploaded.url,
            path: uploaded.path,
            contentType: uploaded.contentType,
            createdAt: new Date().toISOString(),
          },
          ...prevImages,
        ],
      },
    } as any);

    setSavedToPatient(true);
  } catch (err: any) {
    console.error(err);
    alert(err?.message || "Erro ao salvar no Firebase.");
  }
};
  
  const toggleMaterialCheck = (index: number) => {
    setMaterialList(prev => prev.map((item, i) => i === index ? { ...item, checked: !item.checked } : item));
  };

  const copyMaterials = () => {
    const textToCopy = materialList
       .filter(m => !m.checked) 
       .map(m => `[${m.checked ? 'x' : ' '}] ${m.text}`)
       .join('\n');
    
    if (materialList.length > 0) {
      navigator.clipboard.writeText(`Gabarito Cirúrgico - ${currentItem || 'Caso'}\n\n${textToCopy}`);
      setMaterialsCopied(true);
      setTimeout(() => setMaterialsCopied(false), 2000);
    }
  };

  const getOpmeJustification = () => {
    const diag = normalizeAtlasDisplayedText(surgicalPlan?.diagnostico || 'Patologia não especificada');
    const proc = normalizeAtlasDisplayedText(currentItem || 'Procedimento Cirúrgico');
    const screwTxt = biometrics.screwLength > 0 ? `tamanho de ${biometrics.screwLength} mm` : 'tamanho calculado via software';
    
    return `AO: Departamento de Auditoria Médica / Convênio
ASSUNTO: Justificativa Técnica para Planejamento Cirúrgico de Alta Precisão

1. IDENTIFICAÇÃO DO PACIENTE
Nome: ${activePatient?.name || '___________________________________________________'}
Diagnóstico: ${diag}
Procedimento Proposto: ${proc}

2. DESCRIÇÃO DO PLANEJAMENTO TÉCNICO
O planejamento pré-operatório para este caso foi realizado utilizando a plataforma de análise biomecânica OrthoPrecision, que integra reconstrução tridimensional (3D) a partir de exames de imagem do paciente e geração de série radiológica sintética.

O estudo pré-cirúrgico permitiu identificar:
- Classificação da Lesão: ${diag}.
- Vetor de Fixação: Definido através de simulação em 3 faces (AP, Lateral e Oblíqua), garantindo a centralização do implante no eixo longo do fragmento.

3. JUSTIFICATIVA PARA MATERIAIS SOLICITADOS (OPME)
Com base na análise de densidade óssea e volumetria realizada pelo software, os materiais solicitados são imprescindíveis pelos seguintes motivos:

- Precisão do Implante: Foi identificado que o uso de material de ${screwTxt} é necessário para ocupar aproximadamente ${biometrics.canalOcc > 0 ? biometrics.canalOcc : '80'}% do volume alvo, garantindo estabilidade absoluta e compressão do foco da fratura, reduzindo o risco de pseudartrose.
- Minimização de Riscos: O mapeamento em face oblíqua simulada demonstrou que a técnica proposta evita a extrusão cortical, protegendo as superfícies articulares adjacentes.`;
  };

  const copyOpmeJustification = () => {
    const text = getOpmeJustification();
    navigator.clipboard.writeText(text);
    setOpmeCopied(true);
    setTimeout(() => setOpmeCopied(false), 2000);
  };

  const handleSaveCase = () => {
    if (!surgicalPlan) return;
    
    const newCase: SavedCase = {
      id: Date.now().toString(),
      title: currentItem || searchQuery || "Case",
      date: Date.now(),
      image: staticImage || uploadedImages.find((img) => !!img && !img.startsWith('data:application/pdf')) || "", 
      plan: surgicalPlan
    };

    setSavedCases(prev => [newCase, ...prev]);
    alert(language === 'pt' ? "Caso salvo na Biblioteca Offline!" : "Case saved to Offline Library!");
  };

  const handleDeleteCase = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm(language === 'pt' ? "Excluir caso da biblioteca?" : "Delete case from library?")) {
      setSavedCases(prev => prev.filter(c => c.id !== id));
    }
  };

  const handleLoadCase = (savedCase: SavedCase) => {
    setCurrentItem(savedCase.title);
    setSearchQuery(savedCase.title);
    setInputImage(savedCase.image);
    setStaticImage(savedCase.image);
    setSelectedMedia(savedCase.image);
    setGeneratedAtlasImage({
      url: savedCase.image,
      mimeType: savedCase.image.startsWith("data:image/") ? savedCase.image.split(";")[0].replace("data:", "") : "image/png",
      createdAt: Date.now(),
    });
    setViewMode('image');
    const loadedConduct = normalizeSurgicalConduct((savedCase.plan as any)?.conduta);
    setSelectedStrategy(loadedConduct === 'surgical' ? 'surgical' : loadedConduct === 'non-surgical' ? 'conservative' : null);
    setPlanRecommendation(loadedConduct === 'surgical' ? 'surgical' : loadedConduct === 'non-surgical' ? 'conservative' : 'uncertain');
    setSurgicalPlan({
      ...emptySurgicalPlanState,
      ...savedCase.plan,
      diagnosticosDiferenciais: normalizeDifferentialDiagnosisList(savedCase.plan?.diagnosticosDiferenciais),
    });
    setShowLibrary(false);
    setZoomScale(1);
    setPanOffset({x:0, y:0});
    setPatientOrientations(null); 
    setAutoMeasurements(null); 
    setLesionMarker(null);
    setLesionBox(null);
  };

  // --- SAVE TO PATIENT FUNCTION ---
  const handleSaveToPatient = () => {
    if (!activePatient) {
        alert(t('selectPatientFirst'));
        return;
    }

    try {
        // 1. Gather report images (original RX + Atlas reconstruction) without duplicates
        const originalRxImage = uploadedImages.find(
          (img) => !!img && !img.startsWith('data:application/pdf')
        ) || null;
        const imagesToSave: string[] = Array.from(
          new Set(
            [originalRxImage, staticImage].filter((img): img is string => !!img)
          )
        );

        const atlasLocale = language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : 'pt-BR';
        const atlasLanguageLabel = language === 'en' ? 'English' : language === 'es' ? 'Español' : 'Português';

        // 2. Prepare the Clinical Note (Text Report)
        const parts = [];
        parts.push(
          `--- METADADOS DA ANÁLISE ---\n` +
          `- Data/hora: ${new Date().toLocaleString(atlasLocale)}\n` +
          `- Tipo de análise: Plano Cirúrgico\n` +
          `- Paciente: ${activePatient.name}\n` +
          `- Idioma: ${atlasLanguageLabel}\n` +
          `- Imagem original: ${originalRxImage ? 'anexada' : 'não disponível'}\n` +
          `- Imagem gerada: ${staticImage ? 'anexada' : 'não utilizada no fluxo principal'}`
        );
        
        parts.push(`${t('surgicalPlanReportTitle')}: ${currentItem || t('surgicalPlanAnalysisImage')}`);
        if (imagesToSave.length > 0) {
            parts.push(`(Foram salvas ${imagesToSave.length} imagens de planejamento na galeria do paciente)`);
        }
        
        if (surgicalPlan) {
          parts.push(`--- DIAGNÓSTICO ---\n${surgicalPlan.diagnostico}`);
          parts.push(`--- ESTRATÉGIA SELECIONADA ---\n${selectedPlanLabel}`);
          if (scientificEvidenceText) {
            parts.push(`--- EVIDÊNCIA CIENTÍFICA ---\n${scientificEvidenceText}`);
          }
          if (surgicalPlanDifferentials.length > 0) {
            parts.push(`--- DIAGNÓSTICOS DIFERENCIAIS ---\n${surgicalPlanDifferentials.map((item) => `- ${item}`).join('\n')}`);
          }
          parts.push(
            activeStrategy === "surgical"
              ? `--- TÉCNICA CIRÚRGICA DETALHADA ---\n${selectedPlanBullets.map((item) => `- ${item}`).join('\n')}`
              : `--- CONDUTA CONSERVADORA ---\n${selectedPlanBullets.map((item) => `- ${item}`).join('\n')}`
          );

          if (selectedPlanSecondaryBullets.length > 0) {
            parts.push(`--- SEGUIMENTO / PÓS-OPERATÓRIO ---\n${selectedPlanSecondaryBullets.map((item) => `- ${item}`).join('\n')}`);
          }

          if (selectedPlanRiskBullets.length > 0) {
             parts.push(`--- ALERTAS / ARMADILHAS ---\n${selectedPlanRiskBullets.map((item) => `- ${item}`).join('\n')}`);
          }
        }

        if (biometrics.screwLength > 0 || biometrics.canalOcc > 0) {
          parts.push(`--- BIOMETRIA CIRÚRGICA ---\n- Parafuso Principal Estimado: ${biometrics.screwLength}mm\n- Ocupação do Canal/Placa: ${biometrics.canalOcc}%\n- Angulação Alvo: ${biometrics.angulation}°`);
        }

        if (autoMeasurementRows.length) {
            const auto = autoMeasurementRows.map(m => `- ${m.label}: ${m.value} (${m.status || 'N/A'})`).join('\n');
            parts.push(`--- MEDIÇÕES AUTOMÁTICAS (IA) ---\n${auto}`);
        }

        if (materialList.length > 0) {
          const mats = materialList.map(m => `[${m.checked ? 'x' : ' '}] ${m.text}`).join('\n');
          parts.push(`--- CHECKLIST DE MATERIAIS ---\n${mats}`);
        }

        if (activeStrategy === "surgical" ? activeSurgicalOption.planB?.length : activeConservativePlan.conversionCriteria?.length) {
           parts.push(
             activeStrategy === "surgical"
               ? `--- PLANO B (CONTINGÊNCIA) ---\n${(activeSurgicalOption.planB || []).map((item) => `- ${item}`).join('\n')}`
               : `--- CRITÉRIOS DE CONVERSÃO ---\n${(activeConservativePlan.conversionCriteria || []).map((item) => `- ${item}`).join('\n')}`
           );
        }

        parts.push(`--- JUSTIFICATIVA TÉCNICA (OPME) ---\n${getOpmeJustification()}`);

        const newNote: ClinicalNote = {
          id: Date.now().toString(),
          date: Date.now(),
          type: 'Atlas',
          title: `Planejamento 3D Completo: ${currentItem || 'Caso'}`,
          content: parts.join('\n\n')
        };

        // 3. Update Patient
        updatePatient(activePatient.id, {
          clinicalNotes: [newNote, ...(activePatient.clinicalNotes || [])],
          images: [...(activePatient.images || []), ...imagesToSave]
        });
        
        setSavedToPatient(true);
        setTimeout(() => setSavedToPatient(false), 3000);

    } catch (e: any) {
        console.error("Save failed:", e);
        // "Blue screen" prevention: If quota exceeded, alert user instead of crashing React
        if (e.name === 'QuotaExceededError' || e.message?.toLowerCase().includes('quota')) {
        alert(language === 'pt' 
                ? "Armazenamento Local Cheio! Não foi possível salvar as imagens de alta resolução. Tente excluir pacientes antigos ou limpar dados." 
                : "Local Storage Full! Could not save high-res images. Try deleting old patients.");
        } else {
             alert(t('saveRecordError'));
        }
    }
  };

  const handlePrintReport = () => {
    const printContent = document.getElementById('print-area');
    if (!printContent) return;
    const clone = printContent.cloneNode(true) as HTMLElement;
    const controls = clone.querySelectorAll('.print\\:hidden');
    controls.forEach(c => c.remove());
    clone.style.position = 'static';
    clone.style.overflow = 'visible';
    clone.style.height = 'auto';
    clone.style.width = '100%';

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
        alert("Por favor, permita popups para imprimir o relatório.");
        return;
    }

    const html = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>OrthoPrecision Report - ${currentItem || 'Case'}</title>
            <script src="https://cdn.tailwindcss.com"></script>
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
            <style>
                body { 
                    font-family: 'Inter', sans-serif; 
                    background: white; 
                    color: black; 
                    padding: 0;
                    margin: 0;
                    -webkit-print-color-adjust: exact; 
                    print-color-adjust: exact; 
                }
                .surgical-report-print,
                .surgical-report-print * {
                    color: #0f172a !important;
                }
                .surgical-report-print {
                    background: #ffffff !important;
                }
                .surgical-report-print h1,
                .surgical-report-print h2,
                .surgical-report-print h3,
                .surgical-report-print h4 {
                    color: #020617 !important;
                }
                .surgical-report-print p,
                .surgical-report-print li,
                .surgical-report-print span,
                .surgical-report-print div,
                .surgical-report-print strong,
                .surgical-report-print td,
                .surgical-report-print th,
                .surgical-report-print table {
                    color: #0f172a !important;
                }
                .surgical-report-print li::marker {
                    color: #0891b2 !important;
                }
                .surgical-report-print .text-white { color: #0f172a !important; }
                .surgical-report-print .text-slate-50,
                .surgical-report-print .text-slate-100,
                .surgical-report-print .text-slate-200,
                .surgical-report-print .text-slate-300,
                .surgical-report-print .text-slate-400,
                .surgical-report-print .text-slate-500,
                .surgical-report-print .text-slate-600 {
                    color: #0f172a !important;
                }
                .surgical-report-print .bg-slate-900,
                .surgical-report-print .bg-slate-800,
                .surgical-report-print .bg-slate-700,
                .surgical-report-print .bg-slate-600,
                .surgical-report-print .bg-slate-500 {
                    background-color: #ffffff !important;
                    border-color: #e2e8f0 !important;
                }
                .surgical-report-print .bg-yellow-50,
                .surgical-report-print .bg-yellow-100,
                .surgical-report-print .bg-amber-50,
                .surgical-report-print .bg-amber-100 {
                    background: #fffbea !important;
                    border: 1px solid #facc15 !important;
                    color: #0f172a !important;
                }
                .surgical-report-print .border-slate-700,
                .surgical-report-print .border-slate-800,
                .surgical-report-print .border-slate-900 {
                    border-color: #cbd5e1 !important;
                }
                
                .grid { display: grid; }
                .flex { display: flex; }
                img { max-width: 100%; height: auto; display: block; }
                
                @media print {
                    @page { margin: 1.5cm; size: A4; }
                    body { -webkit-print-color-adjust: exact; }
                    .break-inside-avoid { break-inside: avoid; page-break-inside: avoid; }
                }
            </style>
        </head>
        <body>
            <div class="surgical-report-print max-w-5xl mx-auto p-8">
                ${clone.innerHTML}
                
                <div class="mt-12 pt-8 border-t-2 border-slate-900 flex flex-col items-center text-center">
                    <div class="w-48 h-0.5 bg-slate-400 mb-2"></div>
                    <p class="text-sm font-bold text-slate-900">${activePatient?.responsibleDoctor?.name || doctorData?.name || 'Médico Responsável'}</p>
                    <p class="text-xs text-slate-600">
                      ${(activePatient?.responsibleDoctor?.crm || doctorData?.crm) ? `CRM: ${activePatient?.responsibleDoctor?.crm || doctorData?.crm}` : ''} 
                      ${(activePatient?.responsibleDoctor?.rqe || doctorData?.rqe) ? ` • RQE: ${activePatient?.responsibleDoctor?.rqe || doctorData?.rqe}` : ''}
                    </p>
                    <p class="text-[10px] text-slate-400 mt-4 uppercase tracking-widest">Responsabilidade Técnica • Carimbo Digital Ortho ID</p>
                </div>

                <div class="fixed bottom-4 left-0 w-full text-center text-[8px] text-slate-300 uppercase tracking-tighter">
                    Documento gerado eletronicamente via OrthoPrecision System em ${new Date().toLocaleString()}
                </div>
            </div>
            <script>
                window.onload = function() {
                    setTimeout(function() {
                        window.print();
                    }, 500);
                };
            </script>
        </body>
        </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleDownloadMedia = (e: React.MouseEvent) => {
    e.stopPropagation();
    const mediaToDownload = viewMode === 'video' ? selectedMedia : activeGeneratedAtlasMedia;
    if (!mediaToDownload) return;
    
    const link = document.createElement('a');
    link.href = mediaToDownload;
    link.download = viewMode === 'video' 
      ? `Ortho-Veo-${Date.now()}.mp4` 
      : `Ortho-Atlas-${activeVisualizationMode.toUpperCase()}-${Date.now()}.png`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const showCaptureNotice = useCallback((message: string, kind: 'success' | 'error') => {
    setCaptureNotice({ message, kind });
    window.setTimeout(() => setCaptureNotice(null), 2500);
  }, []);

  const downloadCanvasBlob = useCallback((blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, []);

  const captureCompareAsImage = useCallback(async (captureTarget: HTMLDivElement | null) => {
    if (!captureTarget || isCapturing) return;

    const capturePromise = (async () => {
      const html2canvasModule = await import('html2canvas');
      const html2canvas = (html2canvasModule as any).default || html2canvasModule;
      const canvas = await html2canvas(captureTarget, {
        scale: 1,
        useCORS: true,
        backgroundColor: null,
        logging: false,
      });

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((result: any) => {
          if (!result) {
            reject(new Error('Falha ao gerar blob da captura.'));
            return;
          }
          resolve(result);
        }, 'image/png');
      });

      downloadCanvasBlob(blob, `Ortho-Compare-${Date.now()}.png`);
    })();

    const captureTimeout = new Promise<never>((_, reject) => {
      window.setTimeout(() => reject(new Error('Tempo limite ao capturar imagem.')), 15000);
    });

    try {
      setIsCapturing(true);
      await Promise.race([capturePromise, captureTimeout]);
      showCaptureNotice(language === 'pt' ? 'Imagem capturada com sucesso.' : 'Image captured successfully.', 'success');
    } catch (captureError) {
      console.error('[ATLAS][CAPTURE_FAILED]', captureError);

      const fallbackUrl = uploadedImages?.[0] || activeGeneratedAtlasMedia || selectedMedia;
      if (fallbackUrl) {
        try {
          const link = document.createElement('a');
          link.href = fallbackUrl;
          link.download = `Ortho-Atlas-${Date.now()}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          showCaptureNotice(language === 'pt' ? 'Captura indisponível. A imagem foi baixada diretamente.' : 'Capture unavailable. The image was downloaded directly.', 'success');
          return;
        } catch (fallbackError) {
          console.error('[ATLAS][CAPTURE_FALLBACK_FAILED]', fallbackError);
        }
      }

      showCaptureNotice(language === 'pt' ? 'Não foi possível capturar a imagem. Tente novamente.' : 'Could not capture the image. Please try again.', 'error');
    } finally {
      setIsCapturing(false);
    }
  }, [activeGeneratedAtlasMedia, downloadCanvasBlob, isCapturing, language, selectedMedia, showCaptureNotice, uploadedImages]);

  const handleViewerZoomIn = () => {
    if (!compareMode) {
      setZoomScale((prev) => Math.min(4, prev + 0.2));
      return;
    }

    const targetPane = compareSyncEnabled ? activeComparePane : activeComparePane;
    const { zoom, pan } = getComparePaneState(targetPane);
    setComparePaneState(targetPane, Math.min(4, zoom + 0.2), pan);
  };

  const handleViewerReset = () => {
    if (!compareMode) {
      setZoomScale(1);
      setPanOffset({ x: 0, y: 0 });
      setBrightness(100);
      setOriginalRotation(0);
      setAtlasRotation(0);
      return;
    }

    if (compareSyncEnabled) {
      setCompareOriginalZoomScale(1);
      setCompareAtlasZoomScale(1);
      setCompareOriginalPanOffset({ x: 0, y: 0 });
      setCompareAtlasPanOffset({ x: 0, y: 0 });
      setOriginalRotation(0);
      setAtlasRotation(0);
      setBrightness(100);
    } else {
      const targetPane = activeComparePane;
      setComparePaneState(targetPane, 1, { x: 0, y: 0 }, false);
      if (targetPane === 'original') {
        setOriginalRotation(0);
      } else {
        setAtlasRotation(0);
        setBrightness(100);
      }
    }
  };

  const handleViewerRotate = () => {
    if (compareMode) {
      setAtlasRotation((prev) => (prev + 90) % 360);
      return;
    }

    rotateActiveViewerImage();
  };

  const handleViewerLight = () => {
    if (!compareMode) {
      setBrightness((b) => (b >= 175 ? 100 : b + 25));
      return;
    }

    setBrightness((b) => (b >= 175 ? 100 : b + 25));
  };

  const handleViewerCapture = async () => {
    if (isCapturing) return;

    if (compareMode) {
      await captureCompareAsImage(compareCaptureRef.current);
      return;
    }

    if (isFullscreen) {
      await captureCompareAsImage(containerRef.current);
      return;
    }

    handleDownloadMedia({ stopPropagation() {} } as any);
  };

  const handleApiError = (err: any) => {
    let msg = "Unknown Error";
    if (err) {
        msg = typeof err === 'string' ? err : (err.message || JSON.stringify(err) || "Unknown Error");
    }
    
    let status = err?.status || 0;
    const code = String(err?.code || err?.debugCode || err?.details?.debugCode || "").toLowerCase();
    const details = String(err?.details || err?.details?.message || msg || "");
    
    const isQuota = status === 429 || msg.includes("429") || msg.includes("RESOURCE_EXHAUSTED") || msg.includes("quota");
    const isPermission = status === 403 || msg.includes("403") || msg.includes("PERMISSION_DENIED");
    const isAtlasImageIssue =
      code.includes("atlas_image") ||
      code.includes("atlas_timeout") ||
      code.includes("atlas_payload") ||
      code.includes("atlas_json") ||
      code.includes("atlas_ai") ||
      msg.toLowerCase().includes("timed out") ||
      msg.toLowerCase().includes("request timed out") ||
      msg.toLowerCase().includes("não retornou imagem") ||
      msg.toLowerCase().includes("nao retornou imagem");
    
    if (isQuota) {
      setError({ message: "Quota Exceeded (429)", details: "Veo video generation requires a paid API key.", showKeyBtn: true });
    } else if (isPermission) {
      setError({ message: "Permission Denied (403)", details: "Your API key does not have permission for Veo models.", showKeyBtn: true });
    } else if (isAtlasImageIssue) {
      setError({
        message: language === 'pt' ? "Não foi possível gerar o Atlas agora." : language === 'es' ? "No fue posible generar Atlas ahora." : "Could not generate Atlas right now.",
        details: details || (language === 'pt' ? "Tente novamente. Se o problema persistir, envie uma imagem menos pesada." : language === 'es' ? "Inténtalo de nuevo. Si el problema persiste, envía una imagen más ligera." : "Try again. If the issue persists, send a smaller image."),
      });
    } else {
      setError({
        message: language === 'pt' ? "Falha no Atlas" : language === 'es' ? "Fallo de Atlas" : "Atlas failed",
        details: details || msg,
      });
    }
  };

  const readFileAsDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(reader.error || new Error("Falha ao ler arquivo."));
      reader.readAsDataURL(file);
    });

  const processSurgicalPlanFiles = async (files: File[]) => {
    if (files.length === 0) return;

    try {
      setIsUploadingImages(true);
      const remainingSlots = 5 - uploadedImages.length;
      if (remainingSlots <= 0) return;

      const filesToProcess = files.slice(0, remainingSlots);
      const allDataUrls = await Promise.all(filesToProcess.map(readFileAsDataUrl));
      const newImages = allDataUrls.filter(isValidSurgicalPlanAttachment);

      if (newImages.length > 0) {
        setUploadedImages(prev => [...prev, ...newImages]);
      } else {
        // Arquivo enviado não é imagem ou PDF válido — dar feedback ao usuário
        setError({
          message: language === 'pt' ? "Formato não suportado" : language === 'es' ? "Formato no compatible" : "Unsupported format",
          details: language === 'pt'
            ? "Envie imagens (JPG, PNG, WEBP) ou PDFs. Arquivos DICOM ou outros formatos não são suportados."
            : language === 'es'
              ? "Envíe imágenes (JPG, PNG, WEBP) o PDFs."
              : "Upload images (JPG, PNG, WEBP) or PDFs. DICOM or other formats are not supported.",
        });
      }
    } catch (uploadError) {
      console.error("[SurgicalPlan][upload:error]", uploadError);
      setError({
        message: language === 'pt' ? "Falha ao anexar exame" : "Could not attach exam",
        details: language === 'pt' ? "Tente enviar novamente ou use outro arquivo." : "Try uploading again or use another file.",
      });
    } finally {
      setIsUploadingImages(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await processSurgicalPlanFiles(Array.from(e.target.files || []));
  };
  
  const handleRemoveImage = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    if (uploadedImages.length <= 1 && fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSetPrimaryImage = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (index === 0) return;
    const newImages = [...uploadedImages];
    const selected = newImages[index];
    newImages.splice(index, 1);
    newImages.unshift(selected);
    setUploadedImages(newImages);
  };

  const handleResetUpload = (e: React.MouseEvent) => {
    e.stopPropagation();
    setUploadedImages([]);
    setManualAnatomy('');
    setInputImage(null);
    setSelectedMedia(null);
    setGenerated2DImageUrl(null);
    setGenerated2DImageBase64(null);
    setGenerated3DImageUrl(null);
    setGenerated3DImageBase64(null);
    setGeneratedAtlasImage(null);
    setActiveVisualizationMode('3d');
    setActiveTab('visualizacao3d');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleGenerate360 = async () => {
    if (!staticImage) return;
    if (video360) {
      setViewMode('video');
      setSelectedMedia(video360);
      return;
    }
    resetAtlasProgress();
    setIsGenerating(true);
    updateAtlasProgress(5, atlasGenerationLabels.start360, 'video-360');
    try {
      updateAtlasProgress(10, atlasProgressText.validate, 'video-360');
      updateAtlasProgress(25, atlasGenerationLabels.prepare360, 'video-360');
      startSmoothProgress(40, 88, atlasGenerationLabels.generate360, 'video-360', 2, 220);
      const result = await generate360OrthoVideo(currentItem || "Anatomy", "", staticImage);
      stopSmoothProgress();
      if (result) {
        setVideo360(result.videoUrl);
        setViewMode('video');
        setSelectedMedia(result.videoUrl);
        setIsInteractive(true);
      }
      updateAtlasProgress(90, atlasGenerationLabels.refine360, 'video-360');
      await finishAtlasProgress();
    } catch (err: any) {
      resetAtlasProgress();
      handleApiError(err);
    } finally {
      setIsGenerating(false);
      setGenStep('');
    }
  };

  const switchToStatic = () => {
    if (staticImage) {
      setViewMode('image');
      setSelectedMedia(staticImage);
      setActiveTab(activeVisualizationMode === '2d' ? 'visualizacao2d' : 'visualizacao3d');
      setZoomScale(1); setPanOffset({x:0, y:0});
    }
  };

 const handleOriginalWheel = (e: React.WheelEvent<HTMLDivElement>) => {
  e.preventDefault();
  e.stopPropagation();

  const zoomStep = 0.12;
  const direction = e.deltaY > 0 ? -1 : 1;

  setZoomScale((prev) => {
    const next = Math.min(Math.max(1, prev + direction * zoomStep), 4);

    if (next === 1) {
      setPanOffset({ x: 0, y: 0 });
    }

    return next;
  });
};

  const getComparePaneState = (pane: 'original' | 'reconstruction') => {
    if (pane === 'original') {
      return {
        zoom: compareOriginalZoomScale,
        pan: compareOriginalPanOffset,
      };
    }

    return {
      zoom: compareAtlasZoomScale,
      pan: compareAtlasPanOffset,
    };
  };

  const setComparePaneState = (
    pane: 'original' | 'reconstruction',
    nextZoom: number,
    nextPan: { x: number; y: number },
    sync = compareSyncEnabled
  ) => {
    const normalizedZoom = Math.min(Math.max(1, nextZoom), 4);
    const normalizedPan = normalizedZoom <= 1.02 ? { x: 0, y: 0 } : clampComparePan(pane, normalizedZoom, nextPan);

    if (sync) {
      setCompareOriginalZoomScale(normalizedZoom);
      setCompareAtlasZoomScale(normalizedZoom);
      setCompareOriginalPanOffset(normalizedPan);
      setCompareAtlasPanOffset(normalizedPan);
      return;
    }

    if (pane === 'original') {
      setCompareOriginalZoomScale(normalizedZoom);
      setCompareOriginalPanOffset(normalizedPan);
    } else {
      setCompareAtlasZoomScale(normalizedZoom);
      setCompareAtlasPanOffset(normalizedPan);
    }
  };

  const handleCompareWheel = (pane: 'original' | 'reconstruction', e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const { zoom, pan } = getComparePaneState(pane);
    const zoomStep = 0.12;
    const direction = e.deltaY > 0 ? -1 : 1;
    const nextZoom = Math.min(Math.max(1, zoom + direction * zoomStep), 4);

    if (nextZoom === 1) {
      setComparePaneState(pane, 1, { x: 0, y: 0 });
      return;
    }

    setComparePaneState(pane, nextZoom, pan);
  };

  const handleCompareStart = (pane: 'original' | 'reconstruction', e: React.MouseEvent | React.TouchEvent) => {
    if (!compareMode) return;
    const isTouch = 'touches' in e;
    const x = isTouch ? e.touches[0].clientX : e.clientX;
    const y = isTouch ? e.touches[0].clientY : e.clientY;
    const { pan } = getComparePaneState(pane);
    setActiveComparePane(pane);
    setCompareDragPane(pane);
    setCompareInitialX(x);
    setCompareInitialY(y);
    setCompareInitialPan({ x: pan.x, y: pan.y });
  };

  const getTouchDistance = (touches: ArrayLike<{ clientX: number; clientY: number }>) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.max(1, Math.hypot(dx, dy));
  };

  const handleCompareTouchStart = (pane: 'original' | 'reconstruction', e: React.TouchEvent) => {
    if (!compareMode) return;
    if (e.touches.length === 2) {
      e.preventDefault();
      e.stopPropagation();
      const { zoom, pan } = getComparePaneState(pane);
      compareTouchRef.current = {
        pane,
        initialDistance: getTouchDistance(e.touches),
        initialZoom: zoom,
        initialPan: pan,
      };
      setActiveComparePane(pane);
      return;
    }

    handleCompareStart(pane, e);
  };

  const handleCompareTouchMove = (pane: 'original' | 'reconstruction', e: React.TouchEvent) => {
    if (!compareMode) return;

    if (e.touches.length === 2 && compareTouchRef.current?.pane === pane) {
      e.preventDefault();
      e.stopPropagation();
      const nextDistance = getTouchDistance(e.touches);
      const scale = nextDistance / Math.max(1, compareTouchRef.current.initialDistance);
      const nextZoom = Math.min(Math.max(1, compareTouchRef.current.initialZoom * scale), 4);
      setComparePaneState(pane, nextZoom, compareTouchRef.current.initialPan);
      return;
    }

    handleCompareMove(pane, e);
  };

  const handleCompareTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    compareTouchRef.current = null;
    handleCompareEnd();
  };

  const handleCompareMove = (pane: 'original' | 'reconstruction', e: React.MouseEvent | React.TouchEvent) => {
    if (!compareMode || compareDragPane !== pane) return;
    const isTouch = 'touches' in e;
    const x = isTouch ? e.touches[0].clientX : e.clientX;
    const y = isTouch ? e.touches[0].clientY : e.clientY;
    const deltaX = x - compareInitialX;
    const deltaY = y - compareInitialY;
    const { zoom } = getComparePaneState(pane);
    const maxPan = 250 * (zoom - 1);
    const nextPan = {
      x: Math.max(-maxPan, Math.min(maxPan, compareInitialPan.x + deltaX)),
      y: Math.max(-maxPan, Math.min(maxPan, compareInitialPan.y + deltaY)),
    };

    if (compareSyncEnabled) {
      setCompareOriginalPanOffset(nextPan);
      setCompareAtlasPanOffset(nextPan);
      return;
    }

    setComparePaneState(pane, zoom, nextPan, false);
  };

  const handleCompareEnd = () => {
    setCompareDragPane(null);
  };

  const getCompareCursorStyle = (pane: 'original' | 'reconstruction', zoom: number) => {
    if (compareDragPane === pane) return 'cursor-grabbing';
    if (zoom > 1.05) return 'cursor-grab';
    return 'cursor-default';
  };

const handleViewerWheel = (e: React.WheelEvent<HTMLDivElement>) => {
  e.preventDefault();
  e.stopPropagation();

  if (!selectedMedia) return;

  const zoomStep = 0.12;
  const direction = e.deltaY > 0 ? -1 : 1;

  setZoomScale((prev) => {
    const next = Math.min(Math.max(1, prev + direction * zoomStep), 4);

    if (next === 1) {
      setPanOffset({ x: 0, y: 0 });
    }

    return next;
  });
};

  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (!selectedMedia) return;
    const isTouch = 'touches' in e;
    const x = isTouch ? e.touches[0].clientX : e.clientX;
    const y = isTouch ? e.touches[0].clientY : e.clientY;
    setIsDragging(true);
    setInitialX(x);
    setInitialY(y);
    setLastPanPos({ x: panOffset.x, y: panOffset.y });
    if (viewMode === 'video' && videoRef.current && zoomScale <= 1.05) {
      setInitialTime(videoRef.current.currentTime);
      videoRef.current.pause();
      setIsInteractive(true);
    }
  };

  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    const isTouch = 'touches' in e;
    const x = isTouch ? e.touches[0].clientX : e.clientX;
    const y = isTouch ? e.touches[0].clientY : e.clientY;
    if (zoomScale > 1.05) {
      const deltaX = x - initialX;
const deltaY = y - initialY;

const maxPan = 250 * (zoomScale - 1);

const nextX = Math.max(-maxPan, Math.min(maxPan, lastPanPos.x + deltaX));
const nextY = Math.max(-maxPan, Math.min(maxPan, lastPanPos.y + deltaY));

setPanOffset({ x: nextX, y: nextY });
    } else if (viewMode === 'video' && videoRef.current) {
      const deltaX = x - initialX;
      const width = containerRef.current?.offsetWidth || 1;
      const sensitivity = 1.0; 
      const duration = videoRef.current.duration;
      if (!isNaN(duration)) {
        const timeDelta = (deltaX / width) * duration * sensitivity;
        let newTime = initialTime - timeDelta; 
        if (newTime < 0) newTime = duration + (newTime % duration);
        if (newTime > duration) newTime = newTime % duration;
        videoRef.current.currentTime = newTime;
      }
    }
  };

  const handleEnd = () => { setIsDragging(false); };
  const toggleAutoRotate = () => {
    const newState = !isInteractive;
    setIsInteractive(newState);
    if (videoRef.current) { if (!newState) videoRef.current.play(); else videoRef.current.pause(); }
  };
  const handleSeek = (val: number) => {
    if (videoRef.current) {
      const duration = videoRef.current.duration;
      if (!isNaN(duration)) {
        const newTime = (val / 100) * duration;
        videoRef.current.currentTime = newTime;
        setProgress(val);
        setIsInteractive(true);
        videoRef.current.pause();
      }
    }
  };
  const handleStep = (direction: 'left' | 'right') => {
    if (videoRef.current) {
      const duration = videoRef.current.duration;
      const step = 0.05;
      let newTime = videoRef.current.currentTime + (direction === 'right' ? step : -step);
      if (newTime < 0) newTime = duration;
      if (newTime > duration) newTime = 0;
      videoRef.current.currentTime = newTime;
      setIsInteractive(true);
      videoRef.current.pause();
    }
  };
  const onVideoTimeUpdate = () => {
    if (videoRef.current) {
      const duration = videoRef.current.duration;
      const current = videoRef.current.currentTime;
      if (!isNaN(duration) && duration > 0) setProgress((current / duration) * 100);
    }
  };
  const originalReportImage = uploadedImages.find(
    (img) => !!img && !img.startsWith('data:application/pdf')
  ) || null;
  const primaryOriginalImage = inputImage || uploadedImages[0] || null;
  const surgicalPlanOriginalImage = originalReportImage || primaryOriginalImage;
  const surgicalPlanOriginalImageMimeType = getDataUrlMimeType(surgicalPlanOriginalImage);
  const surgicalPlanViewerImage = surgicalPlanOriginalImage || primaryOriginalImage || null;
  const viewerMediaCandidate = viewMode === 'video' ? selectedMedia : activeGeneratedAtlasMedia;
  const isOriginalSelectedMedia = Boolean(primaryOriginalImage && viewerMediaCandidate === primaryOriginalImage);
  const activeViewerRotation = isOriginalSelectedMedia ? originalRotation : atlasRotation;
  const rotateOriginalImage = () => setOriginalRotation((prev) => (prev + 90) % 360);
  const rotateAtlasImage = () => setAtlasRotation((prev) => (prev + 90) % 360);
  const rotateActiveViewerImage = () => {
    if (isOriginalSelectedMedia) {
      rotateOriginalImage();
      return;
    }
    rotateAtlasImage();
  };
  const atlasReportImage = staticImage && !staticImage.startsWith('data:application/pdf')
    ? staticImage
    : null;
  const generatedAtlasMedia = activeGeneratedAtlasMedia || generatedAtlasImage?.url || (generatedAtlasImage?.base64
    ? `data:${generatedAtlasImage.mimeType || "image/png"};base64,${generatedAtlasImage.base64}`
    : null);
  const surgicalPlanImages = uploadedImages.filter(isValidSurgicalPlanAttachment);
  const selectedFilesCount = 0;
  const uploadedFilesCount = surgicalPlanImages.length;
  const imagesCount = surgicalPlanImages.length;
  const hasInputImages = imagesCount > 0 || selectedFilesCount > 0 || uploadedFilesCount > 0;
  const isProcessingSurgicalPlan = isGenerating;
  const isUploading = isUploadingImages;
  const hasPlanPermission = true;
  const disabledReason = isUploading
    ? "uploading"
    : !hasInputImages
      ? "no-images"
      : isProcessingSurgicalPlan
        ? "processing"
        : !hasPlanPermission
          ? "missing-auth-or-plan"
          : null;
  const canProcessSurgicalPlan = hasInputImages && !isProcessingSurgicalPlan && !isUploading && hasPlanPermission;
  const processAnalysisDebug = {
    reason: disabledReason,
    imagesCount,
    selectedFilesCount,
    uploadedFilesCount,
    hasInputImages,
    isProcessingSurgicalPlan,
    isUploading,
    hasPlanPermission,
    canProcessSurgicalPlan,
  };
  const surgicalPlanProcessingSteps =
    language === 'pt'
      ? [
          "Lendo exame enviado",
          "Identificando região anatômica",
          "Avaliando fratura/lesão",
          "Montando plano cirúrgico",
          "Preparando relatório",
        ]
      : [
          "Reading uploaded exam",
          "Identifying anatomical region",
          "Assessing fracture/lesion",
          "Building surgical plan",
          "Preparing report",
        ];
  const defaultViewerMedia = viewMode === 'video' ? selectedMedia : activeGeneratedAtlasMedia;
  const hasViewerInput = uploadedImages.length > 0 || Boolean(defaultViewerMedia);
  const emptyViewerTitle = activeGeneratedAtlasMedia
    ? (language === 'pt' ? 'Pronto para gerar análise' : language === 'es' ? 'Listo para generar análisis' : 'Ready to generate analysis')
    : activeVisualizationMode === '2d'
      ? (language === 'pt' ? 'Nenhuma visualização 2D gerada ainda.' : language === 'es' ? 'Aún no se generó ninguna visualización 2D.' : 'No 2D visualization generated yet.')
      : (language === 'pt' ? 'Nenhuma visualização 3D gerada ainda.' : language === 'es' ? 'Aún no se generó ninguna visualización 3D.' : 'No 3D visualization generated yet.');
  const emptyViewerSubtitle = activeGeneratedAtlasMedia
    ? (language === 'pt' ? "Clique em 'Gerar Atlas' para iniciar a análise." : language === 'es' ? "Haz clic en 'Generar Atlas' para iniciar el análisis." : "Click 'Generate Atlas' to start the analysis.")
    : activeVisualizationMode === '2d'
      ? (language === 'pt' ? 'Gere o Atlas 2D para ver o resultado principal aqui.' : language === 'es' ? 'Genera Atlas 2D para ver el resultado principal aquí.' : 'Generate Atlas 2D to see the main result here.')
      : (language === 'pt' ? 'Gere o Atlas 3D para ver o resultado principal aqui.' : language === 'es' ? 'Genera Atlas 3D para ver el resultado principal aquí.' : 'Generate Atlas 3D to see the main result here.');
  const emptyViewerActionLabel = hasViewerInput
    ? (language === 'pt' ? 'Gerar Atlas' : language === 'es' ? 'Generar Atlas' : 'Generate Atlas')
    : (language === 'pt' ? 'Selecionar exame' : language === 'es' ? 'Seleccionar examen' : 'Select exam');
  useEffect(() => {
    console.info("[SurgicalPlan][process-button:state]", processAnalysisDebug);
    if (disabledReason) {
      console.warn("[SurgicalPlan][process-button:blocked]", processAnalysisDebug);
    }
  }, [imagesCount, selectedFilesCount, uploadedFilesCount, hasInputImages, isProcessingSurgicalPlan, isUploading, hasPlanPermission, canProcessSurgicalPlan, disabledReason]);

  const handleProcessSurgicalPlanOnly = useCallback(async (sourceOverride?: string[], clinicalContextOverride?: string) => {
    console.info("[SurgicalPlan][process-button:click]", processAnalysisDebug);

    if (!canProcessSurgicalPlan) {
      console.warn("[SurgicalPlan][process-button:click-blocked]", processAnalysisDebug);
      return;
    }

    const sourceImages = (sourceOverride || surgicalPlanImages).filter(Boolean);
    const sourceImage = sourceImages.find((image) => image.startsWith("data:image/")) || sourceImages[0];

    if (!sourceImage || !sourceImage.startsWith("data:image/")) {
      setError({
        message: "Imagem não encontrada",
        details: "Envie ao menos 1 imagem válida para análise textual do Plano Cirúrgico.",
      });
      return;
    }

    const uid = auth.currentUser?.uid;
    if (!uid) {
      setError({
        message: t("usageAuthenticationRequired"),
        details: t("usageAuthenticationRequired"),
      });
      return;
    }
    const startedAt = Date.now();

    const clinicalContext = cleanAIText(
      clinicalContextOverride ||
        manualAnatomy ||
        "Plano Cirúrgico"
    );
    console.info("[SurgicalPlan][start]", {
      hasImages: sourceImages.length > 0,
      language,
      clinicalContextLength: clinicalContext.length,
      manualAnatomyLength: manualAnatomy.length,
    });

    setCurrentItem(language === "pt" ? "Plano Cirúrgico" : language === "es" ? "Plan Quirúrgico" : "Surgical Plan");
    setInputImage(sourceImage);
    setError(null);
    setScientificEvidenceText("");
    setScientificEvidenceReport(null);
    setZoomScale(1);
    setPanOffset({ x: 0, y: 0 });
    setDetectedAnatomyRegion("");
    setDetectedAnatomicalAnchors("");
    setLesionMarker(null);
    setLesionBox(null);
    setLesionConfidence(null);
    setStaticImage(null);
    setVideo360(null);
    setPatientOrientations(null);
    setSurgicalPlan(null);
    setSurgicalPlanV2(null);
    setAutoMeasurements(null);
    setGeneratedAtlasImage(null);
    setGenerated2DImageUrl(null);
    setGenerated2DImageBase64(null);
    setGenerated3DImageUrl(null);
    setGenerated3DImageBase64(null);

    resetAtlasProgress();
    setIsGenerating(true);
    updateAtlasProgress(5, surgicalPlanProcessingSteps[0], "main-2d");

    try {
      const aistudio = getAiStudio();
      if (aistudio && typeof aistudio.hasSelectedApiKey === "function") {
        if (!(await aistudio.hasSelectedApiKey())) {
          await aistudio.openSelectKey();
        }
      }

      updateAtlasProgress(12, surgicalPlanProcessingSteps[0], "main-2d");

      const originalSizes = [estimateDataUrlBytes(sourceImage)];
      const compressed = await compressImageForSurgicalPlan(sourceImage, {
        maxDimension: 1024,
        quality: 0.7,
        targetBytes: 95_000,
      });
      const compressedBase64 = String(compressed.dataUrl || "").replace(/^data:[^;]+;base64,/, "");
      const compressedSizes = [compressed.sizeBytes];
      const totalPayloadBytes = compressedBase64.length;

      console.log("[SurgicalPlan][payload:size]", {
        imagesCount: 1,
        originalSizes,
        compressedSizes,
        totalPayloadBytes,
      });

      if (compressedBase64.length > 120000) {
        throw new Error("Imagem ainda grande após compressão");
      }

      updateAtlasProgress(28, surgicalPlanProcessingSteps[1], "main-2d");

      const visualDiagnosticCheck = await generateVisualDiagnosticCheck(
        [clinicalContext, "Plano Cirúrgico por imagem"].filter(Boolean).join("\n"),
        language,
        [compressed.dataUrl]
      );
      console.log("[SurgicalPlan][visualDiagnosticCheck]", visualDiagnosticCheck);

      updateAtlasProgress(48, surgicalPlanProcessingSteps[2], "main-2d");

      const payload = {
        clinicalContext,
        language: "pt" as const,
        fastMode: true,
        visualDiagnosticCheck,
        image: {
          mimeType: compressed.mimeType,
          base64: compressedBase64,
        },
      };

      console.log("[SurgicalPlan][final-payload]", {
        hasImage: Boolean(payload.image?.base64),
        base64Length: payload.image?.base64?.length,
        imagesSent: 1,
        fastMode: payload.fastMode,
        includesUploadedImagesArray: Boolean((payload as any).uploadedImages),
        includesSelectedImagesArray: Boolean((payload as any).selectedImages),
        mode: "textual",
        analysisMode: "fast",
      });

      console.info("[SurgicalPlan][main_call:start]", {
        clinicalContextLength: clinicalContext.length,
        hasImage: Boolean(payload.image?.base64),
        base64Length: payload.image?.base64?.length,
        language: payload.language,
      });

      const surgicalPlanV2Result = await generateSurgicalPlanV2(payload);
      console.log("[SurgicalPlanV2][final_plan]", {
        diagnosis: surgicalPlanV2Result.diagnosis,
        anatomicalLocation: surgicalPlanV2Result.anatomicalLocation,
        aoOtaClassification: surgicalPlanV2Result.aoOtaClassification,
        managementType: surgicalPlanV2Result.managementDecision.type,
        techniques: surgicalPlanV2Result.surgicalTechniques.length,
      });

      updateAtlasProgress(72, surgicalPlanProcessingSteps[3], "main-2d");
      setSurgicalPlan(null);
      setSurgicalPlanV2(surgicalPlanV2Result);
      setPlanRecommendation(
        surgicalPlanV2Result.managementDecision.type === "surgical"
          ? "surgical"
          : surgicalPlanV2Result.managementDecision.type === "conservative"
            ? "conservative"
            : "uncertain"
      );
      setSelectedStrategy(
        surgicalPlanV2Result.managementDecision.type === "surgical"
          ? "surgical"
          : surgicalPlanV2Result.managementDecision.type === "conservative"
            ? "conservative"
            : null
      );
      setDetectedAnatomyRegion(surgicalPlanV2Result.anatomicalLocation);
      setDetectedAnatomicalAnchors(
        normalizeAtlasDisplayedText(
          [
            visualDiagnosticCheck.bone,
            visualDiagnosticCheck.joint,
            ...(visualDiagnosticCheck.views || []),
          ].filter(Boolean).join(" | ")
        )
      );
      setViewMode("image");
      setActiveTab("plano");
      updateAtlasProgress(90, surgicalPlanProcessingSteps[4], "main-2d");
      await finishAtlasProgress();
      console.info("[ATLAS][frontend][generation:done]", {
        item: "Plano Cirúrgico",
        mode: "textual",
        analysisMode: "v2",
        durationMs: Date.now() - startedAt,
      });
      return;
    } catch (err: any) {
      console.error("[SurgicalPlan][main_call:error]", {
        errorCode: err?.code || err?.name || "unknown",
        message: String(err?.message || err || ""),
      });
      resetAtlasProgress();
      console.error("[ATLAS][frontend][generation:error]", {
        item: "Plano Cirúrgico",
        mode: "textual",
        analysisMode: "fast",
        rawError: err,
      });

      if (err instanceof UsageLimitError || err?.message === t("usageAuthenticationRequired") || err?.debugCode === "ATLAS_RATE_LIMIT") {
        console.warn("[SurgicalPlan][plan-permission:blocked]", {
          reason: err instanceof UsageLimitError || err?.debugCode === "ATLAS_RATE_LIMIT" ? "usage-limit" : "authentication-required",
          details: err?.message || getUsageErrorMessage(err),
        });
        setError({ message: t("usageLimitTitle"), details: err?.message || getUsageErrorMessage(err) });
      } else {
        const message = String(err?.message || "");
        if (message === "Imagem ainda grande após compressão") {
          setError({
            message: "Não foi possível gerar o plano. A imagem enviada ainda está pesada para análise textual.",
            details: "A imagem enviada ainda está pesada para análise textual.",
          });
        } else {
          handleApiError(err);
        }
      }
    } finally {
      setIsGenerating(false);
      setGenStep("");
    }
  }, [
    canProcessSurgicalPlan,
    currentItem,
    detectedAnatomyRegion,
    finishAtlasProgress,
    language,
    manualAnatomy,
    processAnalysisDebug,
    resetAtlasProgress,
    surgicalPlanImages,
    surgicalPlanProcessingSteps,
    t,
    updateAtlasProgress,
  ]);

  const handleProcessSurgicalPlanAnalysis = useCallback(() => {
    void handleProcessSurgicalPlanOnly();
  }, [handleProcessSurgicalPlanOnly]);

  const surgicalPlanProcessingStepIndex = Math.min(
    surgicalPlanProcessingSteps.length - 1,
    Math.max(0, Math.floor((atlasGeneration.progress || 0) / 20))
  );

  const getCursorStyle = () => {
    if (isDragging) return 'cursor-grabbing';
    if (zoomScale > 1.05) return 'cursor-grab';
    if (viewMode === 'video' && zoomScale <= 1.05) return 'cursor-ew-resize';
    return 'cursor-default';
  };

  const getAtlasImageStyle = (brightnessOverride?: number, zoomOverride?: number): React.CSSProperties => {
    const zoomActive = (zoomOverride ?? zoomScale) > 1.05;
    const effectiveBrightness = brightnessOverride ?? brightness;
    return {
      maxWidth: zoomActive ? '112%' : '55%',
      maxHeight: zoomActive ? '88%' : '70%',
      filter: `brightness(${effectiveBrightness}%) drop-shadow(0 10px 30px rgba(0,0,0,0.4))`,
    };
  };

  const finalPlanForRender = surgicalPlan || emptySurgicalPlanState;
  const planningDiagnosis = normalizeAtlasDisplayedText(
    finalPlanForRender.diagnostico ||
    finalPlanForRender.mainDiagnosis ||
    (finalPlanForRender as any)?.diagnosis ||
    (finalPlanForRender as any)?.primaryDiagnosis ||
    ""
  );
  const planningLocation = normalizeAtlasDisplayedText(
    finalPlanForRender.anatomicalLocation ||
    (finalPlanForRender as any)?.location ||
    (finalPlanForRender as any)?.localizacaoAnatomica ||
    (finalPlanForRender as any)?.localization ||
    ""
  );
  const planningSeverity = normalizeAtlasDisplayedText(
    finalPlanForRender?.severity || conductBadge?.label || scientificEvidenceText || emptySectionLabel
  );
  const planningIndication = normalizeAtlasDisplayedText(
    finalPlanForRender?.conduta || scientificEvidenceText || emptySectionLabel
  );
  const planningSeedText = [
    planningDiagnosis,
    planningLocation,
    finalPlanForRender.tecnica || "",
    finalPlanForRender.planoB || "",
    scientificEvidenceText || "",
  ].join(" | ");
  const spinePlanningMode = isSpinePlanningCase(planningSeedText);
  const fixedDifferentials = getFixedDifferentials(language, planningSeedText, planningLocation);
  const shouldHideViewerInfoCard = compareMode || isFullscreen;
  const viewerTools = [
    { key: 'rotate', label: 'GIRAR', icon: RotateCw, onClick: handleViewerRotate },
    { key: 'move', label: 'MOVER', icon: Move, onClick: () => {} },
    { key: 'zoom', label: 'ZOOM', icon: ZoomIn, onClick: handleViewerZoomIn },
    { key: 'reset', label: 'RESETAR', icon: RotateCcw, onClick: handleViewerReset },
    { key: 'light', label: 'LUZ', icon: Lightbulb, onClick: handleViewerLight },
    { key: 'capture', label: 'FOTO', icon: Camera, onClick: handleViewerCapture },
  ] as const;
  const complementaryExams = spinePlanningMode
    ? (
        language === 'pt'
          ? ['RM lombar', 'Radiografias dinâmicas em flexão/extensão', 'TC se dúvida óssea/óssea cortical', 'Correlação neurológica']
          : language === 'es'
            ? ['RM lumbar', 'Radiografías dinámicas en flexión/extensión', 'TC si hay duda ósea/cortical', 'Correlación neurológica']
            : ['Lumbar MRI', 'Dynamic flexion/extension radiographs', 'CT if osseous/cortical uncertainty', 'Neurological correlation']
      )
    : (
        language === 'pt'
          ? ['Exame complementar direcionado conforme o segmento', 'Imagem avançada se houver dúvida anatômica', 'Correlação clínica e funcional']
          : language === 'es'
            ? ['Examen complementario dirigido según el segmento', 'Imagen avanzada si existe duda anatómica', 'Correlación clínica y funcional']
            : ['Targeted additional exam according to the segment', 'Advanced imaging if anatomy remains uncertain', 'Clinical and functional correlation']
      );
  const preoperativePlanning = spinePlanningMode
    ? (
        language === 'pt'
          ? ['Confirmar nível exato na RM/TC', 'Mapear raiz neural possivelmente comprimida', 'Correlacionar com dor irradiada, força e reflexos', 'Definir falha do tratamento conservador']
          : language === 'es'
            ? ['Confirmar nivel exacto en RM/TC', 'Mapear la raíz neural posiblemente comprimida', 'Correlacionar con dolor irradiado, fuerza y reflejos', 'Definir fracaso del tratamiento conservador']
            : ['Confirm exact level on MRI/CT', 'Map the possibly compressed neural root', 'Correlate with radicular pain, strength, and reflexes', 'Define failure of conservative management']
      )
    : (
        language === 'pt'
          ? ['Confirmar diagnóstico por imagem e exame físico', 'Definir lateralidade e segmento acometido', 'Checar comorbidades e risco anestésico']
          : language === 'es'
            ? ['Confirmar diagnóstico por imagen y examen físico', 'Definir lateralidad y segmento afectado', 'Revisar comorbilidades y riesgo anestésico']
            : ['Confirm imaging diagnosis and physical exam', 'Define laterality and affected segment', 'Review comorbidities and anesthetic risk']
      );
  const postopCare = spinePlanningMode
    ? (
        language === 'pt'
          ? ['Analgesia e mobilização precoce conforme tolerância', 'Orientar sinais neurológicos novos', 'Reavaliação clínica e funcional', 'Reabilitação com fisioterapia conforme evolução']
          : language === 'es'
            ? ['Analgesia y movilización precoz según tolerancia', 'Orientar signos neurológicos nuevos', 'Reevaluación clínica y funcional', 'Rehabilitación con fisioterapia según evolución']
            : ['Analgesia and early mobilization as tolerated', 'Educate about new neurological signs', 'Clinical and functional reassessment', 'Rehabilitation with physical therapy as evolution allows']
      )
    : (
        language === 'pt'
          ? ['Analgesia e cuidado do sítio cirúrgico', 'Reabilitação conforme segmento', 'Controle de dor e função', 'Revisão clínica programada']
          : language === 'es'
            ? ['Analgesia y cuidado del sitio quirúrgico', 'Rehabilitación según el segmento', 'Control del dolor y la función', 'Revisión clínica programada']
            : ['Analgesia and wound care', 'Segment-specific rehabilitation', 'Pain and function control', 'Scheduled clinical review']
      );
  const redFlags = spinePlanningMode
    ? (
        language === 'pt'
          ? ['Piora progressiva de dor irradiada', 'Fraqueza nova ou piora motora', 'Alteração esfincteriana', 'Dormência em sela', 'Febre ou suspeita infecciosa']
          : language === 'es'
            ? ['Empeoramiento progresivo del dolor irradiado', 'Debilidad nueva o empeoramiento motor', 'Alteración esfinteriana', 'Anestesia en silla de montar', 'Fiebre o sospecha infecciosa']
            : ['Progressive radiating pain worsening', 'New or worsening motor weakness', 'Bowel/bladder changes', 'Saddle anesthesia', 'Fever or infection concern']
      )
    : (
        language === 'pt'
          ? ['Dor desproporcional ou progressiva', 'Déficit neurovascular', 'Febre', 'Inchaço ou sangramento importante']
          : language === 'es'
            ? ['Dolor desproporcionado o progresivo', 'Déficit neurovascular', 'Fiebre', 'Aumento de volumen o sangrado importante']
            : ['Disproportionate or progressive pain', 'Neurovascular deficit', 'Fever', 'Marked swelling or bleeding']
      );
  const planningReferences = getScientificEvidenceCards(scientificEvidenceReport, language).map((item) => item.label);
  const reportEvidenceItems = getTopEvidenceForReport(scientificEvidenceReport?.scientificEvidence || []);
  const reportDiagnosisText = normalizeAtlasDisplayedText(
    cleanPlanningPlaceholder(surgicalPlan?.diagnostico) ||
    cleanPlanningPlaceholder(planningDiagnosis) ||
    (language === "pt" ? "Achado musculoesquelético em avaliação" : "Musculoskeletal finding under evaluation")
  );
  const reportAnatomicalLocation = normalizeAtlasDisplayedText(
    cleanPlanningPlaceholder(surgicalPlan?.anatomicalLocation) ||
    cleanPlanningPlaceholder(planningLocation) ||
    (language === "pt" ? "Localização anatômica não especificada" : "Anatomical location not specified")
  );
  const reportDiagnosisPattern = normalizeAtlasDisplayedText(
    cleanPlanningPlaceholder(surgicalPlan?.classificationObject?.description) ||
    cleanPlanningPlaceholder(surgicalPlan?.classificationDescription) ||
    ""
  );
  const planningNoPlanMessage =
    atlasLabel(language, 'strategyChoiceSubtitle');
  const preferPlanValue = (value: unknown, fallback: string) => {
    const normalizedValue = normalizeAtlasDisplayedText(cleanPlanningPlaceholder(String(value || "")));
    return normalizedValue || fallback;
  };
  const activeStrategy = selectedStrategy ?? (planRecommendation === "surgical" ? "surgical" : planRecommendation === "conservative" ? "conservative" : null);
  const activeConservativePlan = surgicalPlan?.conservativePlan || emptySurgicalPlanState.conservativePlan!;
  const activeSurgicalOption = surgicalPlan?.surgicalOption || emptySurgicalPlanState.surgicalOption!;
  const selectedPlanLabel =
    activeStrategy === "surgical"
      ? (language === "pt" ? "Técnica cirúrgica" : language === "es" ? "Técnica quirúrgica" : "Surgical technique")
      : activeStrategy === "conservative"
        ? (language === "pt" ? "Tratamento conservador" : language === "es" ? "Tratamiento conservador" : "Conservative treatment")
        : (language === "pt" ? "Avaliação médica necessária" : language === "es" ? "Evaluación médica necesaria" : "Medical evaluation required");
  const finalPlanDiagnosticCheck = {
    bone: cleanPlanningPlaceholder(planningLocation),
    joint: "",
    anatomicalRegion: cleanPlanningPlaceholder(planningLocation),
    specificLocation: cleanPlanningPlaceholder(planningLocation),
    suspectedDiagnosis: cleanPlanningPlaceholder(planningDiagnosis),
    visualEvidence: [],
    confidence: 0,
    uncertaintyReason: null,
  };
  const sourceText = [
    finalPlanForRender.diagnostico || planningDiagnosis,
    finalPlanForRender.anatomicalLocation || planningLocation,
    finalPlanForRender.classificationLabel || finalPlanForRender.classificationText || finalPlanForRender.classificationCode,
    finalPlanForRender.tecnica,
    ...(Array.isArray(finalPlanForRender.surgicalTechniques) ? finalPlanForRender.surgicalTechniques.map((item) => item?.title || "") : []),
  ].filter(Boolean).join(" ");
  const rawClassificationCode = cleanPlanningPlaceholder(finalPlanForRender.classificationCode || finalPlanForRender.classificationText || finalPlanForRender.classificationObject?.code || finalPlanForRender.classificationLabel || "");
  const inferredClassification = inferAoOtaClassificationFromDiagnosis({
    classificationObject: finalPlanForRender.classificationObject,
    classificationCode: finalPlanForRender.classificationCode || finalPlanForRender.classificationText || finalPlanForRender.classification,
    classificationLabel: finalPlanForRender.classificationLabel || finalPlanForRender.classificationText || finalPlanForRender.classificationCode,
    classificationDescription: finalPlanForRender.classificationDescription,
    classificationSeverity: finalPlanForRender.classificationSeverity,
    classification: finalPlanForRender.classification,
    diagnosis: finalPlanForRender.diagnostico || finalPlanForRender.mainDiagnosis || (finalPlanForRender as any)?.diagnosis || planningDiagnosis,
    mainDiagnosis: finalPlanForRender.mainDiagnosis || finalPlanForRender.diagnostico || (finalPlanForRender as any)?.diagnosis || planningDiagnosis,
    anatomicalLocation: finalPlanForRender.anatomicalLocation || (finalPlanForRender as any)?.location || (finalPlanForRender as any)?.localizacaoAnatomica || planningLocation,
    bone: finalPlanDiagnosticCheck?.bone,
    lesionClassification: finalPlanForRender.tecnica || finalPlanForRender.classificationText || "",
    visualDiagnosticCheck: finalPlanDiagnosticCheck,
    surgicalTechniqueTitles: Array.isArray(finalPlanForRender.surgicalTechniques) ? finalPlanForRender.surgicalTechniques.map((item) => item?.title || "").filter(Boolean) : [],
  });
  const planningClassification = isValidAoOtaCode(rawClassificationCode)
    ? { ...inferredClassification, code: normalizeAootaDisplayCode(rawClassificationCode || inferredClassification.code) }
    : inferredClassification;
  console.log('[AOOTA][render]', {
    rawCode: rawClassificationCode,
    inferredCode: inferredClassification.code,
    finalCode: planningClassification.code,
    sourceText,
  });
  const planDiagnosisSource = cleanPlanningPlaceholder(
    finalPlanForRender.diagnostico ||
    (finalPlanForRender as any)?.diagnosis ||
    (finalPlanForRender as any)?.mainDiagnosis ||
    ""
  );
  const planLocationSource = cleanPlanningPlaceholder(
    finalPlanForRender.anatomicalLocation ||
    (finalPlanForRender as any)?.location ||
    (finalPlanForRender as any)?.localizacaoAnatomica ||
    ""
  );
  const planningData = {
    diagnosis: normalizeAtlasDisplayedText(planDiagnosisSource),
    mainDiagnosis: normalizeAtlasDisplayedText(
      cleanPlanningPlaceholder(
        (finalPlanForRender as any)?.mainDiagnosis ||
        (finalPlanForRender as any)?.diagnosis ||
        planDiagnosisSource ||
        ""
      )
    ),
    anatomicalLocation: normalizeAtlasDisplayedText(
      planLocationSource ||
      (finalPlanForRender?.level ? `${cleanPlanningPlaceholder(planningLocation) || "Localização anatômica"} • ${finalPlanForRender.level}` : cleanPlanningPlaceholder(planningLocation))
    ),
    classification: planningClassification,
    classificationText: planningClassification.code,
    classificationCode: planningClassification.code,
    classificationLabel: planningClassification.code.replace(/^AO\/OTA\s*/i, "").trim(),
    classificationDescription: planningClassification.description,
    classificationSeverity: (planningClassification.severity.toLowerCase() as "low" | "medium" | "high"),
    severity: normalizeAtlasDisplayedText(
      cleanPlanningPlaceholder(finalPlanForRender?.severity) ||
      (spinePlanningMode
        ? (language === 'pt' ? 'Classificação estrutural da coluna' : language === 'es' ? 'Clasificación estructural de la columna' : 'Structural spine classification')
        : (language === 'pt' ? 'Classificação clínica do caso' : language === 'es' ? 'Clasificación clínica del caso' : 'Clinical case classification'))
    ),
    recommendation: planRecommendation,
    selectedStrategy: activeStrategy,
    differentialDiagnoses: finalPlanForRender?.differentialDiagnoses?.length
      ? finalPlanForRender.differentialDiagnoses
      : fixedDifferentials,
    complementaryExams: finalPlanForRender?.complementaryExams?.length ? finalPlanForRender.complementaryExams : complementaryExams,
    preoperativePlanning: finalPlanForRender?.preoperativePlanning?.length ? finalPlanForRender.preoperativePlanning : preoperativePlanning,
    conservativePlan: {
      indication: preferPlanValue(finalPlanForRender.conservativePlan?.indication || activeConservativePlan.indication, planningNoPlanMessage),
      protocol: finalPlanForRender.conservativePlan?.protocol || activeConservativePlan.protocol || [],
      followUp: finalPlanForRender.conservativePlan?.followUp || activeConservativePlan.followUp || [],
      redFlags: finalPlanForRender.conservativePlan?.redFlags || activeConservativePlan.redFlags || [],
      conversionCriteria: finalPlanForRender.conservativePlan?.conversionCriteria || activeConservativePlan.conversionCriteria || [],
    },
    surgicalOption: {
      indicationCriteria: finalPlanForRender.surgicalOption?.indicationCriteria || activeSurgicalOption.indicationCriteria || [],
      preferredTechnique: preferPlanValue(finalPlanForRender.surgicalOption?.preferredTechnique || activeSurgicalOption.preferredTechnique, planningNoPlanMessage),
      patientPositioning: finalPlanForRender.surgicalOption?.patientPositioning || activeSurgicalOption.patientPositioning || [],
      surgicalApproach: preferPlanValue(finalPlanForRender.surgicalOption?.surgicalApproach || activeSurgicalOption.surgicalApproach, planningNoPlanMessage),
      reductionStrategy: finalPlanForRender.surgicalOption?.reductionStrategy || activeSurgicalOption.reductionStrategy || [],
      fixationStrategy: finalPlanForRender.surgicalOption?.fixationStrategy || activeSurgicalOption.fixationStrategy || [],
      stepByStepTechnique: finalPlanForRender.surgicalOption?.stepByStepTechnique || activeSurgicalOption.stepByStepTechnique || [],
      implantsAndMaterials: finalPlanForRender.surgicalOption?.implantsAndMaterials || activeSurgicalOption.implantsAndMaterials || [],
      fluoroscopyOrImagingControl: finalPlanForRender.surgicalOption?.fluoroscopyOrImagingControl || activeSurgicalOption.fluoroscopyOrImagingControl || [],
      intraoperativeChecks: finalPlanForRender.surgicalOption?.intraoperativeChecks || activeSurgicalOption.intraoperativeChecks || [],
      closureAndImmobilization: finalPlanForRender.surgicalOption?.closureAndImmobilization || activeSurgicalOption.closureAndImmobilization || [],
      postoperativeProtocol: finalPlanForRender.surgicalOption?.postoperativeProtocol || activeSurgicalOption.postoperativeProtocol || [],
      risksAndPitfalls: finalPlanForRender.surgicalOption?.risksAndPitfalls || activeSurgicalOption.risksAndPitfalls || [],
      planB: finalPlanForRender.surgicalOption?.planB || activeSurgicalOption.planB || [],
    },
    surgicalTechniques: finalPlanForRender?.surgicalTechniques || (finalPlanForRender as any)?.techniques || (finalPlanForRender as any)?.surgicalOptions || [],
    techniqueComparison: preferPlanValue(finalPlanForRender?.techniqueComparison, planningNoPlanMessage),
    selectionCriteria: preferPlanValue(finalPlanForRender?.selectionCriteria, planningNoPlanMessage),
    clinicalInterpretation: preferPlanValue(finalPlanForRender?.dicas, preferPlanValue(planningIndication, planningNoPlanMessage)),
    treatmentPlan: preferPlanValue(finalPlanForRender?.tecnica, planningNoPlanMessage),
    surgicalIndicationReason: preferPlanValue(finalPlanForRender?.dicas, preferPlanValue(selectedPlanLabel, planningNoPlanMessage)),
    mostCurrentTechniqueTitle: finalPlanForRender?.mostCurrentTechniqueTitle || null,
    aiRecommendedTechniqueTitle: finalPlanForRender?.aiRecommendedTechniqueTitle || null,
    aiRecommendationRationale: normalizeAtlasDisplayedText(finalPlanForRender?.aiRecommendationRationale || ""),
    alternativeTechniqueRationale: normalizeAtlasDisplayedText(finalPlanForRender?.alternativeTechniqueRationale || ""),
    materials: dedupeList([
      ...(finalPlanForRender?.materials || []),
      ...(finalPlanForRender?.materiais ? [finalPlanForRender.materiais] : []),
      ...(finalPlanForRender?.surgicalTechniques || []).flatMap((technique) => technique.materials || []),
    ].map((item) => cleanAIText(item)).filter(Boolean)),
    recommendations: finalPlanForRender?.recommendations?.length ? finalPlanForRender.recommendations : [],
    scientificReferences: finalPlanForRender?.scientificReferences?.length ? finalPlanForRender.scientificReferences : (planningReferences.length > 0 ? planningReferences : toBulletList(scientificEvidenceText || emptySectionLabel, language)),
    redFlags: finalPlanForRender?.redFlags?.length ? finalPlanForRender.redFlags : redFlags,
  };
  console.log("[SurgicalPlan][FINAL_SINGLE_SOURCE]", {
    diagnosis: finalPlanForRender?.diagnostico,
    mainDiagnosis: finalPlanForRender?.mainDiagnosis,
    location: finalPlanForRender?.anatomicalLocation,
    techniques: Array.isArray(finalPlanForRender?.surgicalTechniques) ? finalPlanForRender.surgicalTechniques.length : 0,
    postoperativeProtocol: finalPlanForRender?.surgicalOption?.postoperativeProtocol?.length || 0,
    postoperativeCare: finalPlanForRender?.postoperativeCare?.length || 0,
    rehabilitation: finalPlanForRender?.postoperativeCare?.length || 0,
    planB: Array.isArray(finalPlanForRender?.planB) ? finalPlanForRender.planB.length : 0,
    contingencyPlan: Array.isArray(finalPlanForRender?.contingencyPlan) ? finalPlanForRender.contingencyPlan.length : 0,
    differentialDiagnosis: Array.isArray(finalPlanForRender?.differentialDiagnosis) ? finalPlanForRender.differentialDiagnosis.length : 0,
    associatedInjuries: Array.isArray(finalPlanForRender?.associatedInjuries) ? finalPlanForRender.associatedInjuries.length : 0,
    scientificEvidence: Array.isArray(finalPlanForRender?.scientificEvidence) ? finalPlanForRender.scientificEvidence.length : 0,
    evidence: Array.isArray(finalPlanForRender?.evidence) ? finalPlanForRender.evidence.length : 0,
    technique1StepByStepTechnique: finalPlanForRender?.surgicalTechniques?.[0]?.stepByStepTechnique?.length || 0,
    technique1Materials: finalPlanForRender?.surgicalTechniques?.[0]?.materials?.length || 0,
    technique1Complications: finalPlanForRender?.surgicalTechniques?.[0]?.complications?.length || 0,
    technique1RecoveryTime: finalPlanForRender?.surgicalTechniques?.[0]?.recoveryTime || "",
    technique2StepByStepTechnique: finalPlanForRender?.surgicalTechniques?.[1]?.stepByStepTechnique?.length || 0,
    technique2Materials: finalPlanForRender?.surgicalTechniques?.[1]?.materials?.length || 0,
  });
  const normalizePlanSignal = (value: unknown) => normalizeAtlasDisplayedText(cleanPlanningPlaceholder(String(value || ""))).toLowerCase();
  const genericSignals = [
    "achado musculoesquelético em avaliação",
    "musculoskeletal finding under evaluation",
    "conteúdo não gerado",
    "content not generated",
    "localização anatômica a confirmar",
    "anatomical location to confirm",
    "não definido",
    "not defined",
    "a confirmar",
    "to confirm",
  ];
  const isGenericSignal = (value: unknown) => {
    const normalized = normalizePlanSignal(value);
    return !normalized || genericSignals.some((signal) => normalized.includes(signal));
  };
  const hasSpecificDiagnosis = !isGenericSignal(
    finalPlanForRender?.diagnostico ||
    finalPlanForRender?.mainDiagnosis ||
    (finalPlanForRender as any)?.diagnosis ||
    (finalPlanForRender as any)?.primaryDiagnosis ||
    ""
  );
  const hasSpecificTechnique = dedupeList([
    ...(finalPlanForRender?.surgicalTechniques || []).map((item) => item?.title || ""),
    finalPlanForRender?.tecnica || "",
    finalPlanForRender?.surgicalOption?.preferredTechnique || "",
  ].map((item) => cleanAIText(item)).filter(Boolean)).some((item) => !isGenericSignal(item));
  const hasConservativeContent = ((finalPlanForRender?.conservativePlan?.protocol || [])).filter((item) => !isGenericSignal(item)).length >= 1;
  const hasRenderablePlanContent = hasSpecificDiagnosis || hasSpecificTechnique || hasConservativeContent;
  const hasMeaningfulPlanData = Boolean(
    finalPlanForRender &&
      (
        cleanPlanningPlaceholder(finalPlanForRender.diagnostico) ||
        cleanPlanningPlaceholder(finalPlanForRender.mainDiagnosis) ||
        cleanPlanningPlaceholder((finalPlanForRender as any)?.diagnosis) ||
        cleanPlanningPlaceholder((finalPlanForRender as any)?.primaryDiagnosis) ||
        cleanPlanningPlaceholder(finalPlanForRender.tecnica) ||
        cleanPlanningPlaceholder(finalPlanForRender.conduta) ||
        cleanPlanningPlaceholder(finalPlanForRender.planoB) ||
        cleanPlanningPlaceholder(finalPlanForRender.materiais) ||
        (finalPlanForRender.surgicalTechniques?.length || 0) > 0 ||
        cleanPlanningPlaceholder(finalPlanForRender.surgicalOption?.preferredTechnique || "") ||
        (finalPlanForRender.conservativePlan?.protocol?.length || 0) > 0
      )
  );
  const surgicalPlanRenderBlocked = false;
  console.log("[SurgicalPlan][validation_input]", {
    diagnosis: finalPlanForRender?.diagnostico || finalPlanForRender?.mainDiagnosis || (finalPlanForRender as any)?.diagnosis || "",
    mainDiagnosis: finalPlanForRender?.mainDiagnosis || finalPlanForRender?.diagnostico || (finalPlanForRender as any)?.diagnosis || "",
    anatomicalLocation: finalPlanForRender?.anatomicalLocation || (finalPlanForRender as any)?.location || (finalPlanForRender as any)?.localizacaoAnatomica || "",
    materialsCount: planningData.materials.length,
    stepsCount: finalPlanForRender?.surgicalOption?.stepByStepTechnique?.length || 0,
    conservativeCount: finalPlanForRender?.conservativePlan?.protocol?.length || 0,
  });
  console.log("[SurgicalPlan][parsed_json]", {
    diagnosis: finalPlanForRender?.diagnostico || finalPlanForRender?.mainDiagnosis || (finalPlanForRender as any)?.diagnosis || "",
    mainDiagnosis: finalPlanForRender?.mainDiagnosis || finalPlanForRender?.diagnostico || (finalPlanForRender as any)?.diagnosis || "",
    anatomicalLocation: finalPlanForRender?.anatomicalLocation || (finalPlanForRender as any)?.location || (finalPlanForRender as any)?.localizacaoAnatomica || "",
    hasSpecificDiagnosis,
    hasSpecificTechnique,
    hasConservativeContent,
    hasRenderablePlanContent,
    hasMeaningfulPlanData,
  });
  if (!hasRenderablePlanContent && hasMeaningfulPlanData) {
    console.warn("[SurgicalPlan][validation_failed_reason]", {
      reason: !hasMeaningfulPlanData
        ? "no_meaningful_plan_data"
        : "no_renderable_plan_content",
    });
  }
  console.log("[SurgicalPlan][plan_has_diagnosis]", hasSpecificDiagnosis);
  console.log("[SurgicalPlan][plan_has_technique]", hasSpecificTechnique);
  console.log("[SurgicalPlan][plan_has_conservative]", hasConservativeContent);
  console.log("[SurgicalPlan][render_allowed]", !surgicalPlanRenderBlocked);
  if (!hasRenderablePlanContent && hasMeaningfulPlanData) {
      console.warn("[SurgicalPlan][normalizer_rejected_generic]", {
      diagnosis: planningData.diagnosis,
      anatomicalLocation: planningData.anatomicalLocation,
    });
    console.warn("[SurgicalPlan][fallback_blocked]", {
      diagnosis: planningData.diagnosis,
      anatomicalLocation: planningData.anatomicalLocation,
      reason: "generic_or_insufficient_surgical_plan",
    });
  } else {
    console.info("[SurgicalPlan][final_plan_valid]", {
      diagnosis: finalPlanForRender?.diagnostico || finalPlanForRender?.mainDiagnosis || (finalPlanForRender as any)?.diagnosis || "",
      anatomicalLocation: finalPlanForRender?.anatomicalLocation || (finalPlanForRender as any)?.location || (finalPlanForRender as any)?.localizacaoAnatomica || "",
    });
  }
  const selectedPlanBullets = activeStrategy === "surgical"
    ? (activeSurgicalOption.stepByStepTechnique || [])
    : activeStrategy === "conservative"
      ? (activeConservativePlan.protocol || [])
      : [];
  const selectedPlanSecondaryBullets = activeStrategy === "surgical"
    ? (activeSurgicalOption.postoperativeProtocol || [])
    : activeStrategy === "conservative"
      ? (activeConservativePlan.followUp || [])
      : [];
  const selectedPlanRiskBullets = activeStrategy === "surgical"
    ? (activeSurgicalOption.risksAndPitfalls || [])
    : activeStrategy === "conservative"
      ? (activeConservativePlan.redFlags || [])
      : [];

  return (
    <div className={`space-y-6 flex flex-col ${isFullscreen ? 'fixed inset-0 z-[100] bg-slate-950 overflow-hidden px-6 pt-6' : 'h-full'}`}>
      
      {/* Report Print Modal */}
      {showReport && (
        <div id="print-area" className="fixed inset-0 z-[200] bg-white text-black overflow-y-auto surgical-report-preview">
            <style>{`
              .surgical-report-preview,
              .surgical-report-preview * {
                color: #0f172a !important;
              }

              .surgical-report-preview {
                background: #ffffff !important;
              }

              .surgical-report-preview h1,
              .surgical-report-preview h2,
              .surgical-report-preview h3,
              .surgical-report-preview h4,
              .surgical-report-preview strong {
                color: #020617 !important;
              }

              .surgical-report-preview p,
              .surgical-report-preview li,
              .surgical-report-preview span,
              .surgical-report-preview div {
                color: #0f172a !important;
              }

              .surgical-report-preview li::marker {
                color: #0891b2 !important;
              }

              .surgical-report-preview .text-white,
              .surgical-report-preview .text-slate-50,
              .surgical-report-preview .text-slate-100,
              .surgical-report-preview .text-slate-200,
              .surgical-report-preview .text-slate-300,
              .surgical-report-preview .text-slate-400,
              .surgical-report-preview .text-white\/70,
              .surgical-report-preview .text-cyan-50 {
                color: #0f172a !important;
              }

              .surgical-report-preview .bg-slate-950,
              .surgical-report-preview .bg-slate-900,
              .surgical-report-preview .bg-slate-800,
              .surgical-report-preview .bg-slate-700,
              .surgical-report-preview .bg-slate-600,
              .surgical-report-preview .bg-slate-500 {
                background-color: #ffffff !important;
              }

              .surgical-report-preview .bg-yellow-50,
              .surgical-report-preview .bg-yellow-100,
              .surgical-report-preview .bg-amber-50,
              .surgical-report-preview .bg-amber-100 {
                background: #fffbea !important;
                border: 1px solid #facc15 !important;
                color: #0f172a !important;
              }

              .surgical-report-preview .border-slate-900,
              .surgical-report-preview .border-slate-800,
              .surgical-report-preview .border-slate-700,
              .surgical-report-preview .border-slate-600 {
                border-color: #cbd5e1 !important;
              }
            `}</style>
            {/* ... Print content unchanged ... */}
            <div className="max-w-5xl mx-auto bg-white p-12 shadow-none min-h-screen surgical-report-preview">
              
              {/* Header */}
              <div className="flex justify-between items-end border-b-4 border-slate-900 pb-6 mb-8">
                  <div>
                    <h1 className="text-4xl font-black tracking-tighter text-slate-900 leading-none">RELATÓRIO DE<br/>PLANEJAMENTO CIRÚRGICO</h1>
                    <p className="text-sm font-bold uppercase tracking-[0.3em] text-slate-500 mt-3">{t('surgicalPlanKitTitle')}</p>
                  </div>
                  <div className="text-right text-sm font-medium space-y-1 text-slate-600">
                    <p>{t('patientDateLabel')}: <span className="text-slate-900 font-bold">{new Date().toLocaleDateString()}</span></p>
                    <p>{t('surgicalPlanCaseIdLabel')}: <span className="text-slate-900 font-bold">{currentItem || t('surgicalPlanAnalysisImage')}</span></p>
                    {activePatient && <p>{t('patientPatientLabel')}: <span className="text-slate-900 font-bold">{activePatient.name}</span></p>}
                  </div>
              </div>

              <div className="grid grid-cols-1 gap-8">
                  
                  {/* Diagnosis */}
                  {reportDiagnosisText && (
                      <div className="break-inside-avoid">
                          <h3 className="text-lg font-bold text-slate-900 border-b border-slate-300 pb-2 mb-3 uppercase tracking-wide">1. Diagnóstico</h3>
                          <div className="space-y-3 text-sm text-slate-800">
                              <p className="text-base font-medium text-slate-800">
                                  <span className="font-bold uppercase tracking-wide text-slate-900">DIAGNÓSTICO:</span>{" "}
                                  {reportDiagnosisText}
                              </p>
                              {reportAnatomicalLocation && reportAnatomicalLocation !== reportDiagnosisText && (
                                  <p className="font-medium text-slate-700">
                                      <span className="font-bold uppercase tracking-wide text-slate-900">LOCALIZAÇÃO:</span>{" "}
                                      {reportAnatomicalLocation}
                                  </p>
                              )}
                              {reportDiagnosisPattern && (
                                  <p className="font-medium text-slate-700">
                                      <span className="font-bold uppercase tracking-wide text-slate-900">PADRÃO / CLASSIFICAÇÃO:</span>{" "}
                                      {reportDiagnosisPattern}
                                  </p>
                              )}
                          </div>
                      </div>
                  )}

                  <div className="break-inside-avoid">
                      <h3 className="text-lg font-bold text-slate-900 border-b border-slate-300 pb-2 mb-3 uppercase tracking-wide">2. Classificação AO/OTA</h3>
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                          <span className={`inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] ${planningClassification.severity === "HIGH" ? "bg-red-100 text-red-800 border-red-200" : planningClassification.severity === "MEDIUM" ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-emerald-100 text-emerald-800 border-emerald-200"}`}>
                              {planningClassification.severity}
                          </span>
                          {planningClassification.isApproximate && (
                              <span className="inline-flex items-center rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-cyan-800">
                                  Aproximada
                              </span>
                          )}
                      </div>
                      <p className="text-2xl font-black tracking-tight text-slate-900">{planningClassification.code}</p>
                      <p className="mt-2 text-base font-medium text-slate-800">{planningClassification.description}</p>
                      {planningClassification.rationale && (
                          <p className="mt-2 text-sm text-slate-600">{planningClassification.rationale}</p>
                      )}
                  </div>

                  {conductBadge && (
                      <div className="flex items-center gap-3">
                         <span className={`inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] ${conductBadge.className}`}>
                            {conductBadge.label}
                         </span>
                         {normalizedConduct === 'relative' && (
                            <span className="text-[11px]" style={{ color: atlasColors.textSecondary }}>{t('surgicalCaseClassificationRelativeSubtitle')}</span>
                         )}
                      </div>
                  )}

                  {surgicalPlanDifferentials.length > 0 && (
                      <div className="break-inside-avoid">
                          <h3 className="text-lg font-bold text-slate-900 border-b border-slate-300 pb-2 mb-3 uppercase tracking-wide">3. {differentialDiagnosisTitle}</h3>
                          <ul className="space-y-2 text-sm text-slate-700">
                              {surgicalPlanDifferentials.map((item, index) => (
                                  console.log("[AtlasModule] differential item", { index, item }),
                                  <li key={`${item}-${index}`} className="flex items-start gap-2">
                                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-slate-500 shrink-0" />
                                      <span>{item}</span>
                                  </li>
                              ))}
                          </ul>
                      </div>
                  )}

                    {/* Images Grid */}
                    {(originalReportImage || atlasReportImage) && (
                      <div className="break-inside-avoid">
                          <h3 className="text-lg font-bold text-slate-900 border-b border-slate-300 pb-2 mb-3 uppercase tracking-wide">{surgicalPlanDifferentials.length > 0 ? '4.' : '3.'} Documentação Visual</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {originalReportImage && (
                          <div className="border border-slate-200 p-2 rounded">
                            <p className="text-xs font-bold text-slate-500 mb-1 uppercase">Raio-X Original</p>
                            <img src={originalReportImage} className="w-full h-48 object-contain bg-black" alt="Raio-X Original" />
                          </div>
                          )}
                          {atlasReportImage && (
                          <div className="border border-slate-200 p-2 rounded">
                            <p className="text-xs font-bold text-slate-500 mb-1 uppercase">Reconstrução Atlas</p>
                            <img src={atlasReportImage} className="w-full h-48 object-contain bg-black" alt="Reconstrução Atlas" />
                          </div>
                          )}
                          </div>
                      </div>
                  )}

                  {/* Biometrics & Auto Measurements */}
                  <div className="grid grid-cols-2 gap-8 break-inside-avoid">
                      {biometrics && (biometrics.screwLength > 0 || biometrics.canalOcc > 0) && (
                          <div>
                              <h3 className="text-lg font-bold text-slate-900 border-b border-slate-300 pb-2 mb-3 uppercase tracking-wide">{surgicalPlanDifferentials.length > 0 ? '5.' : '4.'} Biometria Cirúrgica</h3>
                              <ul className="space-y-2 text-sm text-slate-700">
                                  <li><strong>Parafuso Principal:</strong> {biometrics.screwLength} mm</li>
                                  <li><strong>Ocupação Canal/Placa:</strong> {biometrics.canalOcc}%</li>
                                  <li><strong>Angulação Alvo:</strong> {biometrics.angulation}°</li>
                              </ul>
                          </div>
                      )}
                      {autoMeasurementRows.length > 0 && (
                          <div>
                              <h3 className="text-lg font-bold text-slate-900 border-b border-slate-300 pb-2 mb-3 uppercase tracking-wide">{surgicalPlanDifferentials.length > 0 ? '6.' : '5.'} Medições (IA)</h3>
                              <ul className="space-y-2 text-sm text-slate-700">
                                  {autoMeasurementRows.map((m, i) => (
                                      console.log("[AtlasModule] auto measurement item", { i, m }),
                                      <li key={i} className="flex justify-between border-b border-slate-100 pb-1">
                                          <span>{m.label}</span>
                                          <span className="font-bold font-mono">{m.value}</span>
                                      </li>
                                  ))}
                              </ul>
                          </div>
                      )}
                  </div>

                  {/* Selected Strategy */}
                  {selectedPlanBullets.length > 0 && (
                      <div className="break-inside-avoid">
                          <h3 className="text-lg font-bold text-slate-900 border-b border-slate-300 pb-2 mb-3 uppercase tracking-wide">
                            {activeStrategy === "surgical"
                              ? `${surgicalPlanDifferentials.length > 0 ? '7.' : '6.'} Técnica Cirúrgica Detalhada`
                              : `${surgicalPlanDifferentials.length > 0 ? '7.' : '6.'} Conduta Conservadora Estruturada`}
                          </h3>
                          <div className="text-sm text-slate-800 leading-relaxed text-justify">
                              {renderMarkdown(selectedPlanBullets.map((item) => `- ${item}`).join('\n'))}
                          </div>
                      </div>
                  )}

                  {/* Tips / Risks */}
                  {selectedPlanRiskBullets.length > 0 && (
                      <div className="break-inside-avoid bg-yellow-50 border border-yellow-200 p-6 rounded-xl">
                          <h3 className="text-lg font-bold text-yellow-800 border-b border-yellow-200 pb-2 mb-3 uppercase tracking-wide flex items-center gap-2">
                              <Lightbulb className="w-5 h-5" /> {activeStrategy === "surgical" ? 'Riscos e Armadilhas' : 'Alertas / Conversão'}
                          </h3>
                          <div className="text-sm text-yellow-900 leading-relaxed">
                              {renderMarkdown(selectedPlanRiskBullets.map((item) => `- ${item}`).join('\n'))}
                          </div>
                      </div>
                  )}

                  {/* Plan B & Materials */}
                  <div className="grid grid-cols-2 gap-8 break-inside-avoid">
                      {(activeStrategy === "surgical" && activeSurgicalOption.contingencyPlan?.scenarios?.length) ? (
                          <div>
                              <h3 className="text-lg font-bold text-red-800 border-b border-red-200 pb-2 mb-3 uppercase tracking-wide">
                                {surgicalPlanDifferentials.length > 0 ? '8.' : '7.'} {activeSurgicalOption.contingencyPlan.title || 'Plano de Contingência (B)'}
                              </h3>
                              {activeSurgicalOption.contingencyPlan.summary && (
                                <p className="text-sm text-slate-600 mb-3 italic">{activeSurgicalOption.contingencyPlan.summary}</p>
                              )}
                              <div className="text-sm text-slate-700 space-y-2">
                                {activeSurgicalOption.contingencyPlan.scenarios.map((scenario: any, index: number) => (
                                  <div key={index} className="border-l-2 border-red-200 pl-3">
                                    <strong className="text-red-700">Cenário:</strong> {scenario.problem}<br/>
                                    <strong className="text-red-700">Conduta:</strong> {scenario.action}
                                  </div>
                                ))}
                              </div>
                          </div>
                      ) : (activeStrategy === "surgical" ? activeSurgicalOption.planB : activeConservativePlan.conversionCriteria)?.length ? (
                          <div>
                              <h3 className="text-lg font-bold text-red-800 border-b border-red-200 pb-2 mb-3 uppercase tracking-wide">
                                {activeStrategy === "surgical"
                                  ? `${surgicalPlanDifferentials.length > 0 ? '8.' : '7.'} Plano de Contingência (B)`
                                  : `${surgicalPlanDifferentials.length > 0 ? '8.' : '7.'} Critérios de Conversão`}
                              </h3>
                              <div className="text-sm text-slate-700">
                                {renderMarkdown((activeStrategy === "surgical" ? activeSurgicalOption.planB : activeConservativePlan.conversionCriteria)?.map((item) => `- ${item}`).join('\n'))}
                              </div>
                          </div>
                      ) : null}
                      
                      {materialList.length > 0 && (
                          <div>
                              <h3 className="text-lg font-bold text-slate-900 border-b border-slate-300 pb-2 mb-3 uppercase tracking-wide">{surgicalPlanDifferentials.length > 0 ? '9.' : '8.'} Materiais Previstos</h3>
                              <ul className="text-sm text-slate-700 space-y-1">
                                  {materialList.map((m, i) => (
                                      <li key={i} className="flex items-start gap-2">
                                          <div className={`w-4 h-4 border border-slate-400 rounded flex items-center justify-center ${m.checked ? 'bg-slate-200' : ''}`}>
                                              {m.checked && <Check className="w-3 h-3"/>}
                                          </div>
                                          <span className={m.checked ? 'line-through text-slate-400' : ''}>{m.text}</span>
                                      </li>
                                  ))}
                              </ul>
                          </div>
                      )}
                  </div>

                  {/* Scientific Evidence Summary */}
                  <div className="break-inside-avoid">
                      <h3 className="text-lg font-bold text-slate-900 border-b border-slate-300 pb-2 mb-3 uppercase tracking-wide">{surgicalPlanDifferentials.length > 0 ? '10.' : '9.'} Evidências Científicas</h3>
                      {reportEvidenceItems.length > 0 ? (
                        <ul className="space-y-2 text-sm text-slate-700">
                          {reportEvidenceItems.map((item, index) => {
                            const displayText = formatEvidenceReportLine(item);
                            const safeUrl = String(item.url || "").trim();
                            return (
                              <li key={`${item.url || item.title}-${index}`} className="flex items-start gap-2">
                                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-slate-500 shrink-0" />
                                {safeUrl ? (
                                  <a
                                    href={safeUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="underline decoration-cyan-600 underline-offset-2 hover:text-cyan-700"
                                    style={{ color: "#0f172a" }}
                                  >
                                    {displayText}
                                  </a>
                                ) : (
                                  <span>{displayText}</span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p className="text-sm text-slate-700">Baseado em prática ortopédica consolidada.</p>
                      )}
                  </div>

                  {/* OPME Justification */}
                  <div className="break-inside-avoid border-t-2 border-slate-200 pt-6 mt-4">
                      <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-4">Justificativa Técnica (OPME)</h3>
                      <div className="text-xs text-slate-600 font-mono bg-slate-50 p-4 rounded border border-slate-200 whitespace-pre-wrap">
                          {getOpmeJustification()}
                      </div>
                  </div>

              </div>

              <div className="fixed top-4 right-4 flex gap-2 print:hidden z-50">
                  <button onClick={() => setShowReport(false)} className="px-6 py-3 bg-white hover:bg-slate-100 text-slate-900 font-bold rounded-xl shadow-2xl border border-slate-200">
                      Fechar
                  </button>
                  {activePatient && (
                  <button onClick={handleSaveToPatient} disabled={atlasSaveButtonDisabled} className="px-6 py-3 font-bold rounded-xl shadow-2xl border transition-all disabled:opacity-50 disabled:cursor-not-allowed" title={atlasSaveButtonTitle} style={{ background: atlasColors.bgCard, color: atlasColors.textPrimary, borderColor: atlasColors.border }}>
                    {atlasSaveButtonLabel}
                  </button>
                  )}
                  <button onClick={handlePrintReport} className="px-6 py-3 font-bold rounded-xl shadow-2xl" style={{ background: atlasColors.bgCard, color: atlasColors.textPrimary, border: `1px solid ${atlasColors.border}` }}>
                      Imprimir / PDF
                  </button>
              </div>
            </div>
        </div>
      )}

      {/* Library Modal */}
      {showLibrary && (
        <div className={`fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/90 ${atlasBackdropBlurClass} ${atlasEntranceClass} fade-in duration-300`}>
          <div className={`glass w-full max-w-5xl max-h-[85vh] flex flex-col rounded-3xl border border-slate-700 shadow-2xl ${atlasEntranceClass} zoom-in-95 duration-300 overflow-hidden`}>
            <div className={`p-6 border-b border-slate-700 flex justify-between items-center sticky top-0 bg-slate-900/90 ${atlasBackdropBlurClass} z-10`}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg border" style={{ background: atlasColors.bgCard, borderColor: atlasColors.border }}><Library className="w-6 h-6" style={{ color: atlasColors.accent }} /></div>
                <div>
                    <h3 className="text-xl font-bold text-white">{t('libraryTitle')}</h3>
                    <p className="text-xs text-slate-400 uppercase tracking-widest">Armazenamento Offline (Hardcode)</p>
                </div>
              </div>
              <button onClick={() => setShowLibrary(false)} className="p-2 hover:bg-slate-800 rounded-full transition-colors"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar bg-slate-900/50">
              {savedCases.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-slate-500 opacity-50 gap-4">
                      <BookMarked className="w-16 h-16" />
                      <p className="text-sm font-bold uppercase tracking-widest">Biblioteca Vazia</p>
                  </div>
              ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                      {savedCases.map((savedCase) => (
                          <div key={savedCase.id} className="rounded-2xl border overflow-hidden group transition-all flex flex-col shadow-lg" style={{ background: atlasColors.bgCard, borderColor: atlasColors.border }}>
                              <div className="relative aspect-video overflow-hidden border-b" style={{ background: atlasColors.bgPrimary, borderColor: atlasColors.border }}>
                                  {savedCase.image ? (
                                      <img src={savedCase.image} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                  ) : (
                                      <div className="w-full h-full flex items-center justify-center text-slate-600"><ImageIcon className="w-8 h-8"/></div>
                                  )}
                                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-60"></div>
                                  <div className="absolute bottom-2 left-2 right-2 flex justify-between items-end">
                                      <span className="text-[10px] bg-black/50 backdrop-blur px-2 py-1 rounded text-slate-300 font-mono">
                                          {new Date(savedCase.date).toLocaleDateString()}
                                      </span>
                                  </div>
                              </div>
                              <div className="p-4 flex-1 flex flex-col">
                                  <h4 className="font-bold text-white text-sm mb-1 truncate">{savedCase.title}</h4>
                                  <p className="text-[10px] text-slate-400 line-clamp-2 mb-4">{savedCase.plan?.diagnostico || "Sem diagnóstico"}</p>
                                  
                                  <div className="mt-auto flex gap-2">
                                      <button 
                                          onClick={() => handleLoadCase(savedCase)}
                                          className="flex-1 py-2 rounded-lg text-xs font-bold uppercase tracking-wide flex items-center justify-center gap-2 transition-colors" style={{ background: atlasColors.bgCard, color: atlasColors.textPrimary, border: `1px solid ${atlasColors.border}` }}
                                      >
                                          <Upload className="w-3 h-3" /> Carregar
                                      </button>
                                      <button 
                                          onClick={(e) => handleDeleteCase(savedCase.id, e)}
                                          className="p-2 hover:bg-red-500/20 text-slate-500 hover:text-red-400 rounded-lg transition-colors"
                                          title="Excluir"
                                      >
                                          <Trash2 className="w-4 h-4" />
                                      </button>
                                  </div>
                              </div>
                          </div>
                      ))}
                  </div>
              )}
            </div>
          </div>
        </div>
      )}

      <PatientFilePickerModal
        open={showPatientFilePicker}
        title={t('choosePatientFile')}
        subtitle={t('choosePatientFileSubtitle')}
        initialPatientId={activePatient?.id || ''}
        allowPdf={false}
        onClose={() => setShowPatientFilePicker(false)}
        onSelectFile={async ({ dataUrl }) => {
          setUploadedImages((prev) => {
            if (prev.includes(dataUrl) || prev.length >= 5) {
              return prev;
            }

            return [...prev, dataUrl];
          });
        }}
      />

      {/* Upload & Search Area */}
      {!isFullscreen && !isFocusMode && (
        <div className={`${atlasEntranceClass} slide-in-from-top-4 duration-500`}>
          <div className={`rounded-[28px] border p-5 shadow-[0_18px_50px_rgba(0,0,0,0.28),inset_0_1px_0_rgba(255,255,255,0.05)] ${isLowPerfDevice ? '' : 'backdrop-blur-xl'}`} style={{ background: atlasColors.bgCardPremium, borderColor: atlasColors.borderMuted }}>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.35fr)_auto]">
              <form onSubmit={(e) => { e.preventDefault(); if (searchQuery.trim()) handleSelectItem(searchQuery); }} className="flex flex-col gap-3">
                <div className="space-y-2">
                  <div className="text-[10px] font-bold uppercase tracking-[0.32em]" style={{ color: atlasColors.accent }}>
                    {language === 'pt' ? 'Busca anatômica' : 'Anatomy search'}
                  </div>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-300/70 w-5 h-5" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={language === 'pt' ? "Busca anatômica..." : "Anatomy search..."}
                      className="w-full rounded-2xl py-4 pl-12 pr-4 text-[14px] outline-none transition-all placeholder:text-slate-500 focus:border-cyan-300/40 focus:shadow-[0_0_0_1px_rgba(34,211,238,0.22),0_0_24px_rgba(34,211,238,0.14)]"
                      style={{ background: "rgba(2,6,23,0.96)", border: `1px solid ${atlasColors.border}`, color: atlasColors.textPrimary }}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-[10px] font-bold uppercase tracking-[0.32em]" style={{ color: atlasColors.accent }}>
                    {language === 'pt' ? 'Foco clínico' : 'Clinical focus'}
                  </div>
                  <div className="rounded-2xl border px-4 py-3 text-xs leading-5" style={{ background: "rgba(2,6,23,0.82)", borderColor: atlasColors.border, color: atlasColors.textSecondary }}>
                    {language === 'pt'
                      ? 'Análise do exame, localização anatômica precisa, classificação da lesão e plano cirúrgico textual.'
                      : 'Exam analysis, precise anatomical localization, lesion classification, and text-based surgical plan.'}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <button type="button" onClick={() => setShowLibrary(true)} className="flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-[10px] font-bold uppercase tracking-[0.22em] transition-all hover:-translate-y-0.5 hover:border-cyan-300/25 hover:shadow-[0_0_20px_rgba(34,211,238,0.08)]" style={{ background: "rgba(2,6,23,0.9)", borderColor: atlasColors.border, color: atlasColors.textPrimary }}>
                    <BookMarked className="h-4 w-4" />
                    {t('libraryTitle')}
                  </button>
                  <button type="button" onClick={() => setShowPatientFilePicker(true)} className="flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-[10px] font-bold uppercase tracking-[0.22em] transition-all hover:-translate-y-0.5 hover:border-cyan-300/25 hover:shadow-[0_0_20px_rgba(34,211,238,0.08)]" style={{ background: "rgba(2,6,23,0.9)", borderColor: atlasColors.border, color: atlasColors.textPrimary }}>
                    <FolderSearch className="h-4 w-4" />
                    {t('choosePatientFile')}
                  </button>
                  <button type="submit" disabled={isGenerating || !searchQuery} className="flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-[11px] font-bold uppercase tracking-[0.24em] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_16px_32px_rgba(34,211,238,0.18)] disabled:cursor-not-allowed disabled:opacity-50 md:col-span-2 xl:col-span-1" style={{ background: "linear-gradient(180deg, rgba(34,211,238,0.92), rgba(59,130,246,0.92))", color: "#eff6ff", border: "1px solid rgba(125,211,252,0.36)" }}>
                    <Sparkles className="h-5 w-5" />
                    {language === 'pt' ? 'Gerar plano' : 'Generate plan'}
                  </button>
                </div>
              </form>

              <div
                className={`group relative overflow-hidden rounded-[24px] border-2 border-dashed p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_24px_rgba(34,211,238,0.08)] ${uploadedImages.length > 0 ? '' : 'cursor-pointer'} ${isUploadDragging ? 'shadow-[0_0_28px_rgba(34,211,238,0.18)]' : ''}`}
                style={{ background: "linear-gradient(180deg, rgba(2,6,23,0.94), rgba(5,15,30,0.92))", borderColor: isUploadDragging ? "rgba(34,211,238,0.65)" : "rgba(148,163,184,0.22)" }}
                onClick={() => uploadedImages.length === 0 && fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setIsUploadDragging(true); }}
                onDragEnter={(e) => { e.preventDefault(); setIsUploadDragging(true); }}
                onDragLeave={() => setIsUploadDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsUploadDragging(false);
                  void processSurgicalPlanFiles(Array.from(e.dataTransfer.files || []));
                }}
              >
                {uploadedImages.length > 0 ? (
                  <div className="flex h-full max-h-[200px] min-h-[180px] flex-col gap-3">
                    <div className="flex items-center justify-between gap-3 px-1">
                      <span className="text-[10px] font-bold uppercase tracking-[0.24em]" style={{ color: atlasColors.textSecondary }}>Imagens ({uploadedImages.length}/5)</span>
                      <button onClick={handleResetUpload} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-red-300 transition hover:text-red-200"><Trash2 className="h-3 w-3" /> Limpar</button>
                    </div>
                    <div className="image-list flex flex-1 items-center gap-4 overflow-x-auto overflow-y-hidden pb-1 pr-1">
                      {uploadedImages.map((img, idx) => (
                        <div
                          key={idx}
                          onClick={(e) => handleSetPrimaryImage(idx, e)}
                          className={`thumbnail relative flex h-[140px] w-[140px] shrink-0 overflow-hidden rounded-[14px] border-2 transition-all ${idx === 0 ? 'active' : ''}`}
                          style={{
                            borderColor: idx === 0 ? "rgba(34,211,238,0.6)" : atlasColors.border,
                            boxShadow: idx === 0 ? "0 0 20px rgba(34,211,238,0.25)" : "none",
                          }}
                        >
                          {img.startsWith('data:application/pdf') ? (
                            <div className="flex h-full w-full flex-col items-center justify-center bg-slate-900 text-slate-400">
                              <FileType className="mb-1 h-6 w-6" />
                              <span className="text-[8px] font-bold">PDF</span>
                            </div>
                          ) : (
                            <img src={img} className="h-full w-full object-cover" alt={`Upload ${idx + 1}`} />
                          )}
                          {idx === 0 && <div className="absolute inset-x-0 top-0 py-0.5 text-center text-[8px] font-bold uppercase text-white" style={{ background: atlasColors.accent }}>Principal</div>}
                          <button onClick={(e) => handleRemoveImage(idx, e)} className="absolute right-1 top-1 rounded-full bg-red-500/80 p-1 text-white opacity-0 transition-opacity hover:opacity-100"><X className="h-2 w-2" /></button>
                        </div>
                      ))}
                      {uploadedImages.length < 5 && (
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className="add-thumbnail flex h-[140px] w-[140px] shrink-0 cursor-pointer items-center justify-center rounded-[14px] border border-dashed transition-all hover:border-cyan-300/25 hover:shadow-[0_0_20px_rgba(34,211,238,0.08)]"
                          style={{ borderColor: "rgba(34,211,238,0.3)", background: "rgba(2,6,23,0.8)", color: atlasColors.textSecondary }}
                        >
                          <Plus className="h-4 w-4" />
                        </div>
                      )}
                    </div>

                    <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={manualAnatomy}
                        onChange={(e) => setManualAnatomy(e.target.value)}
                        placeholder={language === 'pt' ? "Contexto anatômico..." : "Anatomy context..."}
                        className="w-full rounded-xl px-3 py-2 text-xs outline-none transition-all focus:border-cyan-300/35 focus:shadow-[0_0_0_1px_rgba(34,211,238,0.16),0_0_18px_rgba(34,211,238,0.08)]"
                        style={{ background: "rgba(2,6,23,0.96)", border: `1px solid ${atlasColors.border}`, color: atlasColors.textPrimary }}
                      />
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProcessSurgicalPlanAnalysis();
                      }}
                      disabled={!canProcessSurgicalPlan}
                      title={disabledReason || undefined}
                      className="flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-[10px] font-bold uppercase tracking-[0.22em] transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_32px_rgba(34,211,238,0.12)] disabled:cursor-not-allowed disabled:opacity-50"
                      style={{ background: "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(2,6,23,0.96))", color: atlasColors.textPrimary, borderColor: atlasColors.border }}
                    >
                      <ScanSearch className="h-4 w-4" /> {isProcessingSurgicalPlan ? t('surgicalPlanProcessing') : t('surgicalPlanProcessAnalysis', { count: imagesCount })}
                    </button>
                    <input type="file" ref={fileInputRef} className="hidden" accept="image/*,application/pdf" multiple onChange={handleFileUpload} />
                  </div>
                ) : (
                  <div className="flex min-h-[180px] flex-col items-center justify-center gap-3 text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-cyan-300/15 bg-cyan-500/10 shadow-[0_0_20px_rgba(34,211,238,0.08)]">
                      <Upload className="h-7 w-7 text-cyan-300" />
                    </div>
                    <div className="space-y-1">
                      <span className="block text-[10px] font-bold uppercase tracking-[0.24em]" style={{ color: atlasColors.textPrimary }}>{t('uploadImagingFiles')}</span>
                      <span className="block text-[12px] leading-[1.4]" style={{ color: atlasColors.textSecondary }}>
                        {isUploadDragging
                          ? (language === 'pt' ? 'Solte o exame aqui' : 'Drop the exam here')
                          : (language === 'pt' ? 'Solte arquivos ou clique para enviar imagens e PDFs' : 'Drop files or click to upload images and PDFs')}
                      </span>
                    </div>
                    <input type="file" ref={fileInputRef} className="hidden" accept="image/*,application/pdf" multiple onChange={handleFileUpload} />
                  </div>
                )}
              </div>
            </div>

            {isProcessingSurgicalPlan && (
              <div className="mt-5 overflow-hidden rounded-[24px] border p-5 shadow-[0_18px_50px_rgba(0,0,0,0.22)]" style={{ background: "linear-gradient(135deg, rgba(8,47,73,0.62), rgba(2,6,23,0.96))", borderColor: "rgba(34,211,238,0.22)" }}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-cyan-200/70">Plano Cirúrgico</p>
                    <h3 className="mt-1 text-lg font-bold text-white">Processando análise cirúrgica</h3>
                    <p className="mt-2 text-xs leading-5 text-slate-300">
                      Mantendo o exame anexado enquanto a IA estrutura diagnóstico, localização e estratégia.
                    </p>
                  </div>
                  <div className="rounded-full border border-cyan-300/20 bg-cyan-400/10 px-3 py-1 text-xs font-bold tabular-nums text-cyan-100">
                    {Math.max(8, atlasGeneration.progress || 8)}%
                  </div>
                </div>

                <div className="mt-5 h-2 overflow-hidden rounded-full bg-slate-950/70">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-cyan-300 via-sky-400 to-blue-500 transition-all duration-500"
                    style={{ width: `${Math.max(8, atlasGeneration.progress || 8)}%` }}
                  />
                </div>

                <div className="mt-5 grid gap-2 md:grid-cols-5">
                  {surgicalPlanProcessingSteps.map((step, index) => {
                    const isDone = index < surgicalPlanProcessingStepIndex;
                    const isActive = index === surgicalPlanProcessingStepIndex;
                    return (
                      <div
                        key={step}
                        className={`rounded-2xl border px-3 py-3 text-xs transition-colors ${
                          isActive
                            ? "border-cyan-300/35 bg-cyan-400/12 text-white"
                            : isDone
                              ? "border-emerald-300/20 bg-emerald-400/10 text-emerald-100"
                              : "border-white/8 bg-white/5 text-slate-400"
                        }`}
                      >
                        <div className="mb-2 flex items-center gap-2">
                          <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-black ${
                            isDone
                              ? "bg-emerald-400 text-slate-950"
                              : isActive
                                ? "bg-cyan-300 text-slate-950"
                                : "bg-slate-800 text-slate-400"
                          }`}>
                            {index + 1}
                          </span>
                          {isActive && <span className="h-2 w-2 rounded-full bg-cyan-300 animate-pulse" />}
                        </div>
                        <span className="font-semibold leading-5">{step}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className={`flex flex-col gap-6 flex-1 min-h-0 ${isFullscreen ? 'overflow-y-auto overflow-x-hidden custom-scrollbar' : ''} pb-[120px]`}>
        
        {/* Main Viewer */}
        {!surgicalPlanningPrimaryFlow && (
        <div
        className="relative flex min-h-[72vh] w-full flex-col overflow-hidden rounded-[24px] border border-cyan-500/12 shadow-[0_0_80px_rgba(0,180,255,0.06),inset_0_0_120px_rgba(0,0,0,0.45)] ring-1 ring-cyan-400/10 lg:min-h-[736px]"
          style={{
            background:
              "radial-gradient(circle at top left, rgba(34,211,238,0.08), transparent 32%), linear-gradient(180deg, #07111f 0%, #050b16 100%)",
          }}
        >
          {captureNotice && (
            <div
              className={`pointer-events-none absolute left-1/2 top-5 z-40 -translate-x-1/2 rounded-full border px-4 py-2 text-[10px] font-bold uppercase tracking-[0.24em] shadow-[0_12px_28px_rgba(0,0,0,0.25)] ${
                captureNotice.kind === 'success' ? 'bg-emerald-500/12 text-emerald-200' : 'bg-rose-500/12 text-rose-200'
              }`}
              style={{ borderColor: captureNotice.kind === 'success' ? 'rgba(52,211,153,0.25)' : 'rgba(251,113,133,0.25)' }}
            >
              {captureNotice.message}
            </div>
          )}

          {!shouldHideViewerInfoCard && (
          <aside className="absolute left-5 top-5 z-30 w-[clamp(180px,16vw,228px)] max-w-[230px] max-h-[calc(100%-120px)] overflow-y-auto rounded-[22px] border p-3 shadow-[0_18px_45px_rgba(0,0,0,0.28),inset_0_1px_0_rgba(255,255,255,0.06)] custom-scrollbar" style={{ background: atlasColors.bgCardPremium, borderColor: atlasColors.borderMuted }}>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-cyan-400/15 bg-cyan-500/10 text-cyan-300 shadow-[0_0_24px_rgba(34,211,238,0.08)]">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[8px] font-bold uppercase tracking-[0.5em] text-slate-400">ORTHOPRECISION</div>
                <div className="mt-1 text-[18px] font-semibold tracking-[-0.02em] text-white/95">Atlas 3D</div>
              </div>
            </div>

            <div className="mt-5 space-y-4 text-left">
              <ClinicalCard
                title="Resumo anatômico"
                items={[makeShortAtlasSummary(detectedAnatomicalAnchors || emptySectionLabel, 84)]}
                variant="default"
              />
              <ClinicalCard
                title="Exame atual"
                items={[selectedMedia ? makeShortAtlasSummary(currentItem || emptySectionLabel, 72) : emptySectionLabel]}
                variant="neutral"
              />
              <ClinicalCard
                title="Interpretação clínica"
                items={[makeShortAtlasSummary(surgicalPlan?.diagnostico || emptySectionLabel, 96)]}
                variant="success"
              />
            </div>
          </aside>
          )}

          <div ref={containerRef} className="absolute inset-0 z-10 flex items-center justify-center px-6 py-6 lg:px-8 lg:py-8">
             {/* ... loading state ... */}
             {isGenerating ? (
               atlasGeneration.active ? (
                 <div className="w-full max-w-xl px-6 z-10">
                   <div className={`rounded-3xl border p-6 shadow-2xl shadow-black/30 ${isLowPerfDevice ? '' : 'backdrop-blur-xl'}`} style={{ background: atlasColors.bgPrimary, borderColor: atlasColors.border }}>
                     <div className="flex items-center gap-4">
                       <div className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border" style={{ borderColor: atlasColors.border, background: atlasColors.bgCard }}>
                         <RotateCcw className="h-8 w-8 animate-spin" style={{ color: atlasColors.accent }} />
                         <div className="absolute inset-0 flex items-center justify-center">
                           <Zap className="h-4 w-4" style={{ color: atlasColors.accent }} />
                         </div>
                       </div>
                       <div className="min-w-0 flex-1 text-left">
                         <p className="text-[10px] font-bold uppercase tracking-[0.35em]" style={{ color: atlasColors.accent }}>
                           {getAtlasGenerationTitle(atlasGeneration.type)}
                         </p>
                         <h3 className="mt-1 text-lg font-bold" style={{ color: atlasColors.textPrimary }}>{atlasGeneration.stage}</h3>
                         <p className="mt-1 text-sm" style={{ color: atlasColors.textSecondary }}>
                           {language === 'pt' ? 'Acompanhe o progresso da geração no Atlas' : 'Track the Atlas generation progress'}
                         </p>
                       </div>
                       <div className="text-right">
                         <p className="text-3xl font-black tabular-nums" style={{ color: atlasColors.textPrimary }}>{atlasGeneration.progress}%</p>
                       </div>
                     </div>
                     <div className="mt-6 space-y-2">
                       <div className="h-3 overflow-hidden rounded-full" style={{ background: atlasColors.bgCard, border: `1px solid ${atlasColors.border}` }}>
                         <div
                           className="h-full rounded-full transition-all duration-300 ease-out"
                           style={{ width: `${atlasGeneration.progress}%`, background: atlasColors.accent }}
                         />
                       </div>
                       <div className="flex items-center justify-between text-[11px] font-medium" style={{ color: atlasColors.textSecondary }}>
                         <span>{atlasGeneration.stage}</span>
                         <span>{atlasGeneration.progress}%</span>
                       </div>
                     </div>
                   </div>
                 </div>
               ) : (
                 <div className="empty-state text-center animate-pulse z-10">
                   <div className="relative mx-auto flex h-24 w-24 items-center justify-center rounded-full border border-cyan-300/15 bg-cyan-500/10 shadow-[0_0_0_1px_rgba(34,211,238,0.08),0_0_42px_rgba(34,211,238,0.12)]">
                     <ScanSearch className="h-10 w-10 animate-pulse text-cyan-300" />
                     <div className="absolute inset-0 rounded-full border border-cyan-200/10" />
                   </div>
                   <div className="space-y-2">
                     <h3 className="text-lg font-bold uppercase tracking-[0.22em]" style={{ color: atlasColors.textPrimary }}>{language === 'pt' ? 'Gerando análise e plano cirúrgico...' : 'Generating analysis and surgical plan...'}</h3>
                     <p className="text-sm font-medium" style={{ color: atlasColors.textSecondary }}>
                       {genStep || (language === 'pt' ? 'Acompanhe o progresso do plano cirúrgico.' : 'Track the surgical plan progress.')}
                     </p>
                   </div>
                 </div>
               )
             ) : error ? (
               // ... error state ...
               <div className="text-center space-y-6 max-w-md p-8 z-10">
                 <div className="p-4 rounded-full w-fit mx-auto border" style={{ background: atlasColors.bgCard, borderColor: atlasColors.border }}><AlertCircle className="w-12 h-12" style={{ color: atlasColors.accent }} /></div>
                 <h3 className="text-xl font-bold leading-tight" style={{ color: atlasColors.textPrimary }}>{error.message}</h3>
                 <p className="text-sm leading-relaxed" style={{ color: atlasColors.textSecondary }}>{error.details}</p>
                 <div className="flex flex-col gap-3">
                     {error.showKeyBtn && getAiStudio() && <button onClick={() => getAiStudio()?.openSelectKey().then(() => setError(null))} className="px-6 py-3 rounded-2xl text-xs font-bold transition-all uppercase tracking-widest flex items-center gap-2 mx-auto" style={{ background: atlasColors.bgCard, color: atlasColors.textPrimary, border: `1px solid ${atlasColors.border}` }}><Key className="w-4 h-4" /> Config API</button>}
                     <button onClick={() => { setError(null); void handleSelectItem(currentItem || searchQuery); }} className="px-6 py-3 rounded-2xl text-xs font-bold transition-all uppercase tracking-widest mx-auto" style={{ background: atlasColors.bgCard, color: atlasColors.textPrimary, border: `1px solid ${atlasColors.border}` }}>{language === 'pt' ? 'Tentar novamente' : language === 'es' ? 'Reintentar' : 'Try again'}</button>
                 </div>
               </div>
              ) : compareMode ? (
                  <div
                    ref={compareCaptureRef}
                    className="relative flex h-full w-full flex-col overflow-hidden rounded-[28px] border p-2 md:p-3 lg:p-4"
                    style={{
                      background: '#07111f',
                      borderColor: atlasColors.border,
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 24px 70px rgba(0,0,0,0.45)',
                    }}
                  >
                    <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 md:grid-cols-2 md:gap-5">
                      {([
                        {
                          key: 'original',
                          label: 'EXAME ORIGINAL',
                          title: 'Imagem original',
                          media: uploadedImages?.[0] || null,
                          zoom: compareOriginalZoomScale,
                          pan: compareOriginalPanOffset,
                          isActive: activeComparePane === 'original',
                        },
                        {
                          key: 'reconstruction',
                          label: activeVisualizationMode === '2d' ? 'RECONSTRUÇÃO 2D' : 'RECONSTRUÇÃO 3D',
                          title: activeVisualizationMode === '2d' ? 'Reconstrução 2D' : 'Reconstrução 3D',
                          media: activeGeneratedAtlasMedia,
                          zoom: compareAtlasZoomScale,
                          pan: compareAtlasPanOffset,
                          isActive: activeComparePane === 'reconstruction',
                        },
                      ] as const).map((panel) => {
                        const isPdf = Boolean(panel.media && panel.media.startsWith('data:application/pdf'));
                        const isOriginal = panel.key === 'original';
                        const imageStyle = isOriginal ? getAtlasImageStyle(100, panel.zoom) : getAtlasImageStyle(undefined, panel.zoom);
                        return (
                          <div
                            key={panel.key}
                            className={`relative overflow-hidden rounded-[24px] border shadow-[0_0_0_1px_rgba(34,211,238,0.08),0_20px_60px_rgba(0,0,0,0.45)] transition-all duration-300 ${panel.isActive ? 'ring-1 ring-cyan-300/15' : ''}`}
                            style={{
                              background: 'radial-gradient(circle at center, rgba(15,23,42,0.92), rgba(2,6,23,1))',
                              borderColor: atlasColors.border,
                            }}
                            onClick={() => setActiveComparePane(panel.key)}
                            onDoubleClick={() => setComparePaneState(panel.key, 1, { x: 0, y: 0 })}
                          >
                            <div className="pointer-events-none absolute left-4 top-4 z-10 space-y-1 text-left">
                              <div className="text-[10px] font-bold uppercase tracking-[0.42em]" style={{ color: atlasColors.accent }}>{panel.label}</div>
                              <div className="text-[14px] font-semibold tracking-[-0.02em]" style={{ color: atlasColors.textPrimary }}>{panel.title}</div>
                            </div>
                            <div
                              ref={(node) => { comparePanelRefs.current[panel.key] = node; }}
                              className={`relative h-[44vh] min-h-[320px] overflow-hidden md:h-[47vh] md:min-h-[360px] ${getCompareCursorStyle(panel.key, panel.zoom)}`}
                              style={{ background: 'radial-gradient(circle at center, rgba(15,23,42,0.9), rgba(2,6,23,1))', touchAction: 'none', overscrollBehavior: 'contain' }}
                              onWheelCapture={(e) => handleCompareWheel(panel.key, e)}
                              onMouseDown={(e) => handleCompareStart(panel.key, e)}
                              onMouseMove={(e) => handleCompareMove(panel.key, e)}
                              onMouseUp={handleCompareEnd}
                              onMouseLeave={handleCompareEnd}
                              onTouchStart={(e) => handleCompareTouchStart(panel.key, e)}
                              onTouchMove={(e) => handleCompareTouchMove(panel.key, e)}
                              onTouchEnd={handleCompareTouchEnd}
                              onTouchCancel={handleCompareTouchEnd}
                            >
                              {panel.media ? (
                                isPdf ? (
                                  <iframe src={panel.media} className="h-full w-full border-none bg-white" title={panel.title} />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center p-1.5 md:p-2">
                                    <div
                                      style={{
                                        transform: `translate(${panel.pan.x}px, ${panel.pan.y}px) scale(${panel.zoom}) rotate(${isOriginal ? originalRotation : atlasRotation}deg)`,
                                        transition: compareDragPane === panel.key ? 'none' : 'transform 0.1s ease-out',
                                      }}
                                      className="flex h-full w-full items-center justify-center transition-transform duration-300"
                                    >
                                      <div className="atlas-image-wrapper flex h-full w-full items-center justify-center">
                                        <img
                                          src={panel.media}
                                          style={imageStyle}
                                          className="block select-none object-contain"
                                          alt={panel.title}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )
                              ) : (
                                <div className="flex h-full items-center justify-center text-sm text-slate-500">
                                  {isOriginal ? 'Sem imagem original' : 'Reconstrução 3D ainda não gerada.'}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  ) : defaultViewerMedia ? (
      <div
        className={`w-full h-full relative group overscroll-none touch-none ${getCursorStyle()}`}
        onWheel={handleOriginalWheel}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
    onTouchStart={handleStart}
    onTouchMove={handleMove}
    onTouchEnd={handleEnd}
  >
  <div
    style={{
      transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomScale}) rotate(${activeViewerRotation}deg)`,
      transition: isDragging ? 'none' : 'transform 0.1s ease-out'
    }}
    className="relative w-full h-full flex items-center justify-center origin-center mx-auto"
  >
                   {/* Handle PDF Display in Viewer */}
                   {defaultViewerMedia.startsWith('data:application/pdf') ? (
                        <iframe 
                            src={defaultViewerMedia} 
                            className="w-full h-full border-none bg-white rounded-lg shadow-xl" 
                            title="PDF Viewer"
                        />
                   ) : (viewMode === 'image' ? <div className="atlas-image-wrapper flex h-full w-full items-center justify-center"><img src={defaultViewerMedia} style={getAtlasImageStyle()} className="block select-none pointer-events-none object-contain" alt="Anatomia" /></div> : <video ref={videoRef} src={defaultViewerMedia} autoPlay loop muted playsInline onTimeUpdate={onVideoTimeUpdate} style={{ filter: `brightness(${brightness}%)` }} className="w-auto h-auto max-w-full max-h-full object-contain pointer-events-none" />)}
                 </div>
               </div>
              ) : (
                <div className="empty-state z-10 flex flex-col items-center justify-center gap-4 text-center opacity-85">
                  <div
                    className="relative flex h-28 w-28 items-center justify-center rounded-full border border-cyan-300/15 bg-cyan-500/10"
                    style={{
                      background: "radial-gradient(circle, rgba(34,211,238,0.06), transparent 45%)",
                      animation: 'atlasPulse 2.2s ease-in-out infinite',
                      boxShadow: '0 0 0 rgba(34,211,238,0.0)',
                    }}
                  >
                    <ScanSearch className="h-14 w-14 text-cyan-200 drop-shadow-[0_0_16px_rgba(34,211,238,0.18)]" />
                    <div className="absolute inset-6 rounded-full border border-cyan-300/10" />
                    <div className="absolute inset-10 rounded-full border border-cyan-200/8" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="mt-[18px] text-[18px] font-bold tracking-[-0.02em]" style={{ color: atlasColors.textPrimary }}>
                      {emptyViewerTitle}
                    </h3>
                    <p className="max-w-[420px] text-[13px] leading-[1.5] opacity-75" style={{ color: atlasColors.textSecondary }}>
                      {emptyViewerSubtitle}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="rounded-full border px-4 py-2 text-[10px] font-bold uppercase tracking-[0.24em] transition-all hover:-translate-y-0.5 hover:border-cyan-300/30 hover:shadow-[0_0_22px_rgba(34,211,238,0.12)]"
                      style={{
                        background: "linear-gradient(135deg, rgba(34,211,238,0.18), rgba(14,165,233,0.22))",
                        color: "#e0faff",
                        borderColor: "rgba(34,211,238,0.35)",
                        boxShadow: "0 10px 30px rgba(34,211,238,0.12)",
                      }}
                    >
                      {emptyViewerActionLabel}
                    </button>
                  </div>
                </div>
              )}
          </div>
          <div className="absolute inset-x-4 bottom-4 z-30 pointer-events-auto">
            <div className="rounded-[20px] border px-4 py-3 shadow-[0_18px_45px_rgba(0,0,0,0.28),inset_0_1px_0_rgba(255,255,255,0.06)]" style={{ background: atlasColors.bgCardPremium, borderColor: atlasColors.borderMuted }}>
              <div className="flex items-center gap-3 overflow-x-auto custom-scrollbar">
                {[
                  { key: 'visualizacao2d', label: 'Visualização 2D', onClick: () => activateVisualizationTab('2d') },
                  { key: 'visualizacao3d', label: 'Visualização 3D', onClick: () => activateVisualizationTab('3d') },
                  { key: 'medidas', label: 'Medidas', onClick: () => setActiveTab('medidas') },
                  { key: 'relatorio', label: 'Relatório', onClick: () => setActiveTab('relatorio') },
                  { key: 'plano', label: 'Plano Cirúrgico', onClick: () => setActiveTab('plano') },
                  { key: 'compare', label: 'Comparar', onClick: () => { setCompareMode((prev) => !prev); setActiveTab(activeVisualizationMode === '2d' ? 'visualizacao2d' : 'visualizacao3d'); } },
                  { key: 'fullscreen', label: 'Tela cheia', onClick: () => setIsFullscreen((prev) => !prev) },
                  { key: 'export', label: 'Exportar', onClick: () => handleDownloadMedia({ stopPropagation() {} } as any) },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={tab.onClick}
                    className={`whitespace-nowrap rounded-full border px-4 py-2 text-[10px] font-bold uppercase tracking-[0.32em] transition-all duration-200 ${
                      tab.key === activeTab || (tab.key === 'compare' && compareMode)
                        ? 'scale-[1.02]'
                        : 'hover:scale-[1.02]'
                    }`}
                    style={
                      tab.key === activeTab || (tab.key === 'compare' && compareMode)
                        ? { borderColor: atlasColors.border, background: atlasColors.bgCard, color: atlasColors.textPrimary, boxShadow: '0 12px 26px rgba(0,0,0,0.18)' }
                        : { borderColor: atlasColors.border, background: atlasColors.bgPrimary, color: atlasColors.textSecondary }
                    }
                    >
                      {tab.label}
                    </button>
                  ))}
              </div>
            </div>
          </div>

          <div className="absolute right-4 top-1/2 z-20 -translate-y-1/2 pointer-events-auto">
            <div className="flex flex-col gap-1.5 rounded-[24px] border p-1.5 shadow-[0_18px_45px_rgba(0,0,0,0.28),inset_0_1px_0_rgba(255,255,255,0.06)]" style={{ background: atlasColors.bgCardPremium, borderColor: atlasColors.borderMuted }}>
              {viewerTools.map((tool) => {
                const Icon = tool.icon;
                return (
                  <button
                    key={tool.key}
                    onClick={tool.onClick}
                    disabled={tool.key === 'capture' && isCapturing}
                    className="w-[72px] min-w-[72px] rounded-[18px] border border-cyan-400/10 bg-[rgba(5,15,30,0.8)] px-2.5 py-2.5 text-center text-slate-200 transition-all duration-200 hover:-translate-y-0.5 hover:border-cyan-300/25 hover:bg-slate-900/90 hover:shadow-[0_0_12px_rgba(0,180,255,0.25)] active:scale-[0.96] disabled:cursor-wait disabled:opacity-60"
                  >
                    <div className="flex flex-col items-center gap-0.5">
                      <Icon className="h-5 w-5 text-cyan-300 drop-shadow-[0_0_10px_rgba(34,211,238,0.18)]" />
                      <span className="block whitespace-nowrap text-[8px] font-bold tracking-[0.22em] text-slate-100/95">
                        {tool.key === 'capture' && isCapturing ? 'CAPTURANDO' : tool.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        )}

        {!isFocusMode && activeTab === 'medidas' && (
          <div className={`${atlasEntranceClass} mt-6 slide-in-from-right-4 duration-500`}>
            <div className="mb-4">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: atlasColors.accent }}>
                {language === 'pt' ? 'Medições' : language === 'es' ? 'Mediciones' : 'Measurements'}
              </div>
              <h3 className="mt-2 text-[24px] font-bold leading-[1.08]" style={{ color: atlasColors.textPrimary }}>
                {language === 'pt' ? 'Biometria e medidas da IA' : language === 'es' ? 'Biometría y medidas de IA' : 'Biometrics and AI measurements'}
              </h3>
            </div>

            <ClinicalGrid>
              <ClinicalCard
                eyebrow={language === 'pt' ? 'Biometria cirúrgica' : language === 'es' ? 'Biometría quirúrgica' : 'Surgical biometry'}
                title={language === 'pt' ? 'Resumo cirúrgico' : language === 'es' ? 'Resumen quirúrgico' : 'Surgical summary'}
                items={[
                  `${language === 'pt' ? 'Parafuso principal' : 'Main screw'}: ${biometrics.screwLength || 0} mm`,
                  `${language === 'pt' ? 'Ocupação canal/placa' : 'Canal/plate occupancy'}: ${biometrics.canalOcc || 0}%`,
                  `${language === 'pt' ? 'Angulação alvo' : 'Target angle'}: ${biometrics.angulation || 0}°`,
                ]}
                variant="default"
              />
              <ClinicalCard
                eyebrow={language === 'pt' ? 'Medições automáticas (IA)' : language === 'es' ? 'Mediciones automáticas (IA)' : 'Automatic measurements (AI)'}
                title={language === 'pt' ? 'Região analisada' : language === 'es' ? 'Región analizada' : 'Analyzed region'}
                items={
                  autoMeasurementRows.length > 0
                    ? autoMeasurementRows.map((m) => `${m.label}: ${m.value}${m.status ? ` • ${m.status}` : ''}`)
                    : [emptyDataLabel]
                }
                variant="neutral"
              />
            </ClinicalGrid>
          </div>
        )}

        {!isFocusMode && activeTab === 'relatorio' && (
          <div className={`${atlasEntranceClass} mt-6 slide-in-from-right-4 duration-500`}>
            <div className="flex items-center justify-between border-b pb-4" style={{ borderColor: atlasColors.border }}>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: atlasColors.accent }}>
                  {t('report')}
                </div>
                <h3 className="mt-2 text-[24px] font-bold leading-[1.08]" style={{ color: atlasColors.textPrimary }}>
                  {language === 'pt' ? 'Resumo técnico do caso' : language === 'es' ? 'Resumen técnico del caso' : 'Technical case summary'}
                </h3>
              </div>
              <button onClick={() => setShowReport(true)} disabled={!selectedMedia} className="rounded-full border px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] transition disabled:cursor-not-allowed disabled:opacity-50" title="Relatório" style={{ borderColor: atlasColors.border, background: atlasColors.bgCard, color: atlasColors.textPrimary }}>
                {language === 'pt' ? 'Abrir relatório' : 'Open report'}
              </button>
            </div>

            <ClinicalGrid className="mt-5">
              <ClinicalCard
                title={language === 'pt' ? 'Diagnóstico principal' : 'Main diagnosis'}
                items={surgicalPlan?.diagnostico ? [surgicalPlan.diagnostico] : [emptySectionLabel]}
                variant="default"
              />
              <ClinicalCard
                title={language === 'pt' ? 'Diagnósticos diferenciais' : 'Differential diagnoses'}
                items={surgicalPlanDifferentials.length > 0 ? surgicalPlanDifferentials : [emptySectionLabel]}
                variant="neutral"
              />
              <ClinicalCard
                title={language === 'pt' ? 'Estratégia selecionada' : 'Selected strategy'}
                items={selectedStrategy ? [selectedStrategy === "surgical" ? (language === 'pt' ? 'Técnica cirúrgica' : 'Surgical technique') : (language === 'pt' ? 'Tratamento conservador' : 'Conservative management')] : [planningNoPlanMessage]}
                variant="success"
              />
              <ClinicalCard
                title={selectedStrategy === "surgical" ? (language === 'pt' ? 'Técnica cirúrgica' : 'Surgical technique') : (language === 'pt' ? 'Tratamento conservador' : 'Conservative management')}
                items={selectedPlanBullets.length > 0 ? selectedPlanBullets : [planningNoPlanMessage]}
                variant="warning"
              />
            </ClinicalGrid>

            <ClinicalGrid className="mt-5 2xl:grid-cols-3">
              <ClinicalCard
                title={language === 'pt' ? 'Evidências científicas' : 'Scientific evidence'}
                items={scientificEvidenceText ? [scientificEvidenceText] : [emptySectionLabel]}
                description="AO, AAOS, PubMed, diretrizes e consensos."
                variant="success"
              />
              <ClinicalCard
                title={language === 'pt' ? 'Alertas clínicos' : 'Clinical alerts'}
                items={selectedPlanRiskBullets.length > 0 ? selectedPlanRiskBullets : [emptySectionLabel]}
                variant="warning"
              />
              <ClinicalCard
                title={language === 'pt' ? 'Pós-operatório / seguimento' : 'Postoperative / follow-up'}
                items={selectedPlanSecondaryBullets.length > 0 ? selectedPlanSecondaryBullets : [emptySectionLabel]}
                variant="default"
              />
            </ClinicalGrid>
          </div>
        )}

        {/* Planning Panel */}
        {!isFocusMode && (surgicalPlanningPrimaryFlow || activeTab === 'plano') && (
        <div className={`${atlasEntranceClass} mt-6 fade-in slide-in-from-right-4 duration-500`}>
            {surgicalPlanViewerImage ? (
              <div className="mb-6">
                <SurgicalPlanOriginalImageViewer
                  language={language}
                  imageUrl={surgicalPlanViewerImage}
                  fileName={currentItem || undefined}
                  mimeType={surgicalPlanOriginalImageMimeType}
                />
              </div>
            ) : null}
            {isProcessingSurgicalPlan ? (
              <div className="rounded-[24px] border p-5 text-left shadow-[0_30px_90px_rgba(0,0,0,0.32)]" style={{ background: atlasColors.bgViewerPremium, borderColor: atlasColors.borderMuted }}>
                <div className="text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: atlasColors.accent }}>
                  {language === 'pt' ? 'Plano cirúrgico' : language === 'es' ? 'Plan quirúrgico' : 'Surgical plan'}
                </div>
                <h3 className="mt-2 text-[24px] font-bold leading-[1.08]" style={{ color: atlasColors.textPrimary }}>
                  {language === 'pt' ? 'Processando análise cirúrgica' : language === 'es' ? 'Procesando análisis quirúrgico' : 'Processing surgical analysis'}
                </h3>
                <p className="mt-3 max-w-3xl text-[14px] leading-[1.6]" style={{ color: atlasColors.textSecondary }}>
                  {language === 'pt'
                    ? 'Mantendo o exame visível enquanto a IA estrutura o diagnóstico e o plano.'
                    : language === 'es'
                      ? 'Manteniendo el examen visible mientras la IA estructura el diagnóstico y el plan.'
                      : 'Keeping the exam visible while AI structures the diagnosis and plan.'}
                </p>
              </div>
            ) : (
              <>
                {surgicalPlanV2 ? (
                  <SurgicalPlanningPremiumV2
                    language={language}
                    plan={surgicalPlanV2}
                    notice={error?.message || null}
                  />
                ) : error?.message ? (
                  <div className="mb-4 rounded-[18px] border px-4 py-3 text-[13px] leading-[1.55]" style={{ borderColor: atlasColors.borderMuted, background: "rgba(2,6,23,0.56)", color: atlasColors.textSecondary }}>
                    {error.message}
                  </div>
                ) : surgicalPlanViewerImage ? (
                  <div className="rounded-[18px] border px-4 py-3 text-[13px] leading-[1.55]" style={{ borderColor: atlasColors.borderMuted, background: "rgba(2,6,23,0.56)", color: atlasColors.textSecondary }}>
                    {language === 'pt'
                      ? 'Gere o plano cirúrgico para estruturar o planejamento.'
                      : language === 'es'
                        ? 'Genere el plan quirúrgico para estructurar la planificación.'
                        : 'Generate the surgical plan to structure the planning.'}
                  </div>
                ) : null}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AtlasModule;
